import { EDGE_FINISH_FACADE, edgeFinishTarget, normalizeEdgeFinish } from "./domain/edgeFinish";
import { chestTopEnamel, enamelColorLabel, shelvingCarcassEnamel } from "./domain/shelvingFinishes";
import { formatNcs, ncsToHex } from "./domain/ncs";
import { CARCASS_MM } from "./domain/carcass";
import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  FACADE_COLORS,
  FACADE_PATTERNS,
  FACADES,
  HANDLES,
  INTEGRATED_HANDLE_STYLES,
  handleChoicesForFacade,
  INTERNALS,
  MODULE_NAMES,
  moduleName,
  WALL_COLORS
} from "./catalog";
import {
  APP_VERSION,
  BUILD_LABEL,
  reloadWithFreshAssets
} from "./buildInfo";
import { calculatePrice, formatPrice, priceIsComplete } from "./domain/pricing";
import { priceKeys, priceValue } from "./domain/priceList";
import { usePriceStore } from "./store/usePriceStore";
import { cornerPlan, cornerSide, cornerReturnWidth, cornerDoorSpan, cornerDoorWidthMm, cornerFillerMm, cornerNeighbourFillerMm, cornerRailPlan, cornerRun, cornerShelfPlan,
  cornerHingeSide, cornerMezzanineMm, cornerToPlan, CORNER_DOOR_MAX_MM, CORNER_MEZZANINE_MAX_MM, CORNER_MEZZANINE_MIN_MM, CORNER_DOOR_MIN_MM, CORNER_FILLER_MAX_MM, CORNER_FILLER_MIN_MM } from "./domain/corner";
import { cornerCanAddRail, cornerItemBounds, cornerItemPosition, cornerMinimumHeight, cornerRailPlacement, cornerSecondRailShortfallMm, CORNER_MAX_RAILS } from "./domain/cornerInternals";
import { clothesFitBelowRail, railClothesText } from "./domain/hanging";
import {
  sendDesignerOrder,
  type DesignerContact,
  type DesignerVisualization
} from "./domain/sendOrder";
import {
  compositionWidthMm,
  createCompositionLayout,
  applyRoomSize,
  BACK_WALL_LENGTH_MM,
  MAX_ROOM_WIDTH_MM,
  MIN_ROOM_WIDTH_MM,
  roomWidthMinimumMm,
  roomWidthMm,
  PERPENDICULAR_WALL_CLEARANCE_MM,
  cornerNeighbour,
  cornerNeighbourSlot,
  canPlaceCornerNeighbour
} from "./domain/layout";
import { createProjectRenderSpecification } from "./domain/renderSpecification";
import { cornerFillerForHandlesMm, cornerNeighbourFillerForHandlesMm } from "./domain/cornerHandles";
import {
  dividerEdgeSteps,
  dividerSpanLimits,
  internalItemMinimumY,
  INTERNAL_DRAWER_MAX_HEIGHT_MM,
  INTERNAL_DRAWER_MIN_HEIGHT_MM,
  resizeInternalDrawer
} from "./domain/internalLayout";
import {
  cabinetBaseHeightMm,
  cabinetBaseSetbackMm,
  cabinetBaseType,
  DEFAULT_BASE_HEIGHT_MM,
  MAX_BASE_SETBACK_MM,
  MAX_SIDE_FILLER_MM,
  MAX_TOP_FILLER_MM,
  MIN_BASE_HEIGHT_MM,
  maxCabinetBaseHeightMm,
  topFillerMm
} from "./domain/installation";
import {
  baseModuleFor,
  cabinetRunWidth,
  cabinetSectionWidths,
  cabinetSupportsToCeiling,
  cabinetTotalHeight,
  ceilingGapMm,
  MAX_CEILING_GAP_MM,
  ceilingHeightMm,
  doorLeafCount,
  hasUnifiedFacade,
  mezzanineFlapLayout,
  MAX_CABINET_SECTION_WIDTH_MM,
  MAX_CABINET_TOTAL_WIDTH_MM,
  MAX_CEILING_HEIGHT_MM,
  MAX_MEZZANINE_HEIGHT_MM,
  SHELVING_MAX_MEZZANINE_HEIGHT_MM,
  shelvingMezzanineHeightRange,
  maxCabinetTotalHeight,
  maxModuleHeight,
  maxModuleWidth,
  maxShelvingTotalHeight,
  mezzanineFor,
  mezzanineHeightLimit,
  maxCabinetRunWidthOnBackWall,
  cornerAddBlocker,
  MIN_CEILING_HEIGHT_MM,
  MIN_MODULE_HEIGHT_MM,
  MAX_MODULE_HEIGHT_MM,
  shelvingTotalHeight,
  shelvingUpperFor,
  usableRoomHeightMm
} from "./domain/moduleRules";
import {
  CABINET_LIGHT_MODE_HINTS,
  CABINET_LIGHT_MODE_NAMES,
  cabinetLightMode,
  cabinetLightPosition,
  cabinetLightTemperature,
  cabinetSupportsLighting,
  withCabinetLightMode
} from "./domain/cabinetLighting";
import {
  cabinetDrawerCount,
  cabinetDrawerFrontHeightMm,
  cabinetDrawerFinishDiffers,
  cabinetDrawerLayout,
  CABINET_DRAWER_MAX_FRONT_MM,
  CABINET_DRAWER_MIN_FRONT_MM,
  CABINET_DRAWER_SEAM_MM,
  handleDoorHeightMm,
  maxCabinetDrawerCount,
  supportsCabinetDrawers
} from "./domain/cabinetDrawers";
import {
  handleCenterRangeMm,
  handleCenterYmm,
  handleEdgeOffsetMm,
  handleEdgeOffsetRangeMm,
  handleLengthMm,
  handleLengthRangeMm,
  handleOrientation,
  integratedHandleStyle,
  handleSupportsOrientation
} from "./domain/handles";
import {
  facadePaintHex,
  facadePaintLabel,
  facadeSupportsMilling
} from "./domain/facades";
import { maxHeight } from "./domain/project";
import {
  ROOM_STYLE_OPTIONS,
  roomStyleName
} from "./domain/roomPresets";
import {
  defaultShelvingCell,
  parseShelvingGridBoardRef,
  shelvingGridBoardLabel,
  shelvingGridSegmentMerge,
  shelvingMergeTowards,
  shelvingFacadeMount,
  shelvingUprightRows,
  getShelvingCellBoards,
  getShelvingCellLayouts,
  getShelvingDiagonalSections,
  getShelvingMergedCells,
  shelvingCellShelfYs,
  SHELVING_GLASS_SHELF_THICKNESS_MM,
  SHELVING_EXTRA_SHELF_CLEARANCE_MM,
  SHELVING_PANEL_THICKNESS_MM,
  SHELVING_FACADE_OVERHANG_OPTIONS_MM,
  normalizeShelvingFacadeOverhang,
  shelvingCellDividerXs,
  shelvingMergeProblem,
  withAddedShelvingBoard,
  withoutShelvingBoard,
  getShelvingGridLayout,
  getShelvingMainDividerSegment,
  maxShelvingColumnsForWidth,
  maxShelvingRowsForHeight,
  shelvingExtraShelfHeight,
  shelvingExtraShelfRange,
  shelvingExtraDividerRange,
  shelvingExtraDividerPosition,
  shelvingExtraDividerMaxAngle,
  SHELVING_STANDARD_ANGLE_DEG,
  shelvingDividerEdgeCenters,
  shelvingMainDividerMaxAngle,
  shelvingLegCount,
  SHELVING_LEG_HEIGHT_MM,
  SHELVING_MAX_HEIGHT_MM,
  SHELVING_PLINTH_HEIGHT_MM,
  SHELVING_MAX_DEPTH_MM,
  SHELVING_MAX_WIDTH_MM,
  SHELVING_MIN_DEPTH_MM,
  SHELVING_MIN_WIDTH_MM,
  parseShelvingCellId,
  shelvingCellId,
  shelvingCellFrontRect,
  shelvingCellFits,
  shelvingDrawerCount,
  shelvingDrawerCountRange,
  minShelvingColumnsForWidth,
  SHELVING_DRAWER_MIN_FRONT_MM,
  CHEST_MAX_DRAWERS,
  CHEST_DRAWER_MIN_FRONT_MM,
  drawerLimits,
  SHELVING_DRAWER_MAX_FRONT_MM,
  SHELVING_MAX_CLEAR_SPAN_MM,
  SHELVING_DRAWERS_MIN_WIDTH_MM,
  CHEST_MAX_COLUMNS,
  chestMaxColumns,
  gridCellWidth,
  shelvingKindAllowsRowColumns,
  SHELVING_MIN_CELL_WIDTH_MM,
  type ShelvingGridLayout
} from "./domain/shelving";
import {
  END_MODULE_MAX_DEPTH_MM,
  END_MODULE_MAX_TIERS,
  END_MODULE_MAX_WIDTH_MM,
  END_MODULE_MIN_DEPTH_MM,
  END_MODULE_MIN_TIERS,
  END_MODULE_MIN_WIDTH_MM,
  boundaryCoreModule,
  canAttachEndModule,
  endModuleAttachmentTargets,
  endModuleForTargetSide,
  endModuleAddBlocker,
  endModuleTarget,
  endModuleShape,
  endModuleSide,
  endModuleTierCount
} from "./domain/endModules";
import { validateProject } from "./domain/validation";
import { PlannerCanvas, type PlannerCanvasHandle } from "./components/PlannerCanvas";
import { RoomRenderDialog } from "./components/RoomRenderDialog";
import { ShelvingLayoutEditor } from "./components/ShelvingLayoutEditor";
import { ShelvingDesignTools } from "./components/ShelvingDesignTools";
import { DecorPicker } from "./components/DecorPicker";
import { interiorVerticalRangeMm, itemCompartmentLabel, usableItemPieces } from "./domain/compartments";
import { NOMINAL_WIDTH_FITTINGS, nominalFittingHint } from "./domain/wardrobeFittings";
import { useCatalogCover } from "./store/useCatalogCover";
import { prepareCover } from "./components/catalogCover";
import { BACK_DECOR_SUGGESTIONS, normalizeBackDecor, shelvingHasBackPanels, supportsBackDecor } from "./domain/backPanel";
import { drawerFrontsCanFollowFacade, INTERIOR_DECOR_SUGGESTIONS, normalizeDrawerDecor, normalizeShelfDecor, supportsInteriorDecor } from "./domain/interiorDecor";
import { doorFacadeLeafCount, doorFacadeOverride, doorLeafName, hasDoorFacades, leafFinish, moduleDoorFacade, supportsDoorFacades, withDoorFacade } from "./domain/doorFacades";
import { cellFrontLeaves, shelvingExcludedLifts } from "./domain/flapFronts";
import {
  chestCellSplit,
  chestDrawerHasOwnPaint,
  chestDrawerPaintFinish,
  chestEnamelFinish,
  chestFinishCell,
  chestFinishPatch,
  chestTopFlapRange,
  CHEST_TOP_FLAP_DEFAULT_MM,
  CHEST_TOP_FLAP_MAX_MM,
  CHEST_TOP_FLAP_MIN_MM,
  isChestGlassDoor,
  normalizeTopDecor
} from "./domain/chest";
import { boardLabel, useShelvingBoardHover, useShelvingUprightFocus } from "./store/useShelvingBoardHover";
import {
  LIGHT_POSITION_HINTS,
  LIGHT_POSITION_NAMES,
  LIGHT_TEMPERATURES,
  normalizeLightPosition,
  normalizeLightTemperature,
  LIGHT_PROFILE_NOTE,
  SHELVING_LIGHTING_HINTS,
  SHELVING_LIGHTING_NAMES,
  shelvingCellLighting,
  shelvingLightingCells
} from "./domain/lighting";
import { SlideOutPanel, SLIDE_OUT_ROOT_ID, useSlideOut } from "./components/SlideOutPanel";

const SHELVING_CELLS_PANEL = "shelving-cells";
import { decorPriceMaterial, decorTextureUrl, resolveDecor } from "./domain/decors";
import {
  normalizeSideMirrors,
  SIDE_MIRROR_MARGIN_MM,
  sideMirrorBlocker,
  sideMirrorSize,
  supportsSideMirror,
  type SideMirrorSide
} from "./domain/sideMirrors";
import { shelvingDesignName } from "./domain/shelvingDesigns";
import {
  shelvingBulkPlan,
  shelvingBulkScopeIds,
  shelvingMergeKeptCell,
  shelvingCellIsConfigured,
  type ShelvingBulkScope
} from "./domain/shelvingBulk";
import { ProductChooser, ProductGuide } from "./components/ProductChooser";
import { canEditProduct, productFamilyFor, productFamilyTitles, productModules, productSelection,
  productWorkflows, productEntryPanel, productMainPanel, projectAcceptsFamily, projectProductFamilies, resolveProductPanel, workflowTitle, workflowShortTitle,
  type ProductFamily, type Panel, type WorkflowPanel } from "./domain/productWorkflow";
import { cornerPartner, usePlannerStore } from "./store/usePlannerStore";
import { EmployeeWorkspace } from "./components/EmployeeWorkspace";
import { CellFrontOpeningPicker, MezzanineOpeningPicker } from "./components/FrontOpeningPicker";
import { moduleHeadroomMm, shelvingCellHeadroomMm } from "./domain/flapHeadroom";
import { ModelCatalog } from "./components/ModelCatalog";
import { readSiteFamilyLock, readSiteLink, SITE_FAMILY_BACK_LABELS, siteFamilyUrl, siteHome, storeSiteFamilyLock, withoutSiteLink, type SiteLink } from "./domain/siteLink";
import { catalogRequest } from "./domain/catalogApi";
import { applyCatalogModel, type CatalogModelDetail } from "./store/catalogApply";
import type {
  FacadeColorType,
  FacadePatternType,
  FacadeType,
  HandleType,
  IntegratedHandleStyle,
  InternalItem,
  ItemType,
  ModuleType,
  PlannerProject,
  ShelvingCellConfig,
  WardrobeModule
} from "./types";

const availableFacadeTypes: FacadeType[] = [
  "wood",
  "glass",
  "mirror",
  "satin"
];
/** Фасады ящиков комода — плита или МДФ в эмали: стекло и зеркало на ящике не ставят. */
const chestFacadeTypes: FacadeType[] = ["wood", "satin"];

/** Смена материала фасада ячейки: фрезеровка бывает только у МДФ, интегрированная ручка — только у МДФ. */
const cellFacadeTypePatch = (key: FacadeType, handle: HandleType | undefined): Partial<ShelvingCellConfig> => ({
  facade: key,
  ...(facadeSupportsMilling(key) ? {} : { facadePattern: "plain" as const }),
  ...(key === "satin" ? {} : handle === "integrated" ? { handle: "bar" as const } : {})
});

const SHORT_FACADE_NAMES: Partial<Record<FacadeType, string>> = {
  wood: "ЛДСП",
  satin: "МДФ в эмали",
  glass: "Стекло",
  mirror: "Зеркало"
};

/**
 * Материал фасада прямо в панели ячеек (замечание Сергея № 2): поставил
 * фасад — сразу выбрал, из чего он. Декор, цвет и фрезеровка — на шаге «Фасады».
 */
function ShelvingCellFacadeMaterial({ cell, onPatch }: {
  cell: ShelvingCellConfig;
  onPatch: (patch: Partial<ShelvingCellConfig>) => void;
}) {
  const choices = cell.content === "drawers" ? chestFacadeTypes : availableFacadeTypes;
  const facade = cell.facade ?? "wood";
  return <>
    <span className="field-caption">Материал фасада</span>
    <div className={`segmented-control shelving-facade-material${choices.length === 2 ? " two" : ""}`}
      role="group" aria-label="Материал фасада">
      {choices.map((key) => (
        <button type="button" key={key} className={facade === key ? "is-selected" : ""}
          aria-pressed={facade === key} title={FACADES[key].name}
          onClick={() => onPatch(cellFacadeTypePatch(key, cell.handle))}>
          {SHORT_FACADE_NAMES[key] ?? FACADES[key].name}
        </button>
      ))}
    </div>
  </>;
}

const availableFacadePatterns: FacadePatternType[] = [
  "plain",
  "art001",
  "art005",
  "art006",
  "art007",
  "art008",
  "art009",
  "art010",
  "art012",
  "art023",
  "art036",
  "art037"
];

const facadeSupportsPattern = facadeSupportsMilling;

const handlesForFacade = (facade: FacadeType) =>
  handleChoicesForFacade(facade).map((key) => [key, HANDLES[key]] as const);

/** Под выбором ручки: объясняет, почему модели ручки здесь нет. */
function HandleChoiceNote({ handle }: { handle: HandleType | undefined }) {
  if (handle === "push") {
    return <small className="handle-choice-note">Фасад открывается нажатием — механизм Tip-On, ручки нет.</small>;
  }
  if (handle === "glassGrip") {
    return <small className="handle-choice-note">Зацеп по всей высоте в алюминиевом профиле створки — отдельной ручки нет.</small>;
  }
  if (handle === "integrated") return null;
  return <small className="handle-choice-note">Модель ручки подбирается с менеджером или покупается отдельно — сборщик установит её на месте.</small>;
}

const panelTitles: Record<Panel, [string, string]> = {
  overview: [
    "Что будем собирать?",
    "У каждого изделия — свои понятные шаги"
  ],
  modules: ["Корпусы", "Добавьте секции и задайте их размеры"],
  shelving: [
    "Настройка стеллажа",
    "Задайте габариты, сетку и способ установки"
  ],
  chest: ["Комод", "Размеры, колонки ящиков и основание"],
  materials: ["Цвет и материал", "Выберите отделку выбранного корпуса"],
  internals: ["Наполнение", "Добавьте полки, штанги и системы хранения"],
  facades: ["Фасады и ручки", "Выберите двери, материал и фурнитуру"],
  room: ["Комната", "Начните с высоты потолка: от неё зависит высота изделия"],
  summary: ["Проверка проекта", "Проверьте состав, стоимость и замечания"]
};

const cellDrawerWord = (count: number) => {
  const tail = count % 100;
  if (tail >= 11 && tail <= 14) return "ящиков";
  const last = count % 10;
  return last === 1 ? "ящик" : last >= 2 && last <= 4 ? "ящика" : "ящиков";
};

const productWord = (count: number) => {
  const lastTwo = count % 100;
  const last = count % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return "изделий";
  if (last === 1) return "изделие";
  if (last >= 2 && last <= 4) return "изделия";
  return "изделий";
};

const sectionWord = (count: number) => {
  const lastTwo = count % 100;
  const last = count % 10;
  if (lastTwo >= 11 && lastTwo <= 14) return "секций";
  if (last === 1) return "секция";
  if (last >= 2 && last <= 4) return "секции";
  return "секций";
};

function Icon({
  name,
  size = 20
}: {
  name:
    | "undo"
    | "redo"
    | "camera"
    | "room"
    | "help"
    | "cube"
    | "layers"
    | "palette"
    | "shelf"
    | "door"
    | "check"
    | "chevron"
    | "back"
    | "plus"
    | "trash"
    | "front"
    | "perspective"
    | "ruler"
    | "close"
    | "download"
    | "expand"
    | "sparkles";
  size?: number;
}) {
  const paths: Record<typeof name, ReactNode> = {
    undo: <path d="M9 7 4 12l5 5M5 12h8a6 6 0 0 1 6 6" />,
    redo: <path d="m15 7 5 5-5 5m4-5h-8a6 6 0 0 0-6 6" />,
    camera: (
      <>
        <path d="M5 8h3l1.5-2h5L16 8h3a2 2 0 0 1 2 2v8H3v-8a2 2 0 0 1 2-2Z" />
        <circle cx="12" cy="13" r="3" />
      </>
    ),
    room: (
      <>
        <path d="M4 20V7l8-4 8 4v13" />
        <path d="M8 20v-7h8v7M4 20h16" />
      </>
    ),
    help: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M9.8 9a2.3 2.3 0 1 1 3.4 2c-.8.5-1.2 1-1.2 2M12 17h.01" />
      </>
    ),
    cube: (
      <>
        <path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" />
        <path d="m4.3 7.7 7.7 4.4 7.7-4.4M12 12v9" />
      </>
    ),
    layers: (
      <>
        <path d="m12 3 9 5-9 5-9-5 9-5Z" />
        <path d="m3 12 9 5 9-5M3 16l9 5 9-5" />
      </>
    ),
    palette: (
      <>
        <circle cx="8" cy="8" r="1" />
        <circle cx="12" cy="6" r="1" />
        <circle cx="16" cy="9" r="1" />
        <path d="M12 3a9 9 0 1 0 0 18h1.5a2 2 0 0 0 0-4H12a2 2 0 0 1 0-4h4a5 5 0 0 0 0-10h-4Z" />
      </>
    ),
    shelf: (
      <>
        <path d="M5 4v16M19 4v16M5 8h14M5 14h14M3 20h18" />
      </>
    ),
    door: (
      <>
        <path d="M5 21V4h14v17M5 21h14" />
        <path d="M12 4v17M9 12h.01M15 12h.01" />
      </>
    ),
    check: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m8 12 2.5 2.5L16 9" />
      </>
    ),
    chevron: <path d="m9 5 7 7-7 7" />,
    back: <path d="m15 18-6-6 6-6" />,
    plus: <path d="M12 5v14M5 12h14" />,
    trash: (
      <>
        <path d="M4 7h16M9 7V4h6v3M7 7l1 14h8l1-14M10 11v6M14 11v6" />
      </>
    ),
    front: (
      <>
        <rect x="5" y="4" width="14" height="16" rx="1" />
        <path d="M12 4v16M9 12h.01M15 12h.01" />
      </>
    ),
    perspective: (
      <>
        <path d="m5 6 9-3 5 4v12l-9 2-5-4V6Z" />
        <path d="m5 6 5 4 9-3M10 10v11" />
      </>
    ),
    ruler: (
      <>
        <path d="M4 17 17 4l3 3L7 20l-3-3Z" />
        <path d="m9 12 2 2m1-5 2 2m1-5 2 2" />
      </>
    ),
    close: <path d="M6 6l12 12M18 6 6 18" />,
    expand: <path d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5" />,
    download: (
      <>
        <path d="M12 3v12m0 0 4-4m-4 4-4-4" />
        <path d="M5 20h14" />
      </>
    ),
    sparkles: (
      <>
        <path d="m12 3 .9 3.1A4 4 0 0 0 15.7 9l3.3 1-3.3 1a4 4 0 0 0-2.8 2.9L12 17l-.9-3.1A4 4 0 0 0 8.3 11L5 10l3.3-1a4 4 0 0 0 2.8-2.9L12 3Z" />
        <path d="m19 16 .4 1.2a2 2 0 0 0 1.4 1.4l1.2.4-1.2.4a2 2 0 0 0-1.4 1.4L19 22l-.4-1.2a2 2 0 0 0-1.4-1.4L16 19l1.2-.4a2 2 0 0 0 1.4-1.4L19 16Z" />
      </>
    )
  };

  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name]}
    </svg>
  );
}

function ToolButton({
  label,
  icon,
  disabled,
  onClick
}: {
  label: string;
  icon: Parameters<typeof Icon>[0]["name"];
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className="tool-button"
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon name={icon} />
    </button>
  );
}


const floorPreviewColors: Record<PlannerProject["room"]["floor"], string> = {
  light: "#d9bb8d", dark: "#665040", laminate: "#b48d63", tile: "#bbb8b1"
};

function RoomPreview({
  wallColor,
  floor
}: {
  wallColor: string;
  floor: PlannerProject["room"]["floor"];
}) {
  return (
    <span
      className="room-preset-preview"
      style={{ backgroundColor: wallColor }}
      aria-hidden="true"
    >
      <i style={{ backgroundColor: floorPreviewColors[floor] }} />
    </span>
  );
}

function ProjectSetupCard({
  project,
  onOpenRoom
}: {
  project: PlannerProject;
  onOpenRoom: () => void;
}) {
  const setProjectName = usePlannerStore((state) => state.setProjectName);

  const commitProjectName = (input: HTMLInputElement) => {
    const nextName = input.value.trim().slice(0, 60) || "Новый проект";
    input.value = nextName;
    if (nextName !== project.name) setProjectName(nextName);
  };

  return (
    <section className="project-setup-card">
      <div className="project-setup-card__heading">
        <div>
          <span className="eyebrow">Ваш проект</span>
          <h3>Основные настройки</h3>
        </div>
      </div>

      <label className="project-setup-field">
        <span>Название</span>
        <input
          key={project.name}
          type="text"
          defaultValue={project.name}
          maxLength={60}
          placeholder="Например, шкаф у окна"
          aria-label="Название проекта"
          onBlur={(event) => commitProjectName(event.currentTarget)}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
            if (event.key === "Escape") {
              event.currentTarget.value = project.name;
              event.currentTarget.blur();
            }
          }}
        />
      </label>

      <div className="project-room-summary">
        <RoomPreview
          wallColor={project.room.wallColor}
          floor={project.room.floor}
        />
        <span>
          <strong>Оформление комнаты</strong>
          <small>{roomStyleName(project.room.style)}</small>
        </span>
        <button type="button" onClick={onOpenRoom}>
          Изменить
        </button>
      </div>

    </section>
  );
}

function WorkflowStepper({
  panel,
  family,
  hasProducts,
  onNavigate
}: {
  panel: WorkflowPanel;
  family: ProductFamily;
  hasProducts: boolean;
  onNavigate: (panel: WorkflowPanel) => void;
}) {
  const workflowPanels = productWorkflows[family];
  const currentIndex = workflowPanels.indexOf(panel);

  return (
    <nav className="workflow-stepper" aria-label="Этапы настройки проекта">
      {workflowPanels.map((workflowPanel, index) => {
        const isActive = workflowPanel === panel;
        const className = [
          "workflow-stepper__item",
          index < currentIndex ? "is-before" : "",
          isActive ? "is-active" : ""
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <button
            className={className}
            type="button"
            key={workflowPanel}
            aria-current={isActive ? "step" : undefined}
            aria-label={`Шаг ${index + 1}. ${workflowTitle(workflowPanel, family)}`}
            title={workflowTitle(workflowPanel, family)}
            disabled={!hasProducts && workflowPanel !== "room" && workflowPanel !== "summary" &&
              workflowPanel !== productMainPanel(family)}
            onClick={() => onNavigate(workflowPanel)}
          >
            <span>{index + 1}</span>
            <strong>{workflowShortTitle(workflowPanel, family)}</strong>
          </button>
        );
      })}
    </nav>
  );
}

/**
 * Удаление изделия рядом с его выбором: заказчик искал эту кнопку и не нашёл —
 * она лежала в свёрнутом блоке внизу панели. Подтверждение вторым нажатием, а не
 * окном: собранный стеллаж слишком дорого потерять случайным кликом, а модальное
 * окно на каждый шаг — слишком громко.
 */
function DeleteProductButton({ project }: { project: PlannerProject }) {
  const remove = usePlannerStore((state) => state.removeSelectedModule);
  const [armed, setArmed] = useState(false);
  const selected = project.modules.find(
    (module) => module.id === project.selectedModuleId
  );
  useEffect(() => {
    setArmed(false);
  }, [project.selectedModuleId]);
  useEffect(() => {
    if (!armed) return;
    const timer = window.setTimeout(() => setArmed(false), 4000);
    return () => window.clearTimeout(timer);
  }, [armed]);
  if (!selected) return null;
  const name =
    selected.type === "shelving" && selected.stackedOnId
      ? "антресоль стеллажа"
      : (moduleName(selected) ?? "изделие").toLowerCase();
  return (
    <button
      className={`product-delete${armed ? " is-armed" : ""}`}
      type="button"
      title={armed ? "Нажмите ещё раз, чтобы удалить" : `Удалить ${name}`}
      aria-label={armed ? "Подтвердить удаление" : `Удалить ${name}`}
      onClick={() => {
        if (!armed) {
          setArmed(true);
          return;
        }
        setArmed(false);
        remove();
      }}
    >
      <Icon name="trash" size={16} />
      <span>{armed ? "Точно?" : "Удалить"}</span>
    </button>
  );
}

/** Большая кнопка удаления внизу шага: тоже в два нажатия, как «Удалить» в шапке. */
function ConfirmDeleteButton({ label, resetKey, onConfirm }: { label: string; resetKey: string; onConfirm: () => void }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => { setArmed(false); }, [resetKey]);
  useEffect(() => {
    if (!armed) return;
    const timer = window.setTimeout(() => setArmed(false), 4000);
    return () => window.clearTimeout(timer);
  }, [armed]);
  return (
    <button className={`danger-button${armed ? " is-armed" : ""}`} type="button"
      aria-label={armed ? "Подтвердить удаление" : label}
      onClick={() => {
        if (!armed) { setArmed(true); return; }
        setArmed(false);
        onConfirm();
      }}>
      <Icon name="trash" size={17} />
      {armed ? "Точно удалить? Нажмите ещё раз" : label}
    </button>
  );
}

function PanelHeader({
  panel,
  family,
  project,
  onNavigate,
  backLabel = "К изделиям"
}: {
  panel: Panel;
  family: ProductFamily | null;
  project: PlannerProject;
  onNavigate: (panel: Panel) => void;
  /** С сайта за шкафом — «Шкафы на сайте» вместо выбора изделия (приёмка 22.10). */
  backLabel?: string;
}) {
  const [defaultTitle, defaultSubtitle] = panelTitles[panel];
  const title = panel === "overview" ? defaultTitle : workflowTitle(panel, family);
  const subtitle = family === "shelving" && panel === "shelving"
    ? "Габарит, число проёмов и способ установки"
    : family === "shelving" && panel === "internals"
    ? "Нажмите ячейку на схеме или в 3D и настройте её наполнение"
    : family === "shelving" && panel === "facades"
      ? "Выберите ячейку с дверцей и настройте её отделку"
      : family === "chest" && panel === "materials"
      ? "Выберите отделку корпуса комода"
      : family === "chest" && panel === "facades"
      ? "Отделка фасадов ящиков и ручки — общие для всего комода"
      : panel === "summary" ? "Все изделия в комнате, стоимость и документы" : defaultSubtitle;
  const workflowPanels = family ? productWorkflows[family] : [];
  const workflowIndex = workflowPanels.indexOf(panel as WorkflowPanel);
  const modules = family ? productModules(project, family) : [];
  const showSelection = panel !== "overview" && panel !== "summary" && panel !== "room" && panel !== "modules";
  return (
    <div
      className={`panel-header${workflowIndex >= 0 ? " panel-header--workflow" : ""}`}
    >
      <div className="panel-header__navigation">
        {panel !== "overview" && (
          <button
            className="back-button"
            type="button"
            title={backLabel === "К изделиям" ? "Вернуться к выбору типа изделия" : backLabel}
            onClick={() => onNavigate("overview")}
          >
            <Icon name="back" size={18} />
            <span className="back-button__text">{backLabel}</span>
          </button>
        )}
        {/* На низком экране заголовок шага — в строке с кнопкой «назад» (приёмка 22.10). */}
        {panel !== "overview" && <h2 className="panel-header__compact-title" aria-hidden="true">{title}</h2>}
        {workflowIndex >= 0 && (
          <span className="panel-step-count">
            Шаг {workflowIndex + 1} из {workflowPanels.length}
          </span>
        )}
      </div>
      <div className="panel-header__copy">
        {family && panel !== "overview" && <span className="product-family-label">{productFamilyTitles[family]}</span>}
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
      {family && workflowIndex >= 0 && (
        <WorkflowStepper
          panel={panel as WorkflowPanel}
          family={family}
          hasProducts={modules.length > 0}
          onNavigate={(nextPanel) => onNavigate(nextPanel)}
        />
      )}
      {showSelection && modules.length > 0 && <div className="product-selection-row">
        <label className="select-field compact product-selection">
          <span>{family === "shelving" ? "Редактируемая часть стеллажа" : family === "chest" ? "Редактируемый комод" : "Редактируемый корпус"}</span>
          <select value={project.selectedModuleId ?? ""} onChange={event => usePlannerStore.getState().selectModule(event.target.value)}>
            {!modules.some(module => module.id === project.selectedModuleId) && <option value="">Выберите изделие</option>}
            {modules.map((module, index) => <option key={module.id} value={module.id}>
              {index + 1}. {module.type === "corner45"
                ? `${cornerSide(module) === "left" ? "Левый" : "Правый"} угловой · ${cornerPlan(module).length} мм у ${cornerRun(module) === "back" ? "задней" : "боковой"} стены`
                : `${module.type === "shelving" && module.stackedOnId ? "Антресоль стеллажа" : module.shelvingKind === "chest" ? "Комод" : MODULE_NAMES[module.type]} · ${module.width} × ${module.height} мм`}
            </option>)}
          </select>
        </label>
        <DeleteProductButton project={project} />
      </div>}
    </div>
  );
}

function WorkflowFooter({
  panel,
  family,
  canContinue = true,
  onNavigate,
  backLabel = "К изделиям"
}: {
  panel: WorkflowPanel;
  family: ProductFamily;
  canContinue?: boolean;
  onNavigate: (panel: Panel) => void;
  backLabel?: string;
}) {
  const workflowPanels = productWorkflows[family];
  const currentIndex = workflowPanels.indexOf(panel);
  const previousPanel: Panel =
    currentIndex > 0
      ? (workflowPanels[currentIndex - 1] ?? "overview")
      : "overview";
  const nextPanel = workflowPanels[currentIndex + 1];
  const previousTitle =
    previousPanel === "overview"
      ? backLabel
      : workflowShortTitle(previousPanel, family);

  return (
    <div className="workflow-footer">
      <button
        className="workflow-footer__back"
        type="button"
        onClick={() => onNavigate(previousPanel)}
      >
        <Icon name="back" size={17} />
        <span>
          <small>{previousPanel === "overview" ? "Вернуться" : "Назад"}</small>
          <strong>{previousTitle}</strong>
        </span>
      </button>
      {nextPanel ? (
        <button
          className="workflow-footer__next"
          type="button"
          disabled={!canContinue}
          onClick={() => onNavigate(nextPanel)}
        >
          <span>
            Далее
            <strong>{workflowShortTitle(nextPanel, family)}</strong>
          </span>
          <Icon name="chevron" size={18} />
        </button>
      ) : (
        <button
          className="workflow-footer__next"
          type="button"
          onClick={() => onNavigate("overview")}
        >
          <span>
            Готово
            <strong>{backLabel}</strong>
          </span>
          <Icon name="chevron" size={18} />
        </button>
      )}
    </div>
  );
}

function ModuleSelectionPrompt() {
  return (
    <section className="panel-section">
      <div className="empty-state">
        <Icon name="cube" />
        <strong>Секция не выбрана</strong>
        <span>Выберите изделие в списке сверху или нажмите на него в сцене.</span>
      </div>
    </section>
  );
}

/**
 * Высоту ограничивает потолок комнаты (замечание Сергея № 5: «почему 2950
 * ограничено?»). Предел считается дважды — с этим потолком и с самым высоким;
 * если с высоким можно больше, мешает именно потолок.
 */
/** Подпись высоты основания по его виду (приёмка 22.10: «основание» было непонятно). */
const baseHeightLabel = (module: WardrobeModule) =>
  cabinetBaseType(module) === "plinth" ? "Высота цоколя" : cabinetBaseType(module) === "legs" ? "Высота ножек" : "Высота основания";

/** Что значит «цоколь утоплен»: схема сбоку (приёмка 22.10). */
function BaseSetbackHint({ value }: { value: number }) {
  return <div className="soft-note base-setback-hint">
    <svg width="132" height="96" viewBox="0 0 132 96" aria-hidden="true">
      <rect x="8" y="8" width="84" height="58" fill="#efe7da" stroke="#6b5a45" strokeWidth="1.4" />
      <text x="50" y="40" fontSize="10" textAnchor="middle" fill="#6b5a45">корпус</text>
      <rect x="92" y="8" width="7" height="62" fill="#d9c7ad" stroke="#6b5a45" strokeWidth="1.2" />
      <text x="102" y="20" fontSize="9" fill="#6b5a45">фасад</text>
      <rect x="8" y="66" width="68" height="16" fill="#cdb894" stroke="#6b5a45" strokeWidth="1.2" />
      <text x="42" y="78" fontSize="9" textAnchor="middle" fill="#4b3d2c">цоколь</text>
      <line x1="2" y1="82.5" x2="130" y2="82.5" stroke="#6b5a45" strokeWidth="1.5" />
      <line x1="76" y1="76" x2="99" y2="76" stroke="#2f7d32" strokeWidth="1.8" />
      <path d="M76 72v8M99 72v8" stroke="#2f7d32" strokeWidth="1.8" />
      <text x="88" y="94" fontSize="11" fontWeight="bold" textAnchor="middle" fill="#2f7d32">{value} мм</text>
    </svg>
    <span>Вид сбоку: насколько цоколь спрятан за фасад, чтобы ноги не упирались в цоколь.</span>
  </div>;
}

function ceilingLimit(project: PlannerProject, maxFor: (project: PlannerProject) => number) {
  const max = maxFor(project);
  const free = maxFor({ ...project, room: { ...project.room, ceilingHeight: MAX_CEILING_HEIGHT_MM } });
  return max < free ? { max, ceiling: ceilingHeightMm(project), gap: ceilingGapMm(project) } : null;
}

/** Почему выше нельзя — простыми словами, без «падуги» (приёмка 22.10). */
const ceilingReason = (limit: { ceiling: number; gap: number }) =>
  `потолок ${limit.ceiling} мм минус зазор ${limit.gap} мм (зазор меняется на шаге «Комната»)`;

/** Подсказка у высоты, когда до предела из-за потолка осталось немного. */
function CeilingLimitNote({ limit, value, onOpenRoom }: {
  limit: { max: number; ceiling: number; gap: number } | null;
  value: number;
  onOpenRoom?: () => void;
}) {
  if (!limit || limit.max - value > 400) return null;
  return <div className="soft-note ceiling-limit-note">
    Высота — до {limit.max} мм: {ceilingReason(limit)}.
    {onOpenRoom && <> <button type="button" className="ceiling-limit-note__link" onClick={onOpenRoom}>Поменять потолок на шаге «Комната»</button></>}
  </div>;
}

/** МДФ в эмали: покраска с одной стороны или с двух (замечание Сергея № 25). */
/** «Антресоль до потолка» (замечание Сергея № 11). */
function CabinetToCeilingToggle({ project, base, notify }: {
  project: PlannerProject;
  base: WardrobeModule;
  notify: (message: string) => void;
}) {
  const setCabinetToCeiling = usePlannerStore((state) => state.setCabinetToCeiling);
  const enabled = Boolean(base.cabinetToCeiling);
  const mezzanine = mezzanineFor(project, base.id);
  const ceiling = ceilingHeightMm(project);
  return <div className="module-lighting__row cabinet-to-ceiling">
    <button type="button" className={`module-lighting__toggle${enabled ? " is-selected" : ""}`} aria-pressed={enabled}
      onClick={() => {
        setCabinetToCeiling(base.id, !enabled);
        const after = usePlannerStore.getState().project.modules.find((module) => module.id === base.id);
        if (!enabled && !after?.cabinetToCeiling) notify("Потолок слишком низкий: шкаф до потолка не собрать");
      }}>
      <span className="cell-option-mark">{enabled ? "✓" : "+"}</span>
      <span><strong>Антресоль до потолка</strong>
        <small>{enabled
          ? `Корпус ${base.height} мм${mezzanine ? ` + антресоль ${mezzanine.height} мм` : ""} — до потолка ${ceiling} мм (под падугу). Поменяете потолок — шкаф подстроится.`
          : "Корпус максимальной высоты, антресоль добирает до потолка"}</small></span>
    </button>
  </div>;
}

function PaintSidesPicker({ twoSided, onChange }: {
  twoSided: boolean;
  onChange: (sides: "two" | undefined) => void;
}) {
  return <div className="paint-sides">
    <span className="field-caption">Покраска</span>
    <div className="segmented-control two" role="group" aria-label="Стороны покраски">
      <button type="button" className={!twoSided ? "is-selected" : ""} aria-pressed={!twoSided}
        onClick={() => onChange(undefined)}>С одной стороны</button>
      <button type="button" className={twoSided ? "is-selected" : ""} aria-pressed={twoSided}
        onClick={() => onChange("two")}>С двух сторон</button>
    </div>
    <small className="paint-sides__hint">С двух сторон дороже — в смете цена пока предварительная.</small>
  </div>;
}

function DimensionField({
  label,
  value,
  min,
  max,
  disabled = false,
  maxReason,
  minReason,
  onInvalid,
  onCommit
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  /** Почему выше нельзя — дописывается к сообщению о превышении (например, потолок комнаты). */
  maxReason?: string;
  /** Почему меньше нельзя (например, у стены стоят шкафы). */
  minReason?: string;
  onInvalid?: (message: string) => void;
  onCommit: (value: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  const [error, setError] = useState("");
  const onCommitRef = useRef(onCommit);
  useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);
  useEffect(() => {
    setDraft(String(value));
    setError("");
  }, [value, min, max]);

  useEffect(() => {
    if (disabled || draft.trim() === "") return;
    const parsed = Number(draft);
    if (
      !Number.isFinite(parsed) ||
      parsed < min ||
      parsed > max ||
      parsed === value
    ) {
      return;
    }
    const timer = window.setTimeout(() => {
      setError("");
      onCommitRef.current(parsed);
    }, 420);
    return () => window.clearTimeout(timer);
  }, [disabled, draft, max, min, value]);

  const rangeError = (parsed: number) => {
    if (parsed > max) return maxReason ? `Не больше ${max} мм — ${maxReason}` : `Превышено максимальное значение: ${max} мм`;
    if (parsed < min) return minReason ? `Не меньше ${min} мм — ${minReason}` : `Значение меньше минимального: ${min} мм`;
    return "";
  };

  const commit = (rawValue = draft) => {
    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed) || rawValue.trim() === "") {
      const message = "Введите числовое значение";
      setError(message);
      onInvalid?.(message);
      return;
    }
    const message = rangeError(parsed);
    if (message) {
      setError(message);
      onInvalid?.(message);
      return;
    }
    setError("");
    onCommitRef.current(parsed);
  };

  return (
    <label className={`dimension-field${error ? " is-invalid" : ""}`}>
      <span>{label}</span>
      <span className="dimension-field__control">
        <input
          type="number"
          value={draft}
          min={min}
          max={max}
          disabled={disabled}
          aria-invalid={Boolean(error)}
          title={error || undefined}
          onChange={(event) => {
            const next = event.target.value;
            setDraft(next);
            const parsed = Number(next);
            setError(
              next.trim() && Number.isFinite(parsed) ? rangeError(parsed) : ""
            );
          }}
          onBlur={(event) => commit(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.currentTarget.blur();
            }
          }}
        />
        <span>мм</span>
      </span>
    </label>
  );
}

function HandleGeometryControls({
  module,
  onChange
}: {
  module: WardrobeModule;
  onChange: (patch: Partial<WardrobeModule>) => void;
}) {
  if (module.handle === "push" || module.handle === "glassGrip" || module.door === "open") return null;
  const leaves = Math.max(1, doorLeafCount(module));
  const doorWidth = module.type === "corner45"
    ? cornerDoorWidthMm(module)
    : module.width / leaves;
  // Над наружными ящиками створка начинается выше — «от низа фасада» считаем от неё.
  const doorHeight = handleDoorHeightMm(module);
  const lengthRange = handleLengthRangeMm(module, doorWidth, doorHeight);
  const centerRange = handleCenterRangeMm(module, doorWidth, doorHeight);
  const edgeRange = handleEdgeOffsetRangeMm(module, doorWidth, doorHeight);
  const orientation = handleOrientation(module);
  if (module.handle === "integrated") {
    const style = integratedHandleStyle(module);
    return (
      <div className="handle-geometry-controls">
        <span className="eyebrow">Вид интегрированной ручки</span>
        <div className="segmented-control integrated-handle-styles" role="group" aria-label="Вид интегрированной ручки">
          {(["edge", "edgePart", "top"] as IntegratedHandleStyle[]).map((key) => (
            <button key={key} type="button" className={style === key ? "is-selected" : ""} aria-pressed={style === key}
              onClick={() => onChange({ integratedHandleStyle: key === "edge" ? undefined : key })}>
              {INTEGRATED_HANDLE_STYLES[key].name}
            </button>
          ))}
        </div>
        {style === "edgePart" && <div className="dimension-grid">
          <DimensionField
            label="Длина выборки"
            value={handleLengthMm(module, doorWidth, doorHeight)}
            min={lengthRange.min}
            max={lengthRange.max}
            onCommit={(handleLength) => onChange({ handleLength })}
          />
          <DimensionField
            label="Центр от низа фасада"
            value={handleCenterYmm(module, doorWidth, doorHeight)}
            min={centerRange.min}
            max={centerRange.max}
            onCommit={(handleCenterY) => onChange({ handleCenterY })}
          />
        </div>}
        <div className="soft-note">
          Паз под пальцы в торце фасада МДФ: спереди фасад гладкий, паз — за передней кромкой.
          {style === "edge" ? " Идёт по всей высоте створки со стороны открывания."
            : style === "edgePart" ? " Участок торца со стороны открывания — на удобной высоте."
            : " По верхнему краю створки — удобно у антресоли и низких дверей."}
          {" "}У ящиков выборка всегда по верхнему краю.
        </div>
      </div>
    );
  }

  return (
    <div className="handle-geometry-controls">
      <div className="shelving-controls__grid">
        <label className="select-field compact">
          <span>Ориентация</span>
          <select
            value={orientation}
            disabled={!handleSupportsOrientation(module.handle)}
            onChange={(event) =>
              onChange({
                handleOrientation: event.target.value as WardrobeModule["handleOrientation"],
                handleLength: undefined,
                handleCenterY: undefined,
                handleEdgeOffset: undefined
              })
            }
          >
            <option value="vertical">Вертикально</option>
            <option value="horizontal">Горизонтально</option>
          </select>
        </label>
        <DimensionField
          label="Длина ручки"
          value={handleLengthMm(module, doorWidth, doorHeight)}
          min={lengthRange.min}
          max={lengthRange.max}
          onCommit={(handleLength) => onChange({ handleLength })}
        />
      </div>
      <div className="dimension-grid">
        <DimensionField
          label="Центр от низа фасада"
          value={handleCenterYmm(module, doorWidth, doorHeight)}
          min={centerRange.min}
          max={centerRange.max}
          onCommit={(handleCenterY) => onChange({ handleCenterY })}
        />
        <DimensionField
          label="Отступ от края"
          value={handleEdgeOffsetMm(module, doorWidth, doorHeight)}
          min={edgeRange.min}
          max={edgeRange.max}
          onCommit={(handleEdgeOffset) => onChange({ handleEdgeOffset })}
        />
      </div>
      <div className="soft-note">
        Размеры задаются для каждой створки. В 3D и технической карте положение
        считается от нижней и открывающей кромок фасада.
      </div>
    </div>
  );
}

function ModuleCard({
  module,
  index,
  selected,
  hasWarning,
  wall,
  onSelect
}: {
  module: WardrobeModule;
  index: number;
  selected: boolean;
  hasWarning: boolean;
  wall: "left" | "back" | "right";
  onSelect: () => void;
}) {
  const wallName = {
    left: "Левая стена",
    back: "Задняя стена",
    right: "Правая стена"
  }[wall];
  return (
    <button
      className={`module-card${selected ? " is-selected" : ""}${hasWarning ? " has-warning" : ""}`}
      type="button"
      onClick={onSelect}
    >
      <span className="module-card__number">{index + 1}</span>
      <span>
        <strong>{module.type === "corner45" ? `${cornerSide(module) === "left" ? "Левый" : "Правый"} угловой шкаф` : moduleName(module)}</strong>
        <small>
          {module.type === "corner45"
            ? `${cornerPlan(module).length} мм у ${cornerRun(module) === "back" ? "задней" : "боковой"} стены · высота ${module.height} мм`
            : `${wallName} · ${module.width} × ${module.height} × ${module.depth} мм`}
        </small>
      </span>
      {hasWarning && <span className="warning-dot" aria-label="Есть замечание" />}
    </button>
  );
}

const selectedShelvingCellDetails = (
  project: PlannerProject,
  module: WardrobeModule
) => {
  const parsed = project.selectedShelvingCellId
    ? parseShelvingCellId(project.selectedShelvingCellId)
    : null;
  if (!parsed || parsed.moduleId !== module.id) return null;

  const grid = getShelvingGridLayout(module);
  const cell =
    module.shelvingCells?.find(
      (candidate) => candidate.id === project.selectedShelvingCellId
    ) ?? defaultShelvingCell(module.id, parsed.column, parsed.row);
  const diagonalSection = getShelvingDiagonalSections(module, grid).find(
    (section) => section.cell.id === cell.id
  );
  const mergedSection = getShelvingMergedCells(module, grid).find(
    (section) => section.cell.id === cell.id
  );
  const width = Math.round(
    diagonalSection?.width ?? mergedSection?.width ?? gridCellWidth(grid, parsed.row, parsed.column)
  );
  const height = Math.round(
    diagonalSection?.height ?? mergedSection?.height ?? grid.cellHeights[parsed.row] ?? 0
  );
  return { parsed, cell, width, height, grid, diagonalSection, mergedSection };
};

function ShelvingCellPrompt({ destination }: { destination: string }) {
  return (
    <div className="shelving-cell-prompt">
      <Icon name="shelf" size={18} />
      <span>
        Выберите ячейку прямо в 3D, чтобы настроить {destination}.
      </span>
    </div>
  );
}

function ShelvingCellShelfPositionField({
  moduleId,
  cellId,
  cellHeight,
  normalizedPosition
}: {
  moduleId: string;
  cellId: string;
  cellHeight: number;
  normalizedPosition?: number;
}) {
  const updateCell = usePlannerStore((state) => state.updateShelvingCell);
  const range = shelvingExtraShelfRange(cellHeight);
  const sliderMin = Math.ceil(range.min / 5) * 5;
  const sliderMax = Math.floor(range.max / 5) * 5;
  const resolvedPosition = Math.max(
    sliderMin,
    Math.min(
      sliderMax,
      Math.round(
        shelvingExtraShelfHeight(cellHeight, normalizedPosition) / 5
      ) * 5
    )
  );
  const [draftPosition, setDraftPosition] = useState(resolvedPosition);

  useEffect(() => {
    setDraftPosition(resolvedPosition);
  }, [cellId, resolvedPosition]);

  const commit = (position: number) => {
    const next = Math.max(sliderMin, Math.min(sliderMax, position));
    const nextNormalized = Number((next / cellHeight).toFixed(4));
    if (Math.abs(nextNormalized - (normalizedPosition ?? 0.5)) < 0.0005) {
      return;
    }
    updateCell(moduleId, cellId, {
      extraShelfPosition: nextNormalized
    });
  };

  return (
    <label className="range-field shelving-cell-shelf-position">
      <span>
        Высота отдельной полки <strong>{draftPosition} мм</strong>
      </span>
      <input
        type="range"
        min={sliderMin}
        max={sliderMax}
        step={5}
        value={draftPosition}
        onChange={(event) => setDraftPosition(Number(event.target.value))}
        onPointerUp={(event) => commit(Number(event.currentTarget.value))}
        onKeyUp={(event) => commit(Number(event.currentTarget.value))}
      />
      <small>От низа выбранной ячейки; полку также можно тянуть прямо в 3D.</small>
    </label>
  );
}

/** Подсказка у курсора: что можно сделать с деталью под ним. */
function boardHoverHint(ref: string, touch = false, customRow = false) {
  const grid = parseShelvingGridBoardRef(ref);
  // Ряд со своими ширинами (№ 19): его стойка двигается одна.
  const upright = customRow ? "Стойка этого ряда" : "Стойка";
  // На телефоне клавиш нет — подсказка только про палец.
  if (touch) {
    if (grid?.axis === "column") return `${upright} · тяните за ручку влево-вправо · коснитесь ещё раз — поворот`;
    if (grid?.axis === "row") return "Полка сетки · тяните за ручку вверх-вниз";
    return ref.startsWith("shelf")
      ? "Полка ячейки · тяните за ручку вверх-вниз"
      : "Перегородка ячейки · убрать её можно в панели ячейки";
  }
  if (grid?.axis === "column") return `${upright} · тяните влево-вправо · щелчок — поворот · Delete — объединить ячейки`;
  if (grid?.axis === "row") return "Полка сетки · тяните вверх-вниз · Delete — объединить ячейки";
  return ref.startsWith("shelf")
    ? "Полка ячейки · тяните вверх-вниз, Delete — удалить"
    : "Перегородка ячейки · Delete — удалить";
}

/** Все полки и перегородки ячейки: добавить, подсветить в 3D, удалить. */
function ShelvingCellBoardsList({ module, cell, width, height }: {
  module: WardrobeModule; cell: ShelvingCellConfig; width: number; height: number;
}) {
  const updateCell = usePlannerStore((state) => state.updateShelvingCell);
  const hovered = useShelvingBoardHover((state) => state.hovered);
  const setHovered = useShelvingBoardHover((state) => state.setHovered);
  const shelves = shelvingCellShelfYs(cell, height);
  const dividers = shelvingCellDividerXs(cell, width);
  const addShelf = withAddedShelvingBoard(cell, "shelf", width, height);
  const addGlassShelf = withAddedShelvingBoard(cell, "shelf", width, height, true);
  const addDivider = withAddedShelvingBoard(cell, "divider", width, height);
  const angled = cell.extraDivider && cell.extraDividerMode === "angled";
  const rows = [
    ...dividers.map((divider) => ({ ref: divider.ref, text: `Перегородка · ${Math.round(divider.x)} мм от левого края` })),
    ...[...shelves].reverse().map((shelf) => ({ ref: shelf.ref,
      text: `${shelf.glass ? "Стеклянная полка" : "Полка"} · ${Math.round(shelf.y)} мм от низа` }))
  ];
  const noShelfTitle = angled ? "С поворотной стойкой полки не ставятся" : "Места под ещё одну полку нет: между полками от 100 мм";
  const remove = (ref: string) => {
    updateCell(module.id, cell.id, withoutShelvingBoard(cell, ref));
    setHovered(null);
  };
  return <div className="cell-boards">
    <div className="cell-boards__heading">
      <strong>Полки и перегородки</strong>
      <small>Наведите на деталь в 3D — она выделится рамкой, Delete удалит её</small>
    </div>
    <div className="cell-boards__add">
      <button type="button" disabled={!addShelf || angled}
        title={!addShelf || angled ? noShelfTitle : undefined}
        onClick={() => addShelf && updateCell(module.id, cell.id, addShelf)}>+ Полка</button>
      <button type="button" disabled={!addGlassShelf || angled}
        title={!addGlassShelf || angled ? noShelfTitle : `Стекло ${SHELVING_GLASS_SHELF_THICKNESS_MM} мм — для витрин`}
        onClick={() => addGlassShelf && updateCell(module.id, cell.id, addGlassShelf)}>+ Стеклянная полка</button>
      <button type="button" disabled={!addDivider}
        title={addDivider ? undefined : "Места под ещё одну перегородку нет: проёмы от 100 мм"}
        onClick={() => addDivider && updateCell(module.id, cell.id, addDivider)}>+ Перегородка</button>
    </div>
    {rows.length > 0 && <ul className="cell-boards__list">
      {rows.map((row) => {
        const active = hovered?.cellId === cell.id && hovered.ref === row.ref;
        return <li key={row.ref} className={active ? "is-hovered" : ""}
          onMouseEnter={() => setHovered({ moduleId: module.id, cellId: cell.id, ref: row.ref })}
          onMouseLeave={() => setHovered(null)}>
          <span>{row.text}</span>
          <button type="button" aria-label={`Удалить: ${row.text}`} onClick={() => remove(row.ref)}>×</button>
        </li>;
      })}
    </ul>}
  </div>;
}

/** Подсветка ячейки: где стоит свет, ближе к кромке или к задней стенке, какой свет. */
function ShelvingCellLightingControls({ module, cell }: { module: WardrobeModule; cell: ShelvingCellConfig }) {
  const updateCell = usePlannerStore((state) => state.updateShelvingCell);
  const lighting = shelvingCellLighting(cell);
  const temperature = normalizeLightTemperature(cell.lightTemperature);
  const position = normalizeLightPosition(cell.lightPosition);
  const litCell = lighting === "none" ? undefined : shelvingLightingCells(module).find((item) => item.id === cell.id);
  const compartments = litCell?.compartments ?? 1;
  // Под непрозрачной доп. полкой своей ленты нет (10.10) — сказать, что снизу будет тень.
  const shadedBelowShelf = lighting === "horizontal" && Boolean(litCell) &&
    shelvingCellShelfYs(cell, litCell!.height).some((shelf) => !shelf.glass);
  return <div className="cell-boards cell-lighting">
    <div className="cell-boards__heading">
      <strong>Подсветка</strong>
      <small>{lighting === "none"
        ? "LED-лента в алюминиевом профиле; блок питания — один на стеллаж"
        : SHELVING_LIGHTING_HINTS[lighting]}</small>
    </div>
    <span className="field-caption">Где</span>
    <div className="cell-lighting__place" role="group" aria-label="Где стоит подсветка">
      {(["none", "horizontal", "vertical"] as const).map((value) => (
        <button key={value} type="button" className={lighting === value ? "is-selected" : ""} aria-pressed={lighting === value}
          onClick={() => updateCell(module.id, cell.id, { lighting: value })}>{SHELVING_LIGHTING_NAMES[value]}</button>
      ))}
    </div>
    {lighting !== "none" && <>
      <span className="field-caption">Ближе к</span>
      <div className="cell-lighting__place cell-lighting__place--two" role="group" aria-label="Положение подсветки по глубине">
        {(["front", "back"] as const).map((value) => (
          <button key={value} type="button" className={position === value ? "is-selected" : ""} aria-pressed={position === value}
            title={LIGHT_POSITION_HINTS[value]}
            onClick={() => updateCell(module.id, cell.id, { lightPosition: value })}>{LIGHT_POSITION_NAMES[value]}</button>
        ))}
      </div>
      <small className="cell-lighting__note">{LIGHT_POSITION_HINTS[position]}. {LIGHT_PROFILE_NOTE}.</small>
      {compartments > 1 && <small className="cell-lighting__note">
        Перегородки делят ячейку на {compartments} {compartments < 5 ? "отсека" : "отсеков"}: профиль ставится в каждый.
      </small>}
      {shadedBelowShelf && <small className="cell-lighting__note">
        Подсветка сверху — только под крышей ячейки: под непрозрачной полкой будет тень. Чтобы светилось до низа, выберите «В стойках» или стеклянную полку.
      </small>}
      <span className="field-caption">Свет</span>
      <div className="cell-lighting__temperature" role="group" aria-label="Цвет света">
        {(["warm", "cold"] as const).map((value) => (
          <button key={value} type="button" className={temperature === value ? "is-selected" : ""} aria-pressed={temperature === value}
            onClick={() => updateCell(module.id, cell.id, { lightTemperature: value })}>
            <i style={{ background: LIGHT_TEMPERATURES[value].css }} aria-hidden="true" />
            <span><strong>{LIGHT_TEMPERATURES[value].name}</strong><small>{LIGHT_TEMPERATURES[value].kelvin} K</small></span>
          </button>
        ))}
      </div>
    </>}
  </div>;
}

/**
 * Подсветка шкафа — как в ячейке стеллажа (замечание Сергея № 13): в полках
 * (под каждой своя лента) или в стойках, ближе к фасаду или к задней стенке.
 */
function CabinetLightingControls({ module }: { module: WardrobeModule }) {
  const updateModule = usePlannerStore((state) => state.updateModule);
  const mode = cabinetLightMode(module);
  const temperature = cabinetLightTemperature(module);
  const position = cabinetLightPosition(module);
  const hasShelves = module.items.some((item) => item.type === "shelf");
  return <div className="cell-boards cell-lighting">
    <div className="cell-boards__heading">
      <strong>Подсветка</strong>
      <small>{mode === "none"
        ? "LED-лента в алюминиевом профиле; блок питания — один на корпус"
        : CABINET_LIGHT_MODE_HINTS[mode]}</small>
    </div>
    <span className="field-caption">Где</span>
    <div className="cell-lighting__place" role="group" aria-label="Где стоит подсветка">
      {(["none", "shelves", "uprights"] as const).map((value) => (
        <button key={value} type="button" className={mode === value ? "is-selected" : ""} aria-pressed={mode === value}
          disabled={value === "shelves" && !hasShelves} title={value === "shelves" && !hasShelves ? "Сначала добавьте полку" : undefined}
          onClick={() => updateModule(module.id, withCabinetLightMode(module, value))}>{CABINET_LIGHT_MODE_NAMES[value]}</button>
      ))}
    </div>
    {mode !== "none" && <>
      <span className="field-caption">Ближе к</span>
      <div className="cell-lighting__place cell-lighting__place--two" role="group" aria-label="Положение подсветки по глубине">
        {(["front", "back"] as const).map((value) => (
          <button key={value} type="button" className={position === value ? "is-selected" : ""} aria-pressed={position === value}
            title={LIGHT_POSITION_HINTS[value]}
            onClick={() => updateModule(module.id, { cabinetLightPosition: value === "back" ? "back" : undefined })}>
            {value === "front" ? "К фасаду" : "К задней стенке"}</button>
        ))}
      </div>
      <small className="cell-lighting__note">{LIGHT_POSITION_HINTS[position]}. {LIGHT_PROFILE_NOTE}.</small>
      <span className="field-caption">Свет</span>
      <div className="cell-lighting__temperature" role="group" aria-label="Цвет света">
        {(["warm", "cold"] as const).map((value) => (
          <button key={value} type="button" className={temperature === value ? "is-selected" : ""} aria-pressed={temperature === value}
            onClick={() => updateModule(module.id, { cabinetLightTemperature: value === "cold" ? "cold" : undefined })}>
            <i style={{ background: LIGHT_TEMPERATURES[value].css }} aria-hidden="true" />
            <span><strong>{LIGHT_TEMPERATURES[value].name}</strong><small>{LIGHT_TEMPERATURES[value].kelvin} K</small></span>
          </button>
        ))}
      </div>
    </>}
  </div>;
}

/** Объединить ячейку с соседней с любой стороны (в том числе уже объединённой), разъединить обратно. */
function ShelvingCellMergeControls({ module, cell, merged }: {
  module: WardrobeModule; cell: ShelvingCellConfig;
  parsed: { column: number; row: number };
  merged: ReturnType<typeof getShelvingMergedCells>[number] | undefined;
}) {
  const mergeCells = usePlannerStore((state) => state.mergeShelvingCells);
  const splitCell = usePlannerStore((state) => state.splitShelvingCell);
  const [message, setMessage] = useState("");
  const columnSpan = merged?.columnSpan ?? 1;
  const rowSpan = merged?.rowSpan ?? 1;
  // Соседняя ячейка может быть уже объединённой — тогда она входит целиком.
  const sides = ([
    ["left", "← С левой"],
    ["right", "С правой →"],
    ["up", "↑ С верхней"],
    ["down", "↓ С нижней"]
  ] as const).map(([side, label]) => ({ side, label, ...shelvingMergeTowards(module, cell.id, side) }));
  const run = (area: { left: number; right: number; bottom: number; top: number }) => {
    // Объединённая получит настройки выбранной ячейки (или уже настроенной
    // соседней, если выбранная пустая). Остальные настройки пропадут — спросить заранее.
    const kept = shelvingMergeKeptCell(module.shelvingCells ?? [], area, cell.id);
    const lost = (module.shelvingCells ?? []).filter((candidate) => candidate.id !== kept?.id &&
      candidate.column >= area.left && candidate.column <= area.right &&
      candidate.row >= area.bottom && candidate.row <= area.top && shelvingCellIsConfigured(candidate));
    const keeps = !kept || kept.id === cell.id ? "выбранной" : `ячейки «колонка ${kept.column + 1} · ряд ${kept.row + 1}»`;
    if (lost.length && !window.confirm(`В объединение попадут настроенные ячейки (${lost.length}). Их фасады, полки и подсветка пропадут, останутся настройки ${keeps}. Объединить?`)) return;
    const problem = mergeCells(module.id, area, cell.id);
    setMessage(problem ?? `Ячейки объединены: ${area.right - area.left + 1} × ${area.top - area.bottom + 1}`);
  };
  return <div className="cell-merge">
    <div className="cell-boards__heading">
      <strong>Объединение ячеек</strong>
      <small>{merged ? `Объединено ${columnSpan} × ${rowSpan}: внутри нет полок и стоек сетки` : "Например, чтобы закрыть две ячейки общим фасадом"}</small>
    </div>
    <div className="cell-boards__add cell-merge__sides">
      {sides.map(({ side, label, area, problem }) => <button key={side} type="button"
        disabled={!area || Boolean(problem)} title={problem ?? undefined}
        onClick={() => area && run(area)}>{label}</button>)}
      {merged && <button type="button" className="cell-merge__split" onClick={() => { splitCell(module.id, cell.id); setMessage("Ячейка разъединена"); }}>Разъединить</button>}
    </div>
    {/* Причина неактивных кнопок — текстом, не только во всплывающей подсказке (приёмка 05.10, sc-07). */}
    {(() => {
      const reasons = sides.filter(({ area, problem }) => area && problem);
      return reasons.length ? <ul className="cell-merge__reasons">
        {reasons.map(({ side, label, problem }) => <li key={side}><b>{label.replace(/[←→↑↓]/g, "").trim()}</b>: {problem}</li>)}
      </ul> : null;
    })()}
    {message && <p className="shelving-bulk-apply__message" aria-live="polite">{message}</p>}
  </div>;
}

/**
 * Ширина ячейки (замечание Сергея № 19): во всех рядах или только в этом —
 * тогда у ряда своя раскладка, и его стойки в 3D тянутся отдельно.
 */
function ShelvingCellWidthControls({ module, grid, row, column, merged }: {
  module: WardrobeModule;
  grid: ShelvingGridLayout;
  row: number;
  column: number;
  merged?: { columnSpan: number; rowSpan: number };
}) {
  const setCellWidth = usePlannerStore((state) => state.setShelvingCellWidth);
  const resetRow = usePlannerStore((state) => state.resetShelvingRowColumns);
  const custom = Boolean(grid.rowCustomColumns[row]);
  const rowAllowed = shelvingKindAllowsRowColumns(module) && (merged?.rowSpan ?? 1) === 1;
  const [scope, setScope] = useState<"all" | "row">(custom ? "row" : "all");
  useEffect(() => setScope(custom ? "row" : "all"), [row, column, custom]);
  if (grid.columns < 2 || (merged?.columnSpan ?? 1) > 1) return null;
  const effectiveScope = rowAllowed ? scope : "all";
  const widths = effectiveScope === "row" ? grid.rowCellWidths[row] ?? grid.cellWidths : grid.cellWidths;
  const width = Math.round(widths[column] ?? 0);
  const neighbour = column < grid.columns - 1 ? column + 1 : column - 1;
  // Ни эта ячейка, ни соседняя не шире 1200 мм в свету (приёмка 04.10, sg-05).
  const pair = (widths[column] ?? 0) + (widths[neighbour] ?? 0);
  const maximum = Math.max(SHELVING_MIN_CELL_WIDTH_MM, Math.floor(Math.min(SHELVING_MAX_CLEAR_SPAN_MM, pair - SHELVING_MIN_CELL_WIDTH_MM)));
  const minimum = Math.min(maximum, Math.max(SHELVING_MIN_CELL_WIDTH_MM, Math.ceil(pair - SHELVING_MAX_CLEAR_SPAN_MM)));
  return <div className="shelving-cell-width">
    <span className="field-caption">Ширина ячейки</span>
    {rowAllowed && <div className="segmented-control two shelving-cell-width__scope">
      <button type="button" className={effectiveScope === "all" ? "is-selected" : ""} aria-pressed={effectiveScope === "all"}
        onClick={() => setScope("all")}>Во всех рядах</button>
      <button type="button" className={effectiveScope === "row" ? "is-selected" : ""} aria-pressed={effectiveScope === "row"}
        onClick={() => setScope("row")}>Только в этом ряду</button>
    </div>}
    <DimensionField label="В свету" value={width} min={minimum} max={maximum}
      maxReason={maximum >= SHELVING_MAX_CLEAR_SPAN_MM ? `полка шире ${SHELVING_MAX_CLEAR_SPAN_MM} мм прогнётся` : undefined}
      onCommit={(next) => setCellWidth(module.id, row, column, next, effectiveScope)} />
    <small className="shelving-cell-width__hint">{effectiveScope === "row"
      ? `Сдвигается стойка ${neighbour > column ? "справа" : "слева"} — только в ряду ${row + 1}. Стойки этого ряда в 3D тоже тянутся отдельно.`
      : custom
        ? `Меняется во всех рядах с общей раскладкой; у ряда ${row + 1} свои ширины, его это не затронет.`
        : `Сдвигается стойка ${neighbour > column ? "справа" : "слева"} во всех рядах, соседняя ячейка уступает место.`}
      {!rowAllowed && shelvingKindAllowsRowColumns(module) && " Ряд входит в объединённую по высоте ячейку — ширина у него общая."}</small>
    {custom && <button type="button" className="shelving-cell-width__reset" onClick={() => resetRow(module.id, row)}>
      Вернуть ряду общие ширины</button>}
  </div>;
}

function ShelvingCellInternalsEditor({
  project, module
}: { project: PlannerProject; module: WardrobeModule }) {
  const updateCell = usePlannerStore((state) => state.updateShelvingCell);
  const applyCellFilling = usePlannerStore((state) => state.applyShelvingCellFilling);
  const details = selectedShelvingCellDetails(project, module);
  const [mainDividerSide, setMainDividerSide] = useState<"left" | "right">("left");
  const [bulkMessage, setBulkMessage] = useState("");
  const setUprightAngle = usePlannerStore((state) => state.setShelvingUprightAngle);
  // null — выбрать само: «вся высота», если стойка повёрнута одинаково во всех рядах.
  const [uprightScope, setUprightScope] = useState<"whole" | "row" | null>(null);
  const uprightFocus = useShelvingUprightFocus((state) => state.focus);
  const uprightDetailsRef = useRef<HTMLDetailsElement>(null);
  // Раскрыт ли «Поворот стойки»: остаётся открытым, пока его не закроют сами,
  // даже когда стойку вернули «Прямо» (приёмка 11.10, sc-09). null — по умолчанию.
  const [uprightOpen, setUprightOpen] = useState<boolean | null>(null);
  useEffect(() => {
    if (!details) return;
    setMainDividerSide(details.parsed.column > 0 ? "left" : "right");
    setUprightScope(null);
    setUprightOpen(null);
  }, [details?.cell.id, details?.parsed.column]);
  useEffect(() => { if (uprightFocus) setUprightOpen(null); }, [uprightFocus?.stamp]);
  // Щелчок по стойке в 3D: показать её поворот.
  useEffect(() => {
    if (!uprightFocus || !details || uprightFocus.moduleId !== module.id) return;
    if (uprightFocus.dividerIndex !== details.parsed.column - 1) return;
    setMainDividerSide("left");
    window.setTimeout(() => uprightDetailsRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }), 60);
  }, [uprightFocus?.stamp]);
  if (!details) return null;
  const { parsed, cell, width, height, grid } = details;
  if (cell.content === "diagonal") return null;
  const patch = (next: Parameters<typeof updateCell>[2]) => updateCell(module.id, cell.id, next);
  const applyToScope = (scope: ShelvingBulkScope) => {
    const targetIds = shelvingBulkScopeIds(module, parsed, scope);
    const plan = shelvingBulkPlan(module, cell.id, targetIds);
    if (!plan.apply.length) {
      setBulkMessage("Для этого выбора нет подходящих ячеек.");
      return;
    }
    if (plan.overwritten.length > 0 && !window.confirm(
      `Будет заменено наполнение ${plan.overwritten.length} настроенных ячеек. Продолжить?`
    )) return;
    const applied = applyCellFilling(module.id, cell.id, targetIds);
    const skipped = plan.skipped.length
      ? ` ${plan.skipped.length} неподходящих ячеек пропущено.`
      : "";
    setBulkMessage(`Применено к ${applied} ${applied === 1 ? "ячейке" : "ячейкам"}.${skipped}`);
  };
  const clearWidth = details.mergedSection?.width ?? gridCellWidth(grid, parsed.row, parsed.column);
  const dividerRange = shelvingExtraDividerRange(clearWidth);
  const dividerPosition = shelvingExtraDividerPosition(clearWidth, cell.extraDividerPosition);
  const dividerMode = cell.extraDividerMode ??
    (Math.abs((cell.extraDividerPosition ?? 0.5) - 0.5) > 0.001 ? "offset" : "centered");
  const dividerBoard = getShelvingCellBoards(cell, clearWidth, height, module.depth)
    .find(board => board.kind === "divider");
  const dividerAngle = dividerBoard?.rotationY ?? 0;
  const dividerEdges = dividerBoard ? shelvingDividerEdgeCenters(dividerBoard) : null;
  const dividerMaxAngle = shelvingExtraDividerMaxAngle(clearWidth, module.depth, 0.5);
  const canAngleDivider = cell.content === "open" && dividerMaxAngle >= SHELVING_STANDARD_ANGLE_DEG;
  const hasMainDividerLeft = parsed.column > 0;
  const hasMainDividerRight = parsed.column < grid.columns - 1;
  const resolvedMainSide = mainDividerSide === "left" && hasMainDividerLeft
    ? "left"
    : hasMainDividerRight
      ? "right"
      : "left";
  const mainDividerIndex = resolvedMainSide === "left"
    ? parsed.column - 1
    : parsed.column;
  const mainDivider = mainDividerIndex >= 0 && mainDividerIndex < grid.columns - 1
    ? getShelvingMainDividerSegment(module, mainDividerIndex, parsed.row, grid)
    : null;
  // Поворот стойки: по умолчанию на всю высоту — во всех рядах, где по обе
  // стороны открытые ячейки. «Только этот ряд» — участок рядом с ячейкой.
  const uprightRows = mainDivider
    ? shelvingUprightRows(module, mainDividerIndex, grid)
    : { visible: [] as number[], rotatable: [] as number[], maximum: 0 };
  const rowSegment = (row: number) => getShelvingMainDividerSegment(module, mainDividerIndex, row, grid);
  const rotatableAngles = uprightRows.rotatable.map((row) => rowSegment(row)?.rotationY ?? 0);
  const uniformUpright = rotatableAngles.every((angle) => Math.abs(angle - (rotatableAngles[0] ?? 0)) < 0.5);
  const uprightScopeResolved = uprightScope ?? (uniformUpright ? "whole" : "row");
  const currentRowRotatable = uprightRows.rotatable.includes(parsed.row);
  const displaySegment = uprightScopeResolved === "whole" && !currentRowRotatable && uprightRows.rotatable.length
    ? rowSegment(uprightRows.rotatable[0]!)
    : mainDivider;
  const mainDividerAngle = displaySegment?.rotationY ?? 0;
  const mainDividerEdges = displaySegment ? shelvingDividerEdgeCenters(displaySegment) : null;
  // Наклонная стойка слева или справа от ячейки (во всех её рядах) или перегородка под углом внутри.
  const cellRows = Array.from({ length: Math.max(1, cell.mergeRowSpan ?? 1) }, (_, index) => parsed.row + index);
  const slantedUpright = [parsed.column - 1, parsed.column + Math.max(1, cell.mergeColumnSpan ?? 1) - 1]
    .filter((index) => index >= 0 && index < grid.columns - 1)
    .some((index) => cellRows.some((row) => Math.abs(getShelvingMainDividerSegment(module, index, row, grid)?.rotationY ?? 0) >= 0.5));
  const slantBlock = slantedUpright
    ? "Фасад и ящики ставятся только в ячейку с прямыми стойками. Рядом стойка под углом — сначала выпрямите её («Поворот стойки» → «Прямо»)."
    : cell.extraDivider && dividerMode === "angled"
      ? "Фасад и ящики ставятся только в ячейку без перегородки под углом — сначала поставьте её прямо."
      : null;
  const mainDividerRearFromLeft = (mainDividerEdges?.rear ?? displaySegment?.x ?? 0) + module.width / 2;
  const mainDividerFrontFromLeft = (mainDividerEdges?.front ?? displaySegment?.x ?? 0) + module.width / 2;
  const mainDividerMaxAngle = uprightRows.maximum;
  const canRotateMainDivider = mainDividerMaxAngle >= SHELVING_STANDARD_ANGLE_DEG && (uprightScopeResolved === "whole"
    ? uprightRows.rotatable.length > 0
    : currentRowRotatable);
  const straightRows = uprightRows.visible.filter((row) => !uprightRows.rotatable.includes(row));
  const rotationRestriction = mainDividerMaxAngle < SHELVING_STANDARD_ANGLE_DEG
    ? `Соседние проёмы слишком узкие для поворота на ${SHELVING_STANDARD_ANGLE_DEG}°.`
    : uprightScopeResolved === "whole"
      ? "Повернуть можно только там, где по обе стороны открытые ячейки, — у этой стойки таких рядов нет."
      : "Под углом можно поставить только участок между двумя открытыми ячейками — без фасадов и ящиков.";
  const patchMainDivider = (angle: number) =>
    setUprightAngle(module.id, mainDividerIndex, angle, uprightScopeResolved === "row" ? parsed.row : undefined);
  const uprightFocused = Boolean(uprightFocus && uprightFocus.moduleId === module.id &&
    uprightFocus.dividerIndex === mainDividerIndex && resolvedMainSide === "left");
  const rowList = (rows: number[]) => rows.map((row) => row + 1).join(", ");
  return (
    <div className="shelving-cell-editor">
      <div className="section-heading">
        <div><span className="eyebrow">Выбранная ячейка</span>
          <h3>Колонка {parsed.column + 1} · ряд {parsed.row + 1} от пола</h3></div>
        <span className="section-metric">{width} × {height} мм</span>
      </div>
      <ShelvingCellWidthControls module={module} grid={grid} row={parsed.row} column={parsed.column}
        merged={details.mergedSection ?? undefined} />
      {mainDivider && <details ref={uprightDetailsRef} className="product-advanced"
        key={`${cell.id}-main-divider-${uprightFocused ? uprightFocus?.stamp : "idle"}`}
        open={uprightOpen ?? (uprightFocused || Math.abs(mainDividerAngle) >= 0.5)}
        onToggle={(event) => setUprightOpen(event.currentTarget.open)}>
        <summary>Поворот стойки</summary>
        <div className="shelving-main-divider-editor">
        <div className="shelving-main-divider-heading">
          <span><strong>{resolvedMainSide === "left" ? "Стойка слева от ячейки" : "Стойка справа от ячейки"}</strong>
            <small>{uprightScopeResolved === "whole"
              ? "Поворачивается на всю высоту — во всех рядах с открытыми ячейками"
              : "Только участок в этом ряду, выше и ниже стойка прямая"}</small></span>
          <b>{Math.abs(mainDividerAngle) >= 0.5 ? "под углом" : "прямая"}</b>
        </div>
        {hasMainDividerLeft && hasMainDividerRight && <div className="segmented-control shelving-main-divider-side">
          <button type="button" className={resolvedMainSide === "left" ? "is-selected" : ""}
            aria-pressed={resolvedMainSide === "left"} onClick={() => setMainDividerSide("left")}>Стойка слева</button>
          <button type="button" className={resolvedMainSide === "right" ? "is-selected" : ""}
            aria-pressed={resolvedMainSide === "right"} onClick={() => setMainDividerSide("right")}>Стойка справа</button>
        </div>}
        <div className="segmented-control two shelving-main-divider-scope">
          <button type="button" className={uprightScopeResolved === "whole" ? "is-selected" : ""}
            aria-pressed={uprightScopeResolved === "whole"}
            onClick={() => {
              setUprightScope("whole");
              // Выбрали «всю высоту» у повёрнутого участка — повернуть и остальные ряды.
              if (Math.abs(mainDividerAngle) >= 0.5) setUprightAngle(module.id, mainDividerIndex, mainDividerAngle);
            }}>Вся высота</button>
          <button type="button" className={uprightScopeResolved === "row" ? "is-selected" : ""}
            aria-pressed={uprightScopeResolved === "row"} onClick={() => setUprightScope("row")}>Только этот ряд</button>
        </div>
        <div className="segmented-control two shelving-main-divider-mode">
          <button type="button" className={Math.abs(mainDividerAngle) < 0.5 ? "is-selected" : ""}
            aria-pressed={Math.abs(mainDividerAngle) < 0.5} onClick={() => patchMainDivider(0)}>Прямо</button>
          <button type="button" className={Math.abs(mainDividerAngle) >= 0.5 ? "is-selected" : ""}
            aria-pressed={Math.abs(mainDividerAngle) >= 0.5} disabled={!canRotateMainDivider}
            title={!canRotateMainDivider ? rotationRestriction : undefined}
            onClick={() => patchMainDivider(SHELVING_STANDARD_ANGLE_DEG)}>Под углом</button>
        </div>
        {Math.abs(mainDividerAngle) >= 0.5 && <>
          <div className="shelving-divider-plan" aria-label="Стойка, вид сверху">
            <span>стена</span>
            <svg viewBox="0 0 160 72" aria-hidden="true">
              <path d="M 8 8 H 152" />
              <rect x="76" y="12" width="8" height="50" rx="2"
                transform={`rotate(${-mainDividerAngle}, 80, 37)`} />
            </svg>
            <small>фасадная сторона</small>
          </div>
          <span className="field-caption">Куда выходит передняя кромка</span>
          <div className="segmented-control shelving-divider-direction">
            <button type="button" className={mainDividerAngle < 0 ? "is-selected" : ""}
              aria-pressed={mainDividerAngle < 0}
              onClick={() => patchMainDivider(-SHELVING_STANDARD_ANGLE_DEG)}>← Влево</button>
            <button type="button" className={mainDividerAngle >= 0 ? "is-selected" : ""}
              aria-pressed={mainDividerAngle >= 0}
              onClick={() => patchMainDivider(SHELVING_STANDARD_ANGLE_DEG)}>Вправо →</button>
          </div>
          <div className="soft-note">Ось у стены — {Math.round(mainDividerRearFromLeft)} мм,
            спереди — {Math.round(mainDividerFrontFromLeft)} мм от левого края стеллажа.</div>
          {uprightScopeResolved === "whole" && straightRows.length > 0 && <small className="shelving-main-divider-restriction">
            {straightRows.length === 1 ? `В ряду ${rowList(straightRows)}` : `В рядах ${rowList(straightRows)}`} от пола
            стойка остаётся прямой: рядом фасад, ящики или объединённая ячейка.
          </small>}
        </>}
        {!canRotateMainDivider && Math.abs(mainDividerAngle) < 0.5 && <small className="shelving-main-divider-restriction">
          {rotationRestriction}
        </small>}
      </div></details>}
      <span className="field-caption">Что находится в ячейке</span>
      <div className="shelving-content-switch">
        {([["open", "Открытая"], ["door", "Фасад"], ["drawers", "Ящики"]] as const).map(([value, label]) => {
          const tooSmall = !shelvingCellFits(value, width, height);
          // Фасад и ящики — только между прямыми стойками (видео Сергея 24.10): стойку не выпрямляем молча.
          const slanted = value !== "open" && cell.content === "open" && slantBlock;
          const disabled = tooSmall || Boolean(slanted);
          return <button className={cell.content === value ? "is-selected" : ""} type="button" key={value}
            disabled={disabled} title={tooSmall ? "Ячейка слишком маленькая" : slanted ? slantBlock! : undefined}
            onClick={() => patch({ content: value,
              ...(value === "drawers" ? { extraShelf: false, extraDivider: false } : {}),
              ...(value !== "open" && dividerMode === "angled"
                ? { extraDividerMode: "centered", extraDividerAngle: 0 }
                : {}),
              // Стекло и зеркало на ящик не ставят.
              ...(value === "drawers" && (cell.facade === "glass" || cell.facade === "mirror")
                ? cellFacadeTypePatch("wood", cell.handle) : {}) })}>
            <span className="shelving-content-switch__mark" aria-hidden="true">
              {value === "open" ? "□" : value === "door" ? "▯" : "☰"}
            </span>
            <span><strong>{label}</strong><small>{value === "open" ? "Без фасада" : value === "door" ? "Дверца или откидной" : `От ${SHELVING_DRAWER_MIN_FRONT_MM} мм`}</small></span>
          </button>;
        })}
      </div>
      {cell.content === "open" && slantBlock && <small className="shelving-main-divider-restriction shelving-slant-block">{slantBlock}</small>}
      {(cell.content === "door" || cell.content === "drawers") && <div className="shelving-cell-hardware">
        <ShelvingCellFacadeMaterial cell={cell} onPatch={patch} />
        {cell.content === "door" && (() => {
          const rect = shelvingCellFrontRect(module, cell.id, grid);
          return <CellFrontOpeningPicker cell={cell} widthMm={rect?.width ?? width} heightMm={rect?.height ?? height}
            headroomMm={shelvingCellHeadroomMm(project, module, cell.id, grid)} excludedLifts={shelvingExcludedLifts(module)} onPatch={patch} />;
        })()}
        <label className="select-field compact shelving-handle-field">
          <span>Открывание</span>
          <select value={cell.handle ?? "bar"}
            onChange={event => patch({ handle: event.target.value as HandleType })}>
            {handlesForFacade(cell.facade ?? "wood").map(([key, handle]) =>
              <option value={key} key={key}>{handle.name}</option>)}
          </select>
        </label>
        <HandleChoiceNote handle={cell.handle} />
        <small className="shelving-cell-hardware__hint">Декор, цвет и фрезеровка фасада — на шаге «Фасады».</small>
      </div>}
      <div className="shelving-cell-options">
        <button className={cell.backPanel || module.shelvingBackPanel ? "is-selected" : ""} type="button"
          disabled={Boolean(module.shelvingBackPanel)} onClick={() => patch({ backPanel: !cell.backPanel })}>
          <span className="cell-option-mark">{cell.backPanel || module.shelvingBackPanel ? "✓" : "+"}</span>
          <span><strong>Стенка ячейки</strong><small>{module.shelvingBackPanel ? "Включена для всего стеллажа" : "8 мм в декоре корпуса"}</small></span>
        </button>
      </div>
      {cell.content !== "drawers" && <ShelvingCellBoardsList module={module} cell={cell}
        width={clearWidth} height={details.mergedSection?.height ?? height} />}
      {cell.content !== "drawers" && <ShelvingCellLightingControls module={module} cell={cell} />}
      <ShelvingCellMergeControls module={module} cell={cell} parsed={parsed} merged={details.mergedSection} />
      {cell.extraDivider && dividerPosition !== null && cell.content !== "drawers" && <>
        <div className="shelving-divider-editor">
          <span className="field-caption">Как стоит перегородка</span>
          <div className="segmented-control shelving-divider-mode">
            <button type="button" className={dividerMode === "centered" ? "is-selected" : ""}
              aria-pressed={dividerMode === "centered"}
              onClick={() => patch({ extraDividerMode: "centered", extraDividerPosition: 0.5, extraDividerAngle: 0 })}>По центру</button>
            <button type="button" className={dividerMode === "offset" ? "is-selected" : ""}
              aria-pressed={dividerMode === "offset"}
              onClick={() => patch({ extraDividerMode: "offset",
                extraDividerPosition: Math.abs((cell.extraDividerPosition ?? 0.5) - 0.5) > 0.01
                  ? cell.extraDividerPosition : 0.35, extraDividerAngle: 0 })}>Со смещением</button>
            <button type="button" className={dividerMode === "angled" ? "is-selected" : ""}
              aria-pressed={dividerMode === "angled"}
              disabled={!canAngleDivider}
              title={!canAngleDivider ? cell.content !== "open" ? "Поворот доступен только в открытой ячейке" : `Ячейка слишком узкая для поворота на ${SHELVING_STANDARD_ANGLE_DEG}°` : undefined}
              onClick={() => patch({ extraDividerMode: "angled", extraDividerPosition: 0.5,
                extraDividerAngle: SHELVING_STANDARD_ANGLE_DEG, extraShelf: false })}>Поворот</button>
          </div>
          {dividerMode === "offset" && <DimensionField label="Ось перегородки слева" value={Math.round(dividerPosition)}
            min={Math.ceil(dividerRange.min)} max={Math.floor(dividerRange.max)}
            onCommit={position => patch({ extraDividerPosition: position / clearWidth })} />}
          {dividerMode === "angled" && <>
            <div className="shelving-divider-plan" aria-label="Положение перегородки, вид сверху">
              <span>стена</span>
              <svg viewBox="0 0 160 72" aria-hidden="true">
                <path d="M 8 8 H 152" />
                <rect x="76" y="12" width="8" height="50" rx="2"
                  transform={`rotate(${-dividerAngle}, 80, 37)`} />
              </svg>
              <small>фасадная сторона</small>
            </div>
            <span className="field-caption">Куда выходит передняя кромка</span>
            <div className="segmented-control shelving-divider-direction">
              <button type="button" className={dividerAngle < 0 ? "is-selected" : ""}
                aria-pressed={dividerAngle < 0}
                onClick={() => patch({ extraDividerAngle: -SHELVING_STANDARD_ANGLE_DEG })}>← Влево</button>
              <button type="button" className={dividerAngle >= 0 ? "is-selected" : ""}
                aria-pressed={dividerAngle >= 0}
                onClick={() => patch({ extraDividerAngle: SHELVING_STANDARD_ANGLE_DEG })}>Вправо →</button>
            </div>
          </>}
          <div className="soft-note">{dividerMode === "angled"
            ? `Стойка повёрнута вокруг вертикальной оси. Ось у стены — ${Math.round(dividerEdges?.rear ?? dividerPosition)} мм, спереди — ${Math.round(dividerEdges?.front ?? dividerPosition)} мм от левой кромки.`
            : dividerMode === "offset"
              ? `Проёмы: ${Math.round(dividerPosition - CARCASS_MM / 2)} мм слева и ${Math.round(clearWidth - dividerPosition - CARCASS_MM / 2)} мм справа.`
              : "Стойка проходит по центру ячейки и делит её на два одинаковых проёма."}</div>
        </div>
      </>}
      {cell.extraShelf && cell.content !== "drawers" && <ShelvingCellShelfPositionField
        moduleId={module.id} cellId={cell.id} cellHeight={height} normalizedPosition={cell.extraShelfPosition} />}
      {cell.content === "drawers" && (() => {
        // Высокий проём нельзя закрыть одним огромным ящиком: фасад выше 500 мм делим.
        // Фасад ниже 250 мм не даёт нормального полезного объёма и места направляющим.
        const range = shelvingDrawerCountRange(height, drawerLimits(module));
        const actual = shelvingDrawerCount(cell.drawerCount, height, drawerLimits(module));
        const front = Math.round(height / actual);
        return <>
          <div className="shelving-drawer-control">
            <span className="field-caption">Количество ящиков</span>
            <div className="shelving-drawer-stepper" role="group" aria-label="Количество ящиков">
              <button type="button" aria-label="Уменьшить количество ящиков" disabled={actual <= range.min}
                onClick={() => patch({ drawerCount: actual - 1 })}>−</button>
              <span><strong>{actual}</strong><small>{cellDrawerWord(actual)} · фасад {front} мм</small></span>
              <button type="button" aria-label="Увеличить количество ящиков" disabled={actual >= range.max}
                onClick={() => patch({ drawerCount: actual + 1 })}>+</button>
            </div>
          </div>
          <div className="soft-note">Допустимо: от {range.min} до {range.max}. Высота одного фасада — {SHELVING_DRAWER_MIN_FRONT_MM}–{SHELVING_DRAWER_MAX_FRONT_MM} мм.</div>
        </>;
      })()}
      <div className="shelving-bulk-apply">
        <div className="shelving-bulk-apply__heading">
          <span><strong>Повторить это наполнение</strong><small>Копируются тип, полка, стенка, фасад и фурнитура</small></span>
        </div>
        <div className="shelving-bulk-apply__buttons">
          {([[
            "row", "Весь ряд"
          ], [
            "column", "Вся колонка"
          ], [
            "all", "Весь стеллаж"
          ]] as const).map(([scope, label]) => {
            const targetIds = shelvingBulkScopeIds(module, parsed, scope);
            const plan = shelvingBulkPlan(module, cell.id, targetIds);
            return <button type="button" key={scope} disabled={!plan.apply.length}
              onClick={() => applyToScope(scope)}>
              <strong>{label}</strong><small>{plan.apply.length} яч.</small>
            </button>;
          })}
        </div>
        {bulkMessage && <p className="shelving-bulk-apply__message" aria-live="polite">{bulkMessage}</p>}
      </div>
      <button className="shelving-cell-reset" type="button"
        onClick={() => patch(defaultShelvingCell(module.id, parsed.column, parsed.row))}>Очистить ячейку</button>
    </div>
  );
}

function FacadePatternPicker({
  value,
  onChange,
  compact = false
}: {
  value: FacadePatternType;
  onChange: (pattern: FacadePatternType) => void;
  compact?: boolean;
}) {
  const prices = usePriceStore((state) => state.values);
  return (
    <div className={`facade-pattern-picker${compact ? " is-compact" : ""}`}>
      <span className="field-caption">Фрезеровка фасада</span>
      <div className="facade-pattern-grid">
        {availableFacadePatterns.map((key) => {
          const pattern = FACADE_PATTERNS[key];
          return (
            <button
              className={value === key ? "is-selected" : ""}
              type="button"
              key={key}
              onClick={() => onChange(key)}
            >
              <span className={`facade-pattern-sample is-${key}`}>
                {(key === "art009" ||
                  key === "art012" ||
                  key === "art023") && <span />}
              </span>
              <span className="facade-pattern-copy">
                <strong>{pattern.name}</strong>
                <small>
                  {pattern.subtitle}
                  {key !== "plain" && priceValue(prices, priceKeys.pattern(key))
                    ? ` · +${formatPrice(priceValue(prices, priceKeys.pattern(key))!)} ₽ / м²`
                    : ""}
                </small>
              </span>
              <span className="radio-mark" />
            </button>
          );
        })}
      </div>
      <div className="soft-note facade-pattern-note">
        Рельеф строится настоящей 3D-геометрией: канавки, фаски и профили
        реагируют на освещение. Производственную карту ЧПУ утверждает технолог.
      </div>
    </div>
  );
}

/**
 * Цвет эмали (Сергей, 28.10): только код NCS или выбор в палитре — без
 * готовых цветов и «экранного образца». Код NCS сразу перекрашивает 3D.
 */
function EnamelColorPicker({
  hex,
  code,
  onChange
}: {
  hex: string;
  code?: string;
  onChange: (patch: { hex?: string; code?: string }) => void;
}) {
  const [draft, setDraft] = useState(code ?? "");
  useEffect(() => setDraft(code ?? ""), [code]);
  const text = draft.trim();
  const converted = text ? ncsToHex(text) : null;
  const typeCode = (value: string) => {
    setDraft(value);
    const next = ncsToHex(value);
    if (next) onChange({ code: value.trim(), hex: next });
  };
  const commitCode = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) { if (code) onChange({ code: undefined }); return; }
    const next = ncsToHex(trimmed);
    onChange(next ? { code: formatNcs(trimmed), hex: next } : { code: trimmed });
  };
  return (
    <div className="enamel-picker">
      <label className="text-field paint-code-field">
        <span>Код цвета NCS</span>
        <input
          aria-label="Код цвета NCS"
          value={draft}
          maxLength={32}
          placeholder="Например, NCS S 2005-Y50R"
          onChange={(event) => typeCode(event.target.value)}
          onBlur={(event) => commitCode(event.target.value)}
        />
      </label>
      <label className="enamel-picker__palette">
        <input
          aria-label="Выбрать цвет в палитре"
          type="color"
          value={hex.toLowerCase()}
          onChange={(event) => onChange({ hex: event.target.value.toUpperCase(), code: undefined })}
        />
        <i style={{ background: hex }} aria-hidden="true" />
        <span>Выбрать в палитре</span>
      </label>
      <small className="enamel-picker__note">
        {text && !converted
          ? "Код не похож на NCS — цвет в 3D не изменится, в заказ уйдёт как написан."
          : "Цвет на экране приблизительный: перед покраской сверяют с веером NCS."}
      </small>
    </div>
  );
}

function ShelvingCellFacadesEditor({
  project,
  module,
  applyToAll = false
}: {
  project: PlannerProject;
  module: WardrobeModule;
  /** Комод: одна отделка и ручка для всех ящиков, образец — первая колонка. */
  applyToAll?: boolean;
}) {
  const updateCell = usePlannerStore((state) => state.updateShelvingCell);
  const updateAllCells = usePlannerStore((state) => state.updateAllShelvingCells);
  // Комод: образец общей отделки — первая колонка с ящиками или нестеклянной дверцей.
  const sampleId = applyToAll ? chestFinishCell(module)?.id ?? shelvingCellId(module.id, 0, 0) : null;
  const details = sampleId
    ? selectedShelvingCellDetails({ ...project, selectedShelvingCellId: sampleId }, module)
    : selectedShelvingCellDetails(project, module);
  if (!details) return <ShelvingCellPrompt destination="её фасад" />;
  const { parsed, cell, width, height } = details;
  const patch = (next: Parameters<typeof updateCell>[2]) =>
    applyToAll ? updateAllCells(module.id, next) : updateCell(module.id, cell.id, next);
  const facade = cell.facade ?? "wood";
  const facadeDecor =
    cell.facadeDecor ?? module.decor ?? project.carcassDecor;
  const facadeColor = cell.facadeColor ?? "white";
  const facadePattern = cell.facadePattern ?? "plain";

  return (
    <div className="shelving-cell-editor">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{applyToAll ? "Фасады комода" : "Фасад выбранной ячейки"}</span>
          <h3>
            {applyToAll
              ? (module.shelvingCells ?? []).some(isChestGlassDoor) ? "Для всех фасадов, кроме стеклянных" : "Для всех ящиков и фасадов комода"
              : `Колонка ${parsed.column + 1} · ряд ${parsed.row + 1} от пола`}
          </h3>
        </div>
        {!applyToAll && <span className="section-metric">{width} × {height} мм</span>}
      </div>

      {cell.content === "diagonal" ? (
        <div className="soft-note shelving-cell-empty-note">
          На диагональную секцию фасад не устанавливается. Верните секцию в режим «Открытая», чтобы добавить фасад или ящики.
        </div>
      ) : cell.content === "open" ? (
        <>
          <div className="soft-note shelving-cell-empty-note">Ячейка открытая — поставьте на неё фасад или ящики.</div>
          <div className="shelving-content-switch shelving-content-switch--two">
            {([["door", "Поставить фасад", "Одна дверца"], ["drawers", "Ящики", `От ${SHELVING_DRAWER_MIN_FRONT_MM} мм`]] as const).map(([value, label, hint]) => {
              const disabled = !shelvingCellFits(value, width, height);
              return <button type="button" key={value} disabled={disabled}
                title={disabled ? "Ячейка слишком маленькая" : undefined}
                onClick={() => patch({ content: value,
                  ...(value === "drawers" ? { extraShelf: false, extraDivider: false } : {}),
                  ...(cell.extraDividerMode === "angled" ? { extraDividerMode: "centered" as const, extraDividerAngle: 0 } : {}),
                  // Стекло и зеркало на ящик не ставят.
                  ...(value === "drawers" && (cell.facade === "glass" || cell.facade === "mirror")
                    ? cellFacadeTypePatch("wood", cell.handle) : {}) })}>
                <span className="shelving-content-switch__mark" aria-hidden="true">{value === "door" ? "▯" : "☰"}</span>
                <span><strong>{label}</strong><small>{hint}</small></span>
              </button>;
            })}
          </div>
        </>
      ) : (
        <>
          <span className="field-caption">Материал фасада</span>
          <div className="choice-list compact-choice-list">
            {(applyToAll || cell.content === "drawers" ? chestFacadeTypes : availableFacadeTypes).map((key) => (
              <button
                className={facade === key ? "is-selected" : ""}
                type="button"
                key={key}
                onClick={() => patch(cellFacadeTypePatch(key, cell.handle))}
              >
                <span>
                  <strong>{FACADES[key].name}</strong>
                  <small>{FACADES[key].subtitle}</small>
                </span>
                <span className="radio-mark" />
              </button>
            ))}
          </div>

          {facade === "wood" && (
            <div className="soft-note">
              ЛДСП выпускается гладким. Для объёмной фрезеровки выберите
              МДФ в эмали.
            </div>
          )}

          {facade === "wood" && (
            <div className="shelving-facade-options">
              <DecorPicker
                label="Декор фасада"
                value={facadeDecor}
                onChange={(code) =>
                  patch({
                    facadeDecor: code,
                    facadeMaterial: decorPriceMaterial(code)
                  })
                }
              />
            </div>
          )}

          {(facade === "satin" || facade === "gloss") && (
            <div className="shelving-facade-options">
              <EnamelColorPicker
                hex={facadePaintHex({ facadeColor, facadeColorHex: cell.facadeColorHex, facadeColorCode: cell.facadeColorCode })}
                code={cell.facadeColorCode}
                onChange={(color) => patch({
                  ...("hex" in color ? { facadeColorHex: color.hex } : {}),
                  ...("code" in color ? { facadeColorCode: color.code } : {})
                })}
              />
              <PaintSidesPicker twoSided={cell.facadePaintSides === "two"}
                onChange={(facadePaintSides) => patch({ facadePaintSides })} />
            </div>
          )}

          {facadeSupportsPattern(facade) && (
            <FacadePatternPicker
              compact
              value={facadePattern}
              onChange={(pattern) => patch({ facadePattern: pattern })}
            />
          )}

          {cell.content === "door" && !applyToAll && (() => {
            const rect = shelvingCellFrontRect(module, cell.id);
            return <CellFrontOpeningPicker cell={cell} widthMm={rect?.width ?? width} heightMm={rect?.height ?? height}
              headroomMm={shelvingCellHeadroomMm(project, module, cell.id)} excludedLifts={shelvingExcludedLifts(module)} onPatch={patch} />;
          })()}
          <div className="shelving-cell-fields">
            <label
              className="select-field compact shelving-cell-fields__wide"
            >
              <span>Ручка</span>
              <select
                value={cell.handle ?? "bar"}
                onChange={(event) =>
                  patch({ handle: event.target.value as HandleType })
                }
              >
                {handlesForFacade(facade).map(([key, handle]) => (
                  <option value={key} key={key}>{handle.name}</option>
                ))}
              </select>
            </label>
          </div>
          {/* Интегрированная ручка ящиков — как у шкафов: на всю длину или участок по центру (видео Сергея 24.10). */}
          {cell.handle === "integrated" && (applyToAll || cell.content === "drawers") && <div className="integrated-handle-styles-wrap">
            <span className="field-caption">Ручка на ящиках</span>
            <div className="segmented-control two" role="group" aria-label="Интегрированная ручка ящиков">
              <button type="button" className={cell.integratedHandleStyle !== "topPart" ? "is-selected" : ""}
                aria-pressed={cell.integratedHandleStyle !== "topPart"}
                onClick={() => patch({ integratedHandleStyle: undefined })}>На всю длину</button>
              <button type="button" className={cell.integratedHandleStyle === "topPart" ? "is-selected" : ""}
                aria-pressed={cell.integratedHandleStyle === "topPart"}
                onClick={() => patch({ integratedHandleStyle: "topPart" })}>Участок по центру</button>
            </div>
          </div>}
          <HandleChoiceNote handle={cell.handle} />
        </>
      )}
    </div>
  );
}

function ModulesPanel({
  project,
  notify,
  onOpenRoom,
}: {
  project: PlannerProject;
  notify: (message: string) => void;
  onOpenRoom?: () => void;
}) {
  const cabinetModules = productModules(project, "wardrobe");
  const warnings = validateProject(project);
  const layout = createCompositionLayout(project);
  const selected = project.modules.find(
    (module) => module.id === project.selectedModuleId
  );
  const mezzanineBase = baseModuleFor(project, selected);
  // Предел ширины шкафа перебирает места у стены — считать один раз на изменение проекта.
  const runWidthLimit = useMemo(
    () => selected && selected.type !== "end" && selected.type !== "corner45" && selected.type !== "shelving"
      ? maxCabinetRunWidthOnBackWall(project, selected) : MAX_CABINET_TOTAL_WIDTH_MM,
    [project, selected]
  );
  const installationTarget =
    selected?.type === "mezzanine" ? mezzanineBase : selected;
  const canAddMezzanine = Boolean(
    selected &&
    selected.type !== "mezzanine" &&
    selected.type !== "corner45" &&
    selected.type !== "shelving" &&
    selected.type !== "end" &&
    !mezzanineFor(project, selected.id) &&
    usableRoomHeightMm(project) - selected.height - topFillerMm(selected) >= MIN_MODULE_HEIGHT_MM
  );
  // Почему антресоль сейчас не поставить — конкретно, а не общими словами (приёмка 04.10, wb-10).
  const mezzanineBlockReason = (() => {
    if (canAddMezzanine) return "";
    if (!selected || selected.type === "shelving") return "сначала выберите корпус";
    if (selected.type === "mezzanine" || mezzanineFor(project, selected.id)) return "у этого корпуса антресоль уже есть";
    if (selected.type === "corner45" || selected.type === "end") return "выберите обычный пенал";
    const free = usableRoomHeightMm(project) - selected.height - topFillerMm(selected);
    return `до потолка ${Math.max(0, Math.round(free))} мм, нужно от ${MIN_MODULE_HEIGHT_MM} — уменьшите высоту корпуса`;
  })();
  const selectModule = usePlannerStore((state) => state.selectModule);
  const addModule = usePlannerStore((state) => state.addModule);
  const updateModule = usePlannerStore((state) => state.updateModule);
  const setCabinetTotalHeight = usePlannerStore(
    (state) => state.setCabinetTotalHeight
  );
  const setMezzanineHeight = usePlannerStore((state) => state.setMezzanineHeight);
  const setCabinetTotalWidth = usePlannerStore(
    (state) => state.setCabinetTotalWidth
  );
  const endAttachmentTargets = endModuleAttachmentTargets(project);
  const selectedEndTarget = selected?.type === "end"
    ? endModuleTarget(project, selected)
    : undefined;
  const selectedEndHasConflict = Boolean(
    selected?.type === "end" &&
    selectedEndTarget &&
    endModuleForTargetSide(project, selectedEndTarget.id, endModuleSide(selected), selected.id)
  );
  // Торец ставится к выбранному пеналу; почему нельзя — пишем на кнопке.
  const leftEndBlocker = endModuleAddBlocker(project, "left");
  const rightEndBlocker = endModuleAddBlocker(project, "right");
  const canAddLeftEnd = !leftEndBlocker;
  const canAddRightEnd = !rightEndBlocker;
  /**
   * Шкаф 600 рядом с угловым в его отделке: «neighbour» — на другой стене перед
   * глухой частью (петли у угла, 31.10), «continue» — вдоль его стены за дверью.
   */
  const addCornerCabinet = (corner: WardrobeModule, place: "neighbour" | "continue") => {
    const left = cornerSide(corner) === "left";
    const gap = PERPENDICULAR_WALL_CLEARANCE_MM;
    applyRoomSize(usePlannerStore.getState().project);
    const slot = cornerNeighbourSlot(corner);
    const along = cornerRun(corner) === "back"
      ? left ? gap + corner.width + 1 : BACK_WALL_LENGTH_MM - gap - corner.width - 601
      : left ? -gap - cornerReturnWidth(corner) - 601 : BACK_WALL_LENGTH_MM + gap + cornerReturnWidth(corner) + 1;
    addModule("single", { width: 600, height: corner.height, depth: corner.depth,
      wallOffset: place === "neighbour" ? slot.wallOffset : along,
      ...(place === "neighbour" ? { doorHinge: slot.doorHinge } : {}),
      material: corner.material, decor: corner.decor, door: corner.door,
      facade: corner.facade, facadeDecor: corner.facadeDecor, facadeMaterial: corner.facadeMaterial,
      facadePattern: corner.facadePattern, facadeColor: corner.facadeColor,
      facadeColorHex: corner.facadeColorHex, facadeColorCode: corner.facadeColorCode,
      handle: corner.handle, baseType: corner.baseType, baseHeight: corner.baseHeight });
  };
  const selectedBase = baseModuleFor(project, selected);
  // Общая высота шкафа упирается в потолок? (замечание № 5)
  const heightCeiling = selected && selected.type !== "mezzanine"
    ? ceilingLimit(project, (candidate) => {
        const target = candidate.modules.find((module) => module.id === selected.id) ?? selected;
        const base = baseModuleFor(candidate, target);
        return target.type === "corner45" || target.type === "end" ? maxModuleHeight(candidate, target)
          : base ? maxCabinetTotalHeight(candidate, base) : maxModuleHeight(candidate, target);
      })
    : null;
  const shownTotalHeight = selected
    ? selected.type === "end" ? selected.height : selectedBase ? cabinetTotalHeight(project, selectedBase) : selected.height
    : 0;

  return (
    <>
      {/* Сборка сверху вниз (видео Сергея 23.10): сначала выбрать, что добавить, ниже — состав и настройки выбранной секции. */}
      <section className="panel-section">
        <span className="eyebrow">Добавить изделие</span>
        <div className="add-module-family">
          <div className="add-module-family__heading">
            <strong>Шкафы</strong>
            <small>Корпусы и верхние модули</small>
          </div>
          <div className="add-module-grid">
          {(
            [
              ["single", "Пенал", "400–600 мм"],
              ["double", "Широкий", "до 1200 мм"],
              ["mezzanine", "Антресоль", "на выбранный корпус"]
            ] as const
          ).filter(([type]) => type !== "mezzanine" || cabinetModules.length > 0).map(([type, title, subtitle]) => (
            <button
              type="button"
              key={type}
              disabled={type === "mezzanine" && !canAddMezzanine}
              onClick={() => {
                const before = usePlannerStore.getState().project.modules.length;
                addModule(type);
                if (type !== "mezzanine" && usePlannerStore.getState().project.modules.length === before) {
                  notify("У стен нет свободного места — сдвиньте изделия или увеличьте комнату на шаге «Комната»");
                }
              }}
            >
              <Icon name="plus" size={17} />
              <span>
                <strong>{title}</strong>
                <small>
                  {type === "mezzanine" && !canAddMezzanine
                    ? mezzanineBlockReason
                    : subtitle}
                </small>
              </span>
            </button>
          ))}
          </div>
        </div>
        <div className="add-module-family">
          <div className="add-module-family__heading"><strong>Угловые шкафы</strong><small>Корпус уходит в угол за соседний шкаф</small></div>
          <div className="add-module-grid">
            {(["left", "right"] as const).map(side => {
              // Угол занят — пишем почему; стоящие шкафы и торцы не сдвигаются (приёмка 07.10, wb-24).
              const blocker = cornerAddBlocker(project, side);
              return <button key={side} type="button" disabled={Boolean(blocker)}
                onClick={() => {
                  addModule("corner45", { cornerSide: side });
                  // Угол сразу готов: вместе с угловым — соседний шкаф перед его глухой частью (29.10).
                  const corner = usePlannerStore.getState().project.modules.find((module) => module.type === "corner45" && cornerSide(module) === side);
                  if (!corner) return;
                  if (canPlaceCornerNeighbour(usePlannerStore.getState().project, corner)) addCornerCabinet(corner, "neighbour");
                  // Доборы сразу под двери и ручки: открытые двери не заденут друг друга.
                  const state = usePlannerStore.getState().project;
                  const placed = state.modules.find((module) => module.id === corner.id);
                  const needed = placed ? cornerFillerForHandlesMm(state, placed) : null;
                  const neededNeighbour = placed ? cornerNeighbourFillerForHandlesMm(state, placed) : null;
                  if (placed && needed !== null && needed > cornerFillerMm(placed)) updateModule(corner.id, { cornerFiller: needed });
                  if (placed && neededNeighbour !== null && neededNeighbour > cornerNeighbourFillerMm(placed)) {
                    updateModule(corner.id, { cornerNeighbourFiller: neededNeighbour });
                  }
                  selectModule(corner.id);
                }}>
                <Icon name="plus" size={17} /><span><strong>{side === "left" ? "Левый угловой" : "Правый угловой"}</strong><small>{blocker ?? "вместе с соседним шкафом"}</small></span>
              </button>;
            })}
          </div>
        </div>
        {cabinetModules.length > 0 && <details className="add-module-family product-advanced">
          <summary>Добавить торцевые полки</summary>
          <div className="add-module-family__heading">
            <strong>Торцевые модули</strong>
            <small>Открытое завершение композиции</small>
          </div>
          <div className="add-module-grid">
            <button
              type="button"
              disabled={!canAddLeftEnd}
              onClick={() => addModule("end", { endSide: "left" })}
            >
              <Icon name="plus" size={17} />
              <span>
                <strong>Левый торец</strong>
                <small>
                  {canAddLeftEnd ? "к выбранному корпусу, слева" : leftEndBlocker}
                </small>
              </span>
            </button>
            <button
              type="button"
              disabled={!canAddRightEnd}
              onClick={() => addModule("end", { endSide: "right" })}
            >
              <Icon name="plus" size={17} />
              <span>
                <strong>Правый торец</strong>
                <small>
                  {canAddRightEnd ? "к выбранному корпусу, справа" : rightEndBlocker}
                </small>
              </span>
            </button>
          </div>
        </details>}
      </section>
      {cabinetModules.length > 0 && <section className="panel-section">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Композиция</span>
            <h3>
              {cabinetModules.length} {sectionWord(cabinetModules.length)} шкафа
            </h3>
          </div>
        </div>
        <div className="module-list">
          {cabinetModules.map((module, index) => (
            <ModuleCard
              key={module.id}
              module={module}
              index={index}
              selected={module.id === project.selectedModuleId}
              hasWarning={warnings.some((warning) => warning.moduleId === module.id)}
              wall={
                layout.placements.find(
                  (placement) =>
                    placement.module.id === module.id && placement.wing === 0
                )?.wall ?? "back"
              }
              onSelect={() => selectModule(module.id)}
            />
          ))}
        </div>
      </section>}

      {selected ? (
        <section className="panel-section">
          <div className="product-delete-heading">
            <span className="eyebrow">Выбранная секция</span>
            <DeleteProductButton project={project} />
          </div>
          <label className="select-field">
            <span>Тип корпуса</span>
            <select
              value={selected.type}
              disabled={selected.type === "mezzanine" || selected.type === "end" || selected.type === "corner45"}
              onChange={(event) => {
                // Тип пенала следует за шириной: одностворчатый — до 600 мм, широкий — шире.
                // Поэтому смена типа — это смена ширины (приёмка 04.10, wb-16).
                const next = event.target.value as ModuleType;
                if (next === "single" || next === "double") {
                  const width = next === "single" ? 600 : 900;
                  setCabinetTotalWidth(selected.id, width);
                  notify(next === "single"
                    ? "Одностворчатый пенал: ширина 600 мм"
                    : "Широкий пенал: ширина 900 мм — меняется полем «Общая ширина»");
                  return;
                }
                updateModule(selected.id, { type: next });
              }}
            >
              <option value="single">Одностворчатый пенал</option>
              <option value="double">Широкий пенал</option>
              {selected.type === "end" && (
                <option value="end">Торцевой модуль</option>
              )}
              {selected.type === "corner45" && <option value="corner45">Угловой шкаф</option>}
              {selected.type === "mezzanine" && (
                <option value="mezzanine">Антресоль</option>
              )}
            </select>
          </label>
          {selected.type === "corner45" && <div className="corner-settings">
            <div className="segmented-control two" aria-label="Угол комнаты">
              {(["left", "right"] as const).map(side => {
                const blocker = cornerSide(selected) === side ? null : cornerAddBlocker(project, side, selected);
                return <button key={side} type="button"
                  className={cornerSide(selected) === side ? "is-selected" : ""}
                  disabled={Boolean(blocker)} title={blocker ?? undefined}
                  onClick={() => updateModule(selected.id, { cornerSide: side })}>
                  {side === "left" ? "Левый угол" : "Правый угол"}
                </button>;
              })}
            </div>
            {(["left", "right"] as const).filter(side => side !== cornerSide(selected)).map(side => {
              const blocker = cornerAddBlocker(project, side, selected);
              return blocker ? <small key={side} className="handle-choice-note">В {side === "left" ? "левый" : "правый"} угол сейчас нельзя: {blocker}.</small> : null;
            })}
            {/* В угол уходит шкаф у задней стены или у боковой — «как кому нравится» (Сергей, 28.10). */}
            <span className="field-caption">В угол уходит</span>
            <div className="segmented-control two" aria-label="Вдоль какой стены угловой">
              {(["back", "side"] as const).map(run => (
                <button key={run} type="button" className={cornerRun(selected) === run ? "is-selected" : ""}
                  aria-pressed={cornerRun(selected) === run}
                  onClick={() => updateModule(selected.id, { cornerRun: run === "side" ? "side" : undefined })}>
                  {run === "back" ? "Шкаф у задней стены" : "Шкаф у боковой стены"}
                </button>
              ))}
            </div>
            <CornerPlanPreview module={selected} />
            <p className="sl-help">Вид сверху. Угловой — один корпус: заходит вглубь угла за соседний шкаф у {cornerRun(selected) === "back" ? "боковой" : "задней"} стены (пунктир). Петли обеих дверей — у угла, двери открываются навстречу друг другу. Поэтому доборов два, в цвет фасадов: добор углового — перед глухой частью, добор соседа — на его лице у угла.</p>
          </div>}
          {selected.type !== "shelving" && <div className={`dimension-grid${selected.type === "corner45" ? " corner-dimensions" : ""}`}>
            {selected.type === "corner45" && <>
              <DimensionField label="Дверь" value={cornerDoorWidthMm(selected)} min={CORNER_DOOR_MIN_MM} max={CORNER_DOOR_MAX_MM}
                maxReason="шире дверь тяжела для петель" onInvalid={notify}
                onCommit={(cornerDoorWidth) => updateModule(selected.id, { cornerDoorWidth })} />
              <DimensionField label="Добор углового" value={cornerFillerMm(selected)} min={CORNER_FILLER_MIN_MM} max={CORNER_FILLER_MAX_MM}
                onInvalid={notify} onCommit={(cornerFiller) => updateModule(selected.id, { cornerFiller })} />
              {selected.door !== "open" && <DimensionField label="Добор соседа" value={cornerNeighbourFillerMm(selected)} min={CORNER_FILLER_MIN_MM} max={CORNER_FILLER_MAX_MM}
                onInvalid={notify} onCommit={(cornerNeighbourFiller) => updateModule(selected.id, { cornerNeighbourFiller })} />}
              {(() => {
                // Открытые двери ложатся вдоль фасадов друг друга (31.10): доборы — не уже двери и выноса ручки.
                const needed = cornerFillerForHandlesMm(project, selected);
                const neededNeighbour = cornerNeighbourFillerForHandlesMm(project, selected);
                const own = needed !== null && cornerFillerMm(selected) < needed;
                const other = neededNeighbour !== null && cornerNeighbourFillerMm(selected) < neededNeighbour;
                if (!own && !other) return null;
                return <p className="sl-help">
                  {own && <>Добор углового — не меньше {needed} мм (толщина двери и вынос ручки): иначе открытая дверь углового может задеть дверь или ручку соседа. </>}
                  {other && <>Добор соседа — не меньше {neededNeighbour} мм: иначе открытая дверь соседа может задеть дверь или ручку углового. </>}
                  <button type="button" className="text-button" onClick={() => updateModule(selected.id, {
                    ...(own ? { cornerFiller: needed! } : {}), ...(other ? { cornerNeighbourFiller: neededNeighbour! } : {}) })}>Исправить</button>
                </p>;
              })()}
            </>}
            {selected.type !== "corner45" && <DimensionField
              label={selected.type === "end" ? "Ширина" : "Общая ширина"}
              value={selected.type === "end" ? selected.width : cabinetRunWidth(project, selected)}
              min={selected.type === "end" ? END_MODULE_MIN_WIDTH_MM : 300}
              max={selected.type === "end" ? END_MODULE_MAX_WIDTH_MM : Math.min(MAX_CABINET_TOTAL_WIDTH_MM, runWidthLimit)}
              maxReason={selected.type !== "end" &&
                runWidthLimit < BACK_WALL_LENGTH_MM - 120
                ? "столько свободно на этой стене рядом с другими изделиями"
                : selected.type !== "end" ? "это вся ширина комнаты; её можно изменить на шаге «Комната»" : undefined}
              disabled={selected.type === "mezzanine"}
              onInvalid={notify}
              onCommit={(width) => {
                if (selected.type === "end") {
                  updateModule(selected.id, { width });
                  return;
                }
                setCabinetTotalWidth(selected.id, width);
                const sections = Math.ceil(width / MAX_CABINET_SECTION_WIDTH_MM);
                if (sections > 1) {
                  const dividerCount = sections - 1;
                  notify(
                    dividerCount === 1
                      ? `${Math.round(width)} мм: добавлена внутренняя стойка; каждый пролёт не шире ${MAX_CABINET_SECTION_WIDTH_MM} мм`
                      : `${Math.round(width)} мм: добавлены ${dividerCount} внутренние стойки; каждый пролёт не шире ${MAX_CABINET_SECTION_WIDTH_MM} мм`
                  );
                }
              }}
            />}
            <DimensionField
              label="Общая высота"
              value={
                selected.type === "end"
                  ? selected.height
                  : selectedBase
                    ? cabinetTotalHeight(project, selectedBase)
                    : selected.height
              }
              min={selected.type === "corner45" ? cornerMinimumHeight(selected) : 300}
              max={
                selected.type === "corner45" || selected.type === "end" ? maxModuleHeight(project, selected) : selectedBase
                  ? maxCabinetTotalHeight(project, selectedBase)
                  : maxModuleHeight(project, selected)
              }
              maxReason={heightCeiling ? ceilingReason(heightCeiling) : undefined}
              onInvalid={notify}
              onCommit={(height) => {
                if (selected.type === "corner45" || selected.type === "end") {
                  updateModule(selected.id, { height });
                } else if (selectedBase) {
                  setCabinetTotalHeight(selectedBase.id, height);
                } else {
                  updateModule(selected.id, { height });
                }
              }}
            />
            <DimensionField
              label="Глубина"
              value={selected.depth}
              min={
                selected.type === "end"
                    ? END_MODULE_MIN_DEPTH_MM
                    : 300
              }
              max={
                selected.type === "end"
                    ? END_MODULE_MAX_DEPTH_MM
                    : selected.type === "mezzanine" && mezzanineBase
                    ? mezzanineBase.depth
                    : 700
              }
              disabled={selected.type === "mezzanine"}
              onInvalid={notify}
              onCommit={(depth) => {
                updateModule(selected.id, { depth });
              }}
            />
          </div>}
          {selectedBase && cabinetSupportsToCeiling(selectedBase) &&
            <CabinetToCeilingToggle project={project} base={selectedBase} notify={notify} />}
          {selected.type !== "mezzanine" && !selectedBase?.cabinetToCeiling &&
            <CeilingLimitNote limit={heightCeiling} value={shownTotalHeight} onOpenRoom={onOpenRoom} />}
          {selected.type !== "mezzanine" && <CabinetDrawersControl module={selected} notify={notify} />}
          {selectedBase && selected.type !== "corner45" && selected.type !== "end" && (() => {
            const mezzanine = mezzanineFor(project, selectedBase.id);
            if (!mezzanine) return null;
            const range = mezzanineHeightLimit(project, selectedBase, mezzanine.height);
            const total = selectedBase.height + mezzanine.height;
            return <div className="mezzanine-split">
              <DimensionField label="Высота антресоли" value={mezzanine.height} min={range.min} max={range.max}
                minReason={range.min > 300 ? `корпус под антресолью — не выше ${MAX_MODULE_HEIGHT_MM} мм` : undefined}
                maxReason={range.ceilingBound ? `корпус под ней — не ниже 300 мм, шкаф — до ${range.available} мм` : undefined}
                disabled={range.min >= range.max} onInvalid={notify}
                onCommit={(height) => setMezzanineHeight(selectedBase.id, height)} />
              <p className="sl-help">{range.pinned
                ? `Антресоль — от ${range.min} до ${range.max} мм. Шкаф до потолка, общая высота ${total} мм не меняется: антресоль выше — корпус ниже (сейчас ${selectedBase.height} мм).`
                : `Антресоль — от ${range.min} до ${range.max} мм. До потолка свободно ${Math.max(0, range.available - total)} мм: сначала антресоль займёт их, дальше корпус (сейчас ${selectedBase.height} мм) станет ниже.`}</p>
            </div>;
          })()}
          {selected.type === "corner45" && <div className="corner-settings">
            {(() => {
              const plan = cornerPlan(selected);
              const neighbour = cornerNeighbour(project, selected);
              return <p className="soft-note">Корпус {plan.length} мм вдоль {plan.run === "back" ? "задней" : "боковой"} стены: глухая часть {plan.blind} мм + добор {plan.filler} мм + дверь {plan.door} мм. {neighbour
                ? `Глухая часть — по соседнему шкафу у ${plan.run === "back" ? "боковой" : "задней"} стены (глубина ${neighbour.depth} мм с дверью); поменяете его глубину — угловой подстроится.`
                : `Соседнего шкафа у ${plan.run === "back" ? "боковой" : "задней"} стены пока нет — поставьте его ниже: он закроет глухую часть, и её глубина станет по нему.`} Внутри — только полки и штанги, во всю длину (в «Наполнении»).</p>;
            })()}
            {(() => {
              // Антресоль над угловым — на одной высоте с антресолями соседей (картинка Сергея, п. 5).
              const neighbour = cornerNeighbour(project, selected);
              const neighbourMezzanine = neighbour ? mezzanineFor(project, neighbour.id) : undefined;
              const mezzanine = cornerMezzanineMm(selected);
              const free = Math.min(CORNER_MEZZANINE_MAX_MM, usableRoomHeightMm(project) - selected.height - topFillerMm(selected));
              if (neighbourMezzanine) {
                return <p className="soft-note">Антресоль над угловым — {mezzanine} мм, как у соседнего шкафа: корпус {selected.height} мм, швы на одной высоте.</p>;
              }
              return <div className="corner-mezzanine">
                <label className="toggle-row">
                  <input type="checkbox" checked={mezzanine > 0} disabled={!mezzanine && free < CORNER_MEZZANINE_MIN_MM}
                    onChange={() => updateModule(selected.id, { cornerMezzanine: mezzanine ? undefined : Math.max(CORNER_MEZZANINE_MIN_MM, Math.min(500, free)) })} />
                  <span>Антресоль над угловым</span>
                </label>
                {!mezzanine && free < CORNER_MEZZANINE_MIN_MM && <small className="sl-help">До потолка свободно {Math.max(0, free)} мм — для антресоли нужно от {CORNER_MEZZANINE_MIN_MM}.</small>}
                {mezzanine > 0 && <DimensionField label="Высота антресоли" value={mezzanine} min={CORNER_MEZZANINE_MIN_MM}
                  max={Math.max(CORNER_MEZZANINE_MIN_MM, free)} onInvalid={notify}
                  onCommit={(cornerMezzanine) => updateModule(selected.id, { cornerMezzanine })} />}
              </div>;
            })()}
            <span className="eyebrow">Продолжить шкаф</span>
            <div className="add-module-grid">
              {(["back", "side"] as const).map(wing => {
                const place = (wing === "side") === (cornerRun(selected) === "back") ? "neighbour" : "continue";
                const taken = place === "neighbour" && Boolean(cornerNeighbour(project, selected));
                return <button key={wing} type="button" disabled={taken} onClick={() => addCornerCabinet(selected, place)}>
                  <Icon name="plus" size={17} /><span><strong>{wing === "back" ? "У задней стены" : "У боковой стены"}</strong><small>{place === "neighbour"
                    ? taken ? "соседний шкаф уже стоит" : "Соседний шкаф перед глухой частью" : "Пенал 600 мм за дверью углового"}</small></span></button>;
              })}
            </div>
          </div>}
          {selectedBase && selected.type !== "mezzanine" && selected.type !== "end" && selected.type !== "corner45" && (
            <div className="soft-note">
              До 2750 мм корпус получает цельные боковые стойки. При большей
              высоте антресоль создаётся автоматически и имеет высоту от 300
              до 1640 мм. При ширине свыше 1200 мм внутри одного корпуса
              автоматически добавляются общие вертикальные стойки.
            </div>
          )}
          {selected.type === "end" && (
            <div className="shelving-controls installation-controls">
              <div className="section-heading shelving-controls__heading">
                <div>
                  <span className="eyebrow">Торец композиции</span>
                  <h3>Форма и ярусы</h3>
                </div>
              </div>
              <label className="select-field compact">
                <span>Прикреплён к корпусу</span>
                <select
                  value={selectedEndTarget?.id ?? ""}
                  disabled={selectedEndHasConflict}
                  onChange={(event) =>
                    updateModule(selected.id, { endAttachedToId: event.target.value })
                  }
                >
                  {endAttachmentTargets.map((target) => (
                    <option
                      value={target.id}
                      key={target.id}
                      disabled={!canAttachEndModule(project, selected, target)}
                    >
                      Секция {cabinetModules.indexOf(target) + 1} · {MODULE_NAMES[target.type]}
                    </option>
                  ))}
                </select>
              </label>
              <div className="segmented-control two">
                <button
                  className={endModuleSide(selected) === "left" ? "is-selected" : ""}
                  type="button"
                  disabled={Boolean(
                    selectedEndTarget &&
                    (endModuleForTargetSide(project, selectedEndTarget.id, "left", selected.id) ||
                      !canAttachEndModule(project, selected, selectedEndTarget, "left"))
                  )}
                  onClick={() => updateModule(selected.id, { endSide: "left" })}
                >
                  Слева
                </button>
                <button
                  className={endModuleSide(selected) === "right" ? "is-selected" : ""}
                  type="button"
                  disabled={Boolean(
                    selectedEndTarget &&
                    (endModuleForTargetSide(project, selectedEndTarget.id, "right", selected.id) ||
                      !canAttachEndModule(project, selected, selectedEndTarget, "right"))
                  )}
                  onClick={() => updateModule(selected.id, { endSide: "right" })}
                >
                  Справа
                </button>
              </div>
              {selectedEndTarget &&
                (!canAttachEndModule(project, selected, selectedEndTarget, "left") ||
                  !canAttachEndModule(project, selected, selectedEndTarget, "right")) && (
                  <p className="soft-note">
                    Недоступная сторона заблокирована: там торец пересечётся с угловым или соседним изделием.
                  </p>
                )}
              <div className="shelving-controls__grid">
                <label className="select-field compact">
                  <span>Форма полок</span>
                  <select
                    value={endModuleShape(selected)}
                    onChange={(event) =>
                      updateModule(selected.id, {
                        endShape: event.target.value as WardrobeModule["endShape"]
                      })
                    }
                  >
                    <option value="rounded">Радиусная</option>
                    <option value="beveled">Скошенная</option>
                  </select>
                </label>
                <label className="select-field compact">
                  <span>Количество ярусов</span>
                  <select
                    value={endModuleTierCount(selected)}
                    onChange={(event) =>
                      updateModule(selected.id, {
                        endShelfCount: Number(event.target.value)
                      })
                    }
                  >
                    {Array.from(
                      { length: END_MODULE_MAX_TIERS - END_MODULE_MIN_TIERS + 1 },
                      (_, index) => END_MODULE_MIN_TIERS + index
                    ).map((value) => (
                      <option value={value} key={value}>{value}</option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="select-field compact">
                <span>Основание</span>
                <select
                  value={cabinetBaseType(selected)}
                  onChange={(event) => {
                    const baseType = event.target.value as WardrobeModule["baseType"];
                    updateModule(selected.id, {
                      baseType,
                      baseHeight:
                        baseType === "floor"
                          ? 0
                          : Math.max(
                              DEFAULT_BASE_HEIGHT_MM,
                              selected.baseHeight ?? DEFAULT_BASE_HEIGHT_MM
                            )
                    });
                  }}
                >
                  <option value="plinth">Цоколь</option>
                  <option value="legs">Регулируемые ножки</option>
                  <option value="floor">Без основания</option>
                </select>
              </label>
              <div className="dimension-grid">
                <DimensionField
                  label={baseHeightLabel(selected)}
                  value={cabinetBaseHeightMm(selected)}
                  min={MIN_BASE_HEIGHT_MM}
                  max={maxCabinetBaseHeightMm(selected)}
                  disabled={cabinetBaseType(selected) === "floor"}
                  onInvalid={notify}
                  onCommit={(baseHeight) => updateModule(selected.id, { baseHeight })}
                />
                {cabinetBaseType(selected) === "plinth" && <DimensionField
                  label="Цоколь утоплен на"
                  value={cabinetBaseSetbackMm(selected)}
                  min={0}
                  max={MAX_BASE_SETBACK_MM}
                  onInvalid={notify}
                  onCommit={(baseSetback) => updateModule(selected.id, { baseSetback })}
                />}
              </div>
              {cabinetBaseType(selected) === "plinth" && <BaseSetbackHint value={cabinetBaseSetbackMm(selected)} />}
              <div className="soft-note">
                Перетаскивание торцевого модуля двигает всю связанную группу.
                Чтобы прикрепить его к другому корпусу, выберите корпус в поле выше.
              </div>
              {selectedEndHasConflict && (
                <div className="soft-note">
                  На этой стороне корпуса уже есть торцевой модуль. Удалите один
                  из дублей — добавить новый такой торец конфигуратор больше не позволит.
                </div>
              )}
            </div>
          )}
          {installationTarget &&
            installationTarget.type !== "shelving" &&
            installationTarget.type !== "corner45" &&
            installationTarget.type !== "end" && (
              <details className="shelving-controls installation-controls product-advanced" key={`${installationTarget.id}-installation`}>
                <summary>Основание и доборы · {cabinetBaseType(installationTarget) === "legs" ? "ножки" : cabinetBaseType(installationTarget) === "floor" ? "на полу" : "цоколь"}</summary>
                {selected.type === "mezzanine" && (
                  <div className="soft-note">
                    Параметры применяются ко всей колонне вместе с нижним корпусом.
                  </div>
                )}
                <label className="select-field compact">
                  <span>Основание</span>
                  <select
                    value={cabinetBaseType(installationTarget)}
                    onChange={(event) => {
                      const baseType = event.target.value as WardrobeModule["baseType"];
                      updateModule(installationTarget.id, {
                        baseType,
                        baseHeight:
                          baseType === "floor"
                            ? 0
                            : Math.max(
                                DEFAULT_BASE_HEIGHT_MM,
                                installationTarget.baseHeight ??
                                  DEFAULT_BASE_HEIGHT_MM
                              )
                      });
                    }}
                  >
                    <option value="plinth">Цоколь</option>
                    <option value="legs">Регулируемые ножки</option>
                    <option value="floor">Без основания</option>
                  </select>
                </label>
                <div className="dimension-grid">
                  <DimensionField
                    label={baseHeightLabel(installationTarget)}
                    value={cabinetBaseHeightMm(installationTarget)}
                    min={MIN_BASE_HEIGHT_MM}
                    max={maxCabinetBaseHeightMm(installationTarget)}
                    disabled={cabinetBaseType(installationTarget) === "floor"}
                    onInvalid={notify}
                    onCommit={(baseHeight) =>
                      updateModule(installationTarget.id, { baseHeight })
                    }
                  />
                  {cabinetBaseType(installationTarget) === "plinth" && <DimensionField
                    label="Цоколь утоплен на"
                    value={cabinetBaseSetbackMm(installationTarget)}
                    min={0}
                    max={MAX_BASE_SETBACK_MM}
                    onInvalid={notify}
                    onCommit={(baseSetback) =>
                      updateModule(installationTarget.id, { baseSetback })
                    }
                  />}
                </div>
                {cabinetBaseType(installationTarget) === "plinth" && <BaseSetbackHint value={cabinetBaseSetbackMm(installationTarget)} />}
                <div className="dimension-grid">
                  <DimensionField
                    label="Добор слева"
                    value={installationTarget.fillerLeft ?? 0}
                    min={0}
                    max={MAX_SIDE_FILLER_MM}
                    onInvalid={notify}
                    onCommit={(fillerLeft) =>
                      updateModule(installationTarget.id, { fillerLeft })
                    }
                  />
                  <DimensionField
                    label="Добор справа"
                    value={installationTarget.fillerRight ?? 0}
                    min={0}
                    max={MAX_SIDE_FILLER_MM}
                    onInvalid={notify}
                    onCommit={(fillerRight) =>
                      updateModule(installationTarget.id, { fillerRight })
                    }
                  />
                  <DimensionField
                    label="Добор сверху"
                    value={installationTarget.fillerTop ?? 0}
                    min={0}
                    max={MAX_TOP_FILLER_MM}
                    onInvalid={notify}
                    onCommit={(fillerTop) =>
                      updateModule(installationTarget.id, { fillerTop })
                    }
                  />
                </div>
                <div className="soft-note">
                  Доборы входят в общий монтажный габарит, цену и техническую карту.
                  У примыкающей стены нижний угол автоматически подрезается под плинтус.
                </div>
                {cabinetBaseType(installationTarget) === "legs" && (
                  <div className="soft-note">
                    При открытых ножках корпус и боковые доборы начинаются над опорами.
                  </div>
                )}
              </details>
            )}
          {selected.type === "mezzanine" && mezzanineBase && (
            <div className="soft-note">
              Антресоль установлена на выбранный модуль и следует за ним. Ширина,
              глубина и положение синхронизированы с нижним корпусом.
            </div>
          )}
        </section>
      ) : (
        <section className="panel-section">
          <div className="empty-state">
            <Icon name="cube" />
            <strong>
              {cabinetModules.length ? "Секция не выбрана" : "Добавьте первый корпус"}
            </strong>
            <span>
              {cabinetModules.length
                ? "Нажмите на шкаф в сцене или выберите его в списке."
                : "Добавьте первую секцию — она автоматически встанет у стены."}
            </span>
          </div>
          {!cabinetModules.length && (
            <button className="product-add-action" type="button" onClick={() =>
              document.querySelector<HTMLElement>(".model-catalog--showcase")?.scrollIntoView({
                behavior: "smooth",
                block: "start"
              })}>
              Или возьмите готовый шкаф из витрины ниже
            </button>
          )}
        </section>
      )}

    </>
  );
}

const CHEST_MIN_WIDTH_MM = 400;
const CHEST_MAX_WIDTH_MM = 2400;
/** От 300 мм — ТВ-тумба бывает и такой (видео Сергея 24.10); чаще всего 400. */
const CHEST_MIN_HEIGHT_MM = 300;
const CHEST_MAX_HEIGHT_MM = 1400;
/** Новый комод: 1200 × 900 × 450, две колонки по три ящика, на цоколе. */
const CHEST_DEFAULTS: Partial<WardrobeModule> = {
  shelvingKind: "chest",
  width: 1200,
  height: 900,
  depth: 450,
  shelvingColumns: 2,
  shelvingRows: 1,
  shelvingColumnRatios: undefined,
  shelvingRowColumnRatios: undefined,
  shelvingRowRatios: undefined,
  shelvingBase: "plinth",
  shelvingBackPanel: true
};

/** Раздел «Комоды»: стеллаж, все ячейки которого — ящики, с простой настройкой. */
function ChestProductPanel({ project, notify, onBrowseModels }: {
  project: PlannerProject;
  notify: (message: string) => void;
  onBrowseModels: () => void;
}) {
  const addModule = usePlannerStore(state => state.addModule);
  const selectModule = usePlannerStore(state => state.selectModule);
  const remove = usePlannerStore(state => state.removeSelectedModule);
  const modules = productModules(project, "chest");
  const selected = modules.find(module => module.id === project.selectedModuleId);
  if (!selected) return <section className="product-empty">
    <h3>{modules.length ? "Выберите комод" : "С чего начнём"}</h3>
    <p>{modules.length ? "Продолжите настройку сохранённого изделия." : "Возьмите готовый комод из витрины или соберите новый."}</p>
    {modules.length ? (
      <button className="product-add-action" type="button" onClick={() => selectModule(modules[0]!.id)}>
        Продолжить настройку комода
      </button>
    ) : (
      <div className="shelving-start-paths">
        <button className="shelving-start-path" type="button" onClick={onBrowseModels}>
          <strong>Готовые модели</strong>
          <small>Выберите комод в витрине ниже и настройте его под себя.</small>
        </button>
        <button className="shelving-start-path" type="button" onClick={() => addModule("shelving", CHEST_DEFAULTS)}>
          <strong>Новый комод</strong>
          <small>1200 × 900 × 450 мм, две колонки по три ящика — размеры и ящики меняются ниже.</small>
        </button>
      </div>
    )}
  </section>;
  return <>
    <ChestSetupPanel key={selected.id} project={project} module={selected} notify={notify} />
    <section className="panel-section">
      <button className="product-add-action" type="button" onClick={onBrowseModels}>Перейти к готовым моделям</button>
      <ConfirmDeleteButton label="Удалить комод" resetKey={selected.id} onConfirm={remove} />
    </section>
  </>;
}

function ChestSetupPanel({ project, module, notify }: {
  project: PlannerProject;
  module: WardrobeModule;
  notify: (message: string) => void;
}) {
  const updateModule = usePlannerStore(state => state.updateModule);
  const updateCell = usePlannerStore(state => state.updateShelvingCell);
  const grid = getShelvingGridLayout(module);
  const cellHeight = Math.round(grid.cellHeights[0] ?? 0);
  const maxColumns = chestMaxColumns(module.width);
  const minColumns = minShelvingColumnsForWidth(module.width, 1);
  const cells = module.shelvingCells ?? [];
  const layouts = getShelvingCellLayouts(module, grid);
  // Колонка комода (16.10): ящики, фасад или откидной сверху над ящиками.
  const columnInfo = grid.cellWidths.map((_, column) => {
    const cell = cells.find(candidate => candidate.column === column && candidate.row === 0);
    const layout = layouts.find(candidate => candidate.column === column && candidate.row === 0);
    const split = cell && layout ? chestCellSplit(module, cell, layout, grid) : null;
    const flapRange = layout ? chestTopFlapRange(module, layout, grid) : null;
    const drawersHeight = Math.round(split ? split.drawersClearMm : cellHeight);
    const kind: "drawers" | "door" | "split" = cell?.content === "door" ? "door" : split ? "split" : "drawers";
    return { cell, split, flapRange, drawersHeight, kind,
      drawerRange: shelvingDrawerCountRange(drawersHeight, drawerLimits(module)), count: shelvingDrawerCount(cell?.drawerCount, drawersHeight, drawerLimits(module)) };
  });
  const doorColumns = columnInfo.filter(info => info.kind === "door").length;
  const flapColumns = columnInfo.filter(info => info.kind === "split").length;
  const drawerTotal = columnInfo.reduce((sum, info) => info.kind === "door" ? sum : sum + info.count, 0);
  const finish = chestFinishCell(module);
  const foundation = module.shelvingBase ?? "plinth";
  const overhang = normalizeShelvingFacadeOverhang(module.shelvingFacadeOverhang) ?? 0;
  const overhangBlocked = foundation === "floor" ? "Комод стоит на полу — свесу некуда"
    : shelvingFacadeMount(module) === "inset" ? "У вкладных фасадов свеса нет" : null;
  return <section className="panel-section shelving-setup-panel chest-setup">
    <div className="shelving-setup-summary">
      <span>
        <small>Комод</small>
        <strong>{module.width} × {module.height} × {module.depth} мм</strong>
      </span>
      <span>{[drawerTotal ? `${drawerTotal} ящ.` : "", doorColumns ? `${doorColumns} ${doorColumns === 1 ? "фасад" : "фасада"}` : "",
        flapColumns ? `${flapColumns} откидн.` : ""].filter(Boolean).join(" + ")} · {grid.columns} {grid.columns === 1 ? "колонка" : grid.columns < 5 ? "колонки" : "колонок"}</span>
    </div>

    <div className="shelving-setup-step">
      <div className="shelving-flow-heading">
        <strong>Габариты комода</strong>
        <span>Высота {CHEST_MIN_HEIGHT_MM}–{CHEST_MAX_HEIGHT_MM} мм</span>
      </div>
      <div className="dimension-grid">
        <DimensionField label="Ширина" value={module.width} min={CHEST_MIN_WIDTH_MM} max={CHEST_MAX_WIDTH_MM}
          onInvalid={notify} onCommit={(width) => updateModule(module.id, { width, shelvingColumnRatios: undefined })} />
        <DimensionField label="Высота" value={module.height} min={CHEST_MIN_HEIGHT_MM} max={CHEST_MAX_HEIGHT_MM}
          onInvalid={notify} onCommit={(height) => updateModule(module.id, { height })} />
        <DimensionField label="Глубина" value={module.depth} min={SHELVING_MIN_DEPTH_MM} max={SHELVING_MAX_DEPTH_MM}
          onInvalid={notify} onCommit={(depth) => updateModule(module.id, { depth })} />
      </div>
    </div>

    <div className="shelving-setup-step">
      <div className="shelving-flow-heading">
        <strong>Колонки ящиков</strong>
        <span>Ящик не уже {SHELVING_DRAWERS_MIN_WIDTH_MM} мм</span>
      </div>
      <div className="segmented-control cabinet-drawers__count" role="group" aria-label="Число колонок ящиков">
        {Array.from({ length: CHEST_MAX_COLUMNS }, (_, index) => index + 1).map((count) => (
          <button type="button" key={count}
            className={grid.columns === count ? "is-selected" : ""}
            aria-pressed={grid.columns === count}
            disabled={count > maxColumns || count < minColumns}
            title={count < minColumns ? "Пролёт шире 1200 мм — нужна стойка" : count > maxColumns ? "Ящики получатся слишком узкими" : undefined}
            onClick={() => updateModule(module.id, { shelvingColumns: count, shelvingColumnRatios: undefined })}>
            {count}
          </button>
        ))}
      </div>
      <p className="shelving-step-help">Колонки по {Math.round(grid.cellWidths[0] ?? 0)} мм в свету.</p>

      <div className="shelving-flow-heading">
        <strong>Что в колонках</strong>
        <span>Ящики, фасад или откидной над ящиками</span>
      </div>
      <div className="chest-columns">
        {columnInfo.map(({ cell, split, flapRange, drawerRange, count, kind }, column) => {
          const cellId = shelvingCellId(module.id, column, 0);
          const rect = shelvingCellFrontRect(module, cellId, grid);
          const doorFits = shelvingCellFits("door", grid.cellWidths[column] ?? 0, cellHeight);
          // Стеклянная дверца, ставшая ящиками, получает отделку комода.
          const unglass = cell?.facade === "glass" && finish ? chestFinishPatch(finish) : {};
          const setKind = (next: "drawers" | "door" | "split") => updateCell(module.id, cellId, next === "door"
            // Дверца: одна полка внутри посередине; открывание подберётся по высоте фасада.
            ? { content: "door", chestTopFlap: undefined, chestTopFlapGlass: undefined, chestFrontManual: undefined,
              extraShelf: cellHeight >= 2 * SHELVING_EXTRA_SHELF_CLEARANCE_MM + SHELVING_PANEL_THICKNESS_MM,
              extraShelfPosition: 0.5, doorOpening: undefined, doorLift: undefined, doorHinge: column === 0 ? "left" : "right" }
            : { content: "drawers", extraShelf: false, doorOpening: undefined, doorLift: undefined, chestFrontManual: undefined, ...unglass,
              ...(next === "split" && flapRange
                ? { chestTopFlap: Math.min(flapRange.max, Math.max(flapRange.min, cell?.chestTopFlap ?? CHEST_TOP_FLAP_DEFAULT_MM)) }
                : { chestTopFlap: undefined, chestTopFlapGlass: undefined }) });
          const front = kind === "door" && cell && rect
            ? cellFrontLeaves(cell, rect.width, rect.height, shelvingCellHeadroomMm(project, module, cellId, grid), shelvingExcludedLifts(module))
            : null;
          // От одного до пяти ящиков (видео Сергея 24.10); что не помещается по высоте — неактивно с причиной.
          const drawerButtons = <div className="segmented-control chest-column__count" role="group" aria-label={`Ящики в колонке ${column + 1}`}>
            {Array.from({ length: CHEST_MAX_DRAWERS }, (_, index) => index + 1).map((value) => {
              const fits = value >= drawerRange.min && value <= drawerRange.max;
              const reason = value < drawerRange.min
                ? `Фасад выйдет выше ${SHELVING_DRAWER_MAX_FRONT_MM} мм — это уже не ящик`
                : `Фасад выйдет ниже ${CHEST_DRAWER_MIN_FRONT_MM} мм — увеличьте высоту комода`;
              return <button type="button" key={value} className={count === value ? "is-selected" : ""} aria-pressed={count === value}
                disabled={!fits} title={fits ? undefined : reason}
                onClick={() => updateCell(module.id, cellId, { drawerCount: value })}>
                {value}
              </button>;
            })}
          </div>;
          return <div className={`chest-column is-${kind}`} key={column}>
            <span>Колонка {column + 1}</span>
            <div className="segmented-control chest-column__kind" role="group" aria-label={`Колонка ${column + 1}: что в ней`}>
              <button type="button" className={kind === "drawers" ? "is-selected" : ""} aria-pressed={kind === "drawers"} onClick={() => setKind("drawers")}>Ящики</button>
              <button type="button" className={kind === "door" ? "is-selected" : ""} aria-pressed={kind === "door"} disabled={!doorFits && kind !== "door"}
                title={doorFits ? undefined : "Колонка слишком маленькая для фасада"} onClick={() => setKind("door")}>Фасад</button>
              <button type="button" className={kind === "split" ? "is-selected" : ""} aria-pressed={kind === "split"} disabled={!flapRange && kind !== "split"}
                title={flapRange ? "Сверху откидной вверх, снизу ящики" : `Под откидным (от ${CHEST_TOP_FLAP_MIN_MM} мм) не помещается ящик — увеличьте высоту комода`}
                onClick={() => setKind("split")}>Откидной + ящики</button>
            </div>
            {kind === "door" && cell ? <div className="chest-column__front">
              {!cell.chestFrontManual && front && <p className="chest-auto-note">
                Подобрано по высоте фасада {Math.round(rect?.height ?? 0)} мм: {front.flap
                  ? `откидной ${front.flap.direction === "down" ? "вниз" : "вверх"}`
                  : front.opening === "double" ? "две дверцы на петлях" : `дверца на петлях ${front.opening === "right" ? "справа" : "слева"}`}.
                До 600 мм — откидной вниз, выше — на петлях.</p>}
              <CellFrontOpeningPicker cell={cell} widthMm={rect?.width ?? grid.cellWidths[column] ?? 0} heightMm={rect?.height ?? cellHeight}
                headroomMm={shelvingCellHeadroomMm(project, module, cellId)} excludedLifts={shelvingExcludedLifts(module)}
                onPatch={(patch) => updateCell(module.id, cellId, { ...patch, chestFrontManual: true })} />
              {cell.chestFrontManual && <button type="button" className="chest-auto-reset"
                onClick={() => updateCell(module.id, cellId, { chestFrontManual: undefined })}>Подобрать открывание автоматически</button>}
              <span className="field-caption">Фасад колонки</span>
              <div className="segmented-control chest-column__glass" role="group" aria-label={`Фасад колонки ${column + 1}`}>
                <button type="button" className={cell.facade !== "glass" ? "is-selected" : ""} aria-pressed={cell.facade !== "glass"}
                  onClick={() => finish && updateCell(module.id, cellId, chestFinishPatch(finish))}>Как у комода</button>
                <button type="button" className={cell.facade === "glass" ? "is-selected" : ""} aria-pressed={cell.facade === "glass"}
                  onClick={() => updateCell(module.id, cellId, { ...cellFacadeTypePatch("glass", cell.handle), facadePattern: "plain" })}>Стекло в рамке</button>
              </div>
            </div> : kind === "split" && cell && split && flapRange ? <div className="chest-column__front">
              <DimensionField label="Откидной сверху, высота фасада" value={split.flapHeightMm} min={flapRange.min} max={flapRange.max}
                onInvalid={notify} onCommit={(chestTopFlap) => updateCell(module.id, cellId, { chestTopFlap })} />
              <span className="field-caption">Ящики под ним</span>
              {drawerButtons}
              <span className="field-caption">Фасад откидного</span>
              <div className="segmented-control chest-column__glass" role="group" aria-label={`Откидной фасад колонки ${column + 1}`}>
                <button type="button" className={!cell.chestTopFlapGlass ? "is-selected" : ""} aria-pressed={!cell.chestTopFlapGlass}
                  onClick={() => updateCell(module.id, cellId, { chestTopFlapGlass: undefined })}>Как у комода</button>
                <button type="button" className={cell.chestTopFlapGlass ? "is-selected" : ""} aria-pressed={Boolean(cell.chestTopFlapGlass)}
                  onClick={() => updateCell(module.id, cellId, { chestTopFlapGlass: true })}>Стекло в рамке</button>
              </div>
              <small className="sl-help">Подъёмник Aventos HK top: фасад поворачивается вверх перед столешницей — то, что стоит на комоде, не мешает.</small>
            </div> : drawerButtons}
          </div>;
        })}
      </div>
      <p className="shelving-step-help">
        Высота проёма {cellHeight} мм, фасад ящика не ниже {CHEST_DRAWER_MIN_FRONT_MM} мм. За фасадом — одна полка.
        Откидной сверху — {CHEST_TOP_FLAP_MIN_MM}–{CHEST_TOP_FLAP_MAX_MM} мм. Отделка и ручки — на шаге «Фасады и ручки».
      </p>
    </div>

    <div className="shelving-setup-step">
      <div className="shelving-flow-heading">
        <strong>Как комод стоит на полу?</strong>
      </div>
      <div className="shelving-base-grid" role="group" aria-label="Основание комода">
        {([
          ["plinth", "Цоколь", `${SHELVING_PLINTH_HEIGHT_MM} мм · закрытое основание`],
          ["legs", "Ножки", `${SHELVING_LEG_HEIGHT_MM} мм · корпус над полом`],
          ["floor", "На полу", "Боковины до пола"]
        ] as const).map(([value, label, description]) => (
          <button type="button" key={value} aria-pressed={foundation === value}
            onClick={() => updateModule(module.id, { shelvingBase: value })}>
            <strong>{label}</strong><small>{description}</small>
          </button>
        ))}
      </div>
      {foundation === "legs" && <div className="soft-note shelving-base-note">{shelvingLegCount(module)} регулируемых опор.</div>}
      <label className={`chest-option${overhangBlocked ? " is-disabled" : ""}`} title={overhangBlocked ?? undefined}>
        <input type="checkbox" checked={Boolean(overhang) && !overhangBlocked} disabled={Boolean(overhangBlocked)}
          onChange={(event) => updateModule(module.id, { shelvingFacadeOverhang: event.target.checked ? SHELVING_FACADE_OVERHANG_OPTIONS_MM[1] : undefined })} />
        <span><strong>Фасады со свесом{overhang ? ` ${overhang} мм` : ""}</strong>
          <small>{overhangBlocked ?? "Нижние фасады опускаются ниже корпуса и прикрывают цоколь; остальные стоят на месте"}</small></span>
      </label>
      {overhang > 0 && !overhangBlocked && <div className="segmented-control chest-overhang" role="group" aria-label="Свес фасадов, мм">
        {SHELVING_FACADE_OVERHANG_OPTIONS_MM.map((value) => (
          <button type="button" key={value} className={overhang === value ? "is-selected" : ""} aria-pressed={overhang === value}
            onClick={() => updateModule(module.id, { shelvingFacadeOverhang: value })}>{value} мм</button>
        ))}
      </div>}
    </div>

  </section>;
}

function ShelvingProductPanel({ project, notify, onContinue, onBrowseModels, onOpenRoom }: {
  project: PlannerProject;
  notify: (message: string) => void;
  onContinue: () => void;
  onBrowseModels: () => void;
  onOpenRoom?: () => void;
}) {
  const addModule = usePlannerStore(state => state.addModule);
  const selectModule = usePlannerStore(state => state.selectModule);
  const remove = usePlannerStore(state => state.removeSelectedModule);
  const modules = productModules(project, "shelving");
  const selected = modules.find(module => module.id === project.selectedModuleId);
  if (!selected) return <section className="product-empty">
    <h3>{modules.length ? "Выберите стеллаж" : "С чего начнём"}</h3>
    <p>{modules.length ? "Продолжите настройку сохранённого изделия." : "Возьмите готовую конструкцию или соберите пустой стеллаж."}</p>
    {modules.length ? (
      <button className="product-add-action" type="button" onClick={() => selectModule(modules[0]!.id)}>
        Продолжить настройку стеллажа
      </button>
    ) : (
      <div className="shelving-start-paths">
        <button className="shelving-start-path" type="button" onClick={onBrowseModels}>
          <strong>Готовые модели</strong>
          <small>Перейдите к большой витрине ниже, выберите конструкцию и настройте её под себя.</small>
        </button>
        <button className="shelving-start-path" type="button" onClick={() => {
          addModule("shelving");
          // Панель открывается на габаритах, а не прокрученной вниз (замечание № 1).
          requestAnimationFrame(() => requestAnimationFrame(() => {
            const step = document.querySelector<HTMLElement>(".shelving-setup-step");
            if (!step) return;
            const scroller = step.closest<HTMLElement>(".inspector-content");
            if (scroller && scroller.scrollHeight > scroller.clientHeight + 1) {
              const top = step.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - 12;
              scroller.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
            } else {
              step.scrollIntoView({ block: "start", behavior: "smooth" });
            }
          }));
        }}>
          <strong>Пустой стеллаж</strong>
          <small>Зададите размеры и сетку сами, наполнение — потом и только если нужно.</small>
        </button>
      </div>
    )}
  </section>;
  return <>
    <ShelvingSetupPanel key={selected.id} project={project} notify={notify}
      onOpenConstructor={onContinue} onOpenRoom={onOpenRoom} />
    <section className="panel-section">
      <button className="product-add-action" type="button" onClick={onBrowseModels}>Перейти к готовым моделям</button>
      <ConfirmDeleteButton label={selected.stackedOnId ? "Удалить антресоль стеллажа" : "Удалить выбранный стеллаж"}
        resetKey={selected.id} onConfirm={remove} />
    </section>
  </>;
}

function ShelvingSetupPanel({
  project,
  notify,
  onOpenConstructor,
  onOpenRoom,
}: {
  project: PlannerProject;
  notify: (message: string) => void;
  onOpenConstructor: () => void;
  onOpenRoom?: () => void;
}) {
  const updateModule = usePlannerStore((state) => state.updateModule);
  const setShelvingTotalHeight = usePlannerStore(
    (state) => state.setShelvingTotalHeight
  );
  const setShelvingMezzanineHeight = usePlannerStore((state) => state.setShelvingMezzanineHeight);
  const selected = project.modules.find(
    (module) => module.id === project.selectedModuleId
  );
  if (!selected || selected.type !== "shelving") {
    return (
      <section className="panel-section">
        <div className="empty-state">
          <Icon name="shelf" />
          <strong>Стеллаж не выбран</strong>
          <span>Выберите стеллаж в списке над настройками.</span>
        </div>
      </section>
    );
  }

  const isUpper = Boolean(selected.stackedOnId);
  const lower = isUpper ? baseModuleFor(project, selected) : selected;
  const grid = getShelvingGridLayout(selected);
  const totalHeight = isUpper ? selected.height : shelvingTotalHeight(project, selected);
  const upper = isUpper ? selected : shelvingUpperFor(project, selected.id);
  // Высота упирается в потолок? (замечание № 5)
  const heightCeiling = ceilingLimit(project, (candidate) => {
    const target = candidate.modules.find((module) => module.id === selected.id) ?? selected;
    return isUpper ? maxModuleHeight(candidate, target) : maxShelvingTotalHeight(candidate, target);
  });
  const maxColumns = maxShelvingColumnsForWidth(selected.width);
  const minColumns = minShelvingColumnsForWidth(selected.width);
  const maxRows = maxShelvingRowsForHeight(
    selected.height,
    selected.shelvingBase
  );
  const foundation = selected.shelvingBase ?? "plinth";
  const hasConfiguredCellsOutside = (axis: "column" | "row", count: number) =>
    (selected.shelvingCells ?? []).some((cell) => {
      const configured =
        cell.content !== "open" ||
        cell.backPanel ||
        cell.extraShelf ||
        cell.extraDivider ||
        Boolean(cell.leftDividerAngle);
      const outside =
        axis === "column"
          ? cell.column >= count ||
            (cell.content === "diagonal" &&
              cell.column + (cell.diagonalColumnSpan ?? 1) > count)
          : cell.row >= count ||
            (cell.content === "diagonal" &&
              cell.row + (cell.diagonalRowSpan ?? 1) > count);
      return configured && outside;
    });
  const changeGridCount = (axis: "column" | "row", count: number) => {
    if (
      hasConfiguredCellsOutside(axis, count) &&
      !window.confirm(
        `При уменьшении ${axis === "column" ? "числа колонок" : "числа рядов"} часть настроенных ячеек исчезнет. Продолжить?`
      )
    ) {
      return;
    }
    updateModule(
      selected.id,
      axis === "column"
        ? { shelvingColumns: count, shelvingColumnRatios: undefined }
        : { shelvingRows: count, shelvingRowRatios: undefined }
    );
  };

  return (
    <section className="panel-section shelving-setup-panel">
      <div className="shelving-setup-summary">
        <span>
          <small>{isUpper ? "Антресоль стеллажа" : "Выбранный стеллаж"}</small>
          <strong>{selected.width} × {totalHeight} × {selected.depth} мм</strong>
        </span>
        <span>{grid.columns} × {grid.rows} ячеек{!isUpper && upper ? " + антресоль" : ""}</span>
      </div>
      {shelvingDesignName(selected.shelvingDesign) && (
        <p className="shelving-design-badge">
          <span>Схема</span>
          <strong>{shelvingDesignName(selected.shelvingDesign)}</strong>
          <small>Пропадёт, как только вы измените сетку, габариты или ячейку.</small>
        </p>
      )}

      <div className="shelving-setup-step">
        <div className="shelving-flow-heading">
          <strong>{isUpper ? "Габариты антресоли" : "Габариты стеллажа"}</strong>
          <span>{isUpper ? `Высота — от 300 до ${SHELVING_MAX_MEZZANINE_HEIGHT_MM} мм` : `Цельная стойка — до ${SHELVING_MAX_HEIGHT_MM} мм`}</span>
        </div>
        <p className="shelving-step-help">
          {isUpper
            ? "Ширина, глубина и положение связаны с нижним стеллажом. Высоту, сетку, ячейки, заднюю стенку и отделку можно менять отдельно."
            : <>Начните с размеров свободного места — сетку внутри задаёте ниже. Антресоль появляется сама: при общей высоте больше {SHELVING_MAX_HEIGHT_MM} мм стеллаж остаётся {SHELVING_MAX_HEIGHT_MM} мм, а остаток становится отдельной антресолью от 300 до {SHELVING_MAX_MEZZANINE_HEIGHT_MM} мм (если остаток меньше 300 — стеллаж ниже).</>}
        </p>
        <div className="dimension-grid">
          <DimensionField label="Ширина" value={selected.width} min={SHELVING_MIN_WIDTH_MM} max={SHELVING_MAX_WIDTH_MM}
            disabled={isUpper} onInvalid={notify} onCommit={(width) => updateModule(selected.id, { width })} />
          <DimensionField label={isUpper ? "Высота антресоли" : "Общая высота"} value={totalHeight} min={300}
            max={isUpper ? maxModuleHeight(project, selected) : maxShelvingTotalHeight(project, selected)}
            maxReason={heightCeiling ? ceilingReason(heightCeiling) : undefined}
            onInvalid={notify} onCommit={(height) => isUpper
              ? updateModule(selected.id, { height })
              : setShelvingTotalHeight(selected.id, height)} />
          <DimensionField label="Глубина" value={selected.depth} min={SHELVING_MIN_DEPTH_MM} max={SHELVING_MAX_DEPTH_MM}
            disabled={isUpper} onInvalid={notify} onCommit={(depth) => updateModule(selected.id, { depth })} />
        </div>
        <CeilingLimitNote limit={heightCeiling} value={totalHeight} onOpenRoom={onOpenRoom} />
        {!isUpper && !upper && maxShelvingTotalHeight(project, selected) < SHELVING_MAX_HEIGHT_MM + 300 && (
          // Кнопки «+ Антресоль» больше нет (11.10): антресоль появляется сама по общей
          // высоте. Если до потолка её не поставить — объясняем, что сделать.
          <small className="shelving-mezzanine-hint">
            Антресоль ставится над стеллажом {SHELVING_MAX_HEIGHT_MM} мм — нужна общая высота от {SHELVING_MAX_HEIGHT_MM + 300} мм, а до потолка {maxShelvingTotalHeight(project, selected)} мм.
            {onOpenRoom && <> <button type="button" className="ceiling-limit-note__link" onClick={onOpenRoom}>Поднять потолок на шаге «Комната»</button></>}
          </small>
        )}
        {!isUpper && upper && (() => {
          // Высота антресоли — прямо здесь (видео Сергея, 28.10: «высоту не нашёл»).
          const range = shelvingMezzanineHeightRange(totalHeight);
          return <div className="shelving-mezzanine-height">
            <div className="dimension-grid dimension-grid--two">
              <DimensionField label="Высота антресоли" value={upper.height} min={range.min} max={range.max}
                minReason={range.min > 300 ? `стеллаж под антресолью — не выше ${SHELVING_MAX_HEIGHT_MM} мм` : undefined}
                onInvalid={notify} onCommit={(height) => setShelvingMezzanineHeight(selected.id, height)} />
              <DimensionField label="Стеллаж под ней" value={selected.height} min={0} max={SHELVING_MAX_HEIGHT_MM} disabled
                onCommit={() => undefined} />
            </div>
            <small className="shelving-step-help">Общая высота {totalHeight} мм не меняется: антресоль выше — стеллаж ниже. Антресоль — от {range.min} до {range.max} мм. Её сетку и ячейки настраивают, выбрав её в 3D или в списке изделий.</small>
          </div>;
        })()}
        {isUpper && lower && <div className="soft-note">Антресоль установлена над стеллажом высотой {lower.height} мм и перемещается вместе с ним.</div>}
      </div>

      <div className="shelving-setup-step">
        <div className="section-heading shelving-controls__heading">
          <div><span className="eyebrow">{isUpper ? "Сетка антресоли" : "Сетка стеллажа"}</span><h3>{grid.columns} колонки × {grid.rows} рядов</h3></div>
          <span className="section-metric">{grid.columns * grid.rows} ячеек</span>
        </div>
        <p className="shelving-step-help">Здесь только количество проёмов. Наполнение каждой ячейки — в панели ячеек: кнопка «Настроить ячейки» ниже или клик по ячейке в 3D.</p>
        <div className="shelving-controls__grid">
          <label className="select-field compact"><span>Колонки</span><select value={grid.columns}
            onChange={(event) => changeGridCount("column", Number(event.target.value))}>
            {Array.from({ length: maxColumns - minColumns + 1 }, (_, index) => index + minColumns).map(
              (value) => <option value={value} key={value}>{value}</option>
            )}
          </select></label>
          <label className="select-field compact"><span>Ряды</span><select value={grid.rows}
            onChange={(event) => changeGridCount("row", Number(event.target.value))}>
            {Array.from({ length: maxRows }, (_, index) => index + 1).map(
              (value) => <option value={value} key={value}>{value}</option>
            )}
          </select></label>
        </div>
        <div className="shelving-size-preview">
          <span>Ширина проёмов: {grid.cellWidths.map((value) => Math.round(value)).join(" · ")} мм</span>
          <span>Высота рядов: {grid.cellHeights.map((value) => Math.round(value)).join(" · ")} мм</span>
          {grid.rowCustomColumns.some(Boolean) && <span>Свои ширины в рядах: {grid.rowCustomColumns.flatMap((custom, row) =>
            custom ? [`${row + 1} — ${grid.rowCellWidths[row]!.map((value) => Math.round(value)).join(" · ")}`] : []).join("; ")} мм</span>}
        </div>
        {minColumns > 2 && <div className="soft-note">
          При ширине {selected.width} мм меньше {minColumns} колонок не поставить: полка длиннее {SHELVING_MAX_CLEAR_SPAN_MM} мм прогнётся, поэтому проём делит стойка.
        </div>}
        <ShelvingDesignTools module={selected} />
        <button className="secondary-button shelving-equalize" type="button" onClick={() =>
          updateModule(selected.id, { shelvingColumnRatios: undefined, shelvingRowColumnRatios: undefined, shelvingRowRatios: undefined })}>
          Сделать ячейки одинаковыми
        </button>
        <div className="shelving-mini" aria-hidden="true" style={{
          gridTemplateColumns: grid.cellWidths.map(width => `${width}fr`).join(" "),
          gridTemplateRows: [...grid.cellHeights].reverse().map(height => `${height}fr`).join(" ")
        }}>
          {Array.from({ length: grid.columns * grid.rows }, (_, index) => <span key={index} />)}
        </div>
        <button className="shelving-enter" type="button" onClick={onOpenConstructor}>
          <Icon name="shelf" size={26} />
          <span>
            <strong>Настроить ячейки</strong>
            <small>Дверцы, ящики, полки, перегородки и объединение ячеек — панель откроется слева.</small>
          </span>
          <i aria-hidden="true">‹</i>
        </button>
        <p className="shelving-enter-tip">Или просто нажмите нужную ячейку прямо на стеллаже в 3D.</p>
      </div>

      <div className="shelving-setup-step">
        {!isUpper && <>
          <div className="shelving-flow-heading">
            <strong>Как стеллаж стоит на полу?</strong>
            <span>Влияет на доступное число рядов</span>
          </div>
          <div className="shelving-base-grid" role="group" aria-label="Основание стеллажа">
            {([
              ["plinth", "Цоколь", `${SHELVING_PLINTH_HEIGHT_MM} мм · закрытое основание`],
              ["legs", "Ножки", `${SHELVING_LEG_HEIGHT_MM} мм · корпус целиком над полом`],
              ["floor", "На полу", "Боковины доходят до пола"]
            ] as const).map(([value, label, description]) => (
              <button type="button" key={value} aria-pressed={foundation === value}
                onClick={() => updateModule(selected.id, { shelvingBase: value })}>
                <strong>{label}</strong><small>{description}</small>
              </button>
            ))}
          </div>
          {foundation === "legs" && (
            <div className="soft-note shelving-base-note">{shelvingLegCount(selected)} регулируемых опор в два ряда. Нижняя панель и обе боковины начинаются над ножками.</div>
          )}
        </>}
        {isUpper && <div className="shelving-flow-heading">
          <strong>Задняя стенка антресоли</strong>
          <span>Настраивается независимо</span>
        </div>}
        <label className="select-field compact shelving-back-field"><span>Задняя стенка</span>
          <select value={selected.shelvingBackPanel ? "full" : "none"} onChange={(event) => {
            const full = event.target.value === "full";
            updateModule(selected.id, { shelvingBackPanel: full });
            // Антресоль — одно изделие со стеллажом (24.10): общая стенка меняется и у неё.
            if (!isUpper) project.modules.filter((candidate) => candidate.stackedOnId === selected.id && candidate.type === "shelving")
              .forEach((upper) => updateModule(upper.id, { shelvingBackPanel: full }));
          }}>
            <option value="none">Без стенки</option><option value="full">По всей площади</option>
          </select>
        </label>
        {selected.shelvingBackPanel && (
          <div className="soft-note shelving-global-back-note">Общая задняя стенка закрывает все ячейки. {normalizeBackDecor(selected)
            ? `Декор задника — ${resolveDecor(normalizeBackDecor(selected)).name}; меняется на шаге «Материал».`
            : "Она в декоре корпуса; свой декор задника — на шаге «Материал»."}</div>
        )}
      </div>

    </section>
  );
}

function MaterialsPanel({ project }: { project: PlannerProject }) {
  const setDecor = usePlannerStore((state) => state.setDecor);
  const updateModule = usePlannerStore((state) => state.updateModule);
  const applyCarcassToAllCabinets = usePlannerStore((state) => state.applyCarcassToAllCabinets);
  const [carcassApplied, setCarcassApplied] = useState<{ moduleId: string; count: number } | null>(null);
  const selected = project.modules.find(
    (module) => module.id === project.selectedModuleId
  );
  if (!selected) return <ModuleSelectionPrompt />;
  const shelvingColor = selected.shelvingColorHex;
  const enamel = shelvingCarcassEnamel(selected);
  // Стеллаж с антресолью — одна колонна: эмаль меняется у обоих.
  const root = selected.stackedOnId ? project.modules.find((candidate) => candidate.id === selected.stackedOnId) ?? selected : selected;
  const stack = [root, ...project.modules.filter((candidate) => candidate.stackedOnId === root.id && candidate.type === "shelving")];
  const setEnamel = (patch: Partial<WardrobeModule>) =>
    stack.forEach((candidate) => updateModule(candidate.id, { ...patch, shelvingColorHex: undefined, shelvingColorCode: undefined }));
  return (
    <section className="panel-section">
      {selected.type === "shelving" && (
        // Корпус стеллажа и комода — ЛДСП Egger или МДФ в эмали (видео Сергея, 28.10).
        <div className="cell-lighting__place cell-lighting__place--two carcass-kind" role="group" aria-label="Материал корпуса">
          <button type="button" className={!enamel ? "is-selected" : ""} aria-pressed={!enamel}
            onClick={() => enamel && setEnamel({ carcassEnamelHex: undefined, carcassEnamelCode: undefined })}>ЛДСП Egger</button>
          <button type="button" className={enamel ? "is-selected" : ""} aria-pressed={Boolean(enamel)}
            onClick={() => !enamel && setEnamel({ carcassEnamelHex: "#F6F5F0", carcassEnamelCode: undefined })}>Эмаль</button>
        </div>
      )}
      {enamel ? <>
        <span className="eyebrow">Цвет корпуса</span>
        <EnamelColorPicker hex={enamel.hex} code={enamel.code} onChange={(color) => setEnamel({
          carcassEnamelHex: color.hex ?? enamel.hex,
          carcassEnamelCode: "code" in color ? color.code : enamel.code
        })} />
        <p className="carcass-thickness-note">Корпус — МДФ {CARCASS_MM} мм в эмали: боковины, крышка, дно, стойки, полки и перегородки. Торцы окрашены, кромки нет. Задние стенки — из плиты.</p>
      </> : <>
        <DecorPicker
          label={selected.type === "shelving" && selected.stackedOnId ? "Декор антресоли стеллажа" : "Декор выбранного изделия"}
          value={selected.decor ?? project.carcassDecor}
          onChange={setDecor}
        />
        {/* Замечание Сергея № 17: толщина корпуса подписана у выбора ЛДСП. */}
        <p className="carcass-thickness-note">Корпус — ЛДСП Egger {CARCASS_MM} мм: боковины, крышка, дно, стойки, полки и перегородки.</p>
      </>}
      {!selected.stackedOnId && project.modules.some(module => module.stackedOnId === selected.id) && (
        <p className="carcass-thickness-note">Антресоль над ним меняется вместе с корпусом. Свой декор антресоли — выберите её саму.</p>
      )}
      {selected.type === "shelving" && shelvingColor && !enamel && (
        <div className="soft-note shelving-finish-legacy">
          {/* Без кнопки «Показать декор» и кода цвета (приёмка 11.10, sd-01). */}
          Цвет — как у готовой модели. Выберите декор выше, и стеллаж перекрасится в него.
        </div>
      )}
      <EdgeFinishSection project={project} module={selected} />
      {/* Угловой и сосед перед его глухой частью — одно изделие (29.10). */}
      {cornerPartner(project, selected) && <p className="carcass-thickness-note">
        {selected.type === "corner45" ? "Соседний шкаф перед глухой частью" : "Угловой шкаф"} меняется вместе с этим: корпус, кромка, цоколь и фасады у них одни.
      </p>}
      {/* «Сделать одинаково» для корпуса (29.10) — как у фасадов. */}
      {(selected.type === "single" || selected.type === "double" || selected.type === "corner45") &&
        project.modules.filter((module) => module.type === "single" || module.type === "double" || module.type === "corner45").length > 1 && <>
        <button type="button" className="product-add-action facade-apply-all"
          onClick={() => setCarcassApplied({ moduleId: selected.id, count: applyCarcassToAllCabinets(selected.id) })}>
          Применить этот корпус ко всем шкафам
        </button>
        {carcassApplied?.moduleId === selected.id && <small className="soft-note">
          {carcassApplied.count ? `Готово: декор корпуса, кромка и цоколь как у этого шкафа — ещё у ${carcassApplied.count} ${carcassApplied.count === 1 ? "шкафа" : "шкафов"}.` : "Другие шкафы уже такие же."}
        </small>}
      </>}
      <BackDecorSection project={project} module={selected} />
      <InteriorDecorSection project={project} module={selected} part="shelves" />
      <InteriorDecorSection project={project} module={selected} part="drawers" />
      <TopDecorSection module={selected} />
      <div className="soft-note">
        Материал применяется к выбранному изделию. Чтобы изменить другое,
        выберите его в списке сверху или нажмите на него в 3D-сцене.
      </div>
    </section>
  );
}

/**
 * Кромка корпуса (видео Сергея 24.10): как корпус, в цвет фасадов или свой
 * декор. Корпус чёрный, фасады белые — кромку делают белой, чтобы в зазорах
 * между фасадами не было тёмных полос.
 */
function EdgeFinishSection({ project, module }: { project: PlannerProject; module: WardrobeModule }) {
  const updateModule = usePlannerStore((state) => state.updateModule);
  // У антресоли шкафа кромка — как у шкафа под ней; у торцевого модуля своей настройки нет.
  if (module.type === "mezzanine" || module.type === "end") return null;
  if (module.type === "shelving" && (module.shelvingColorHex || shelvingCarcassEnamel(module))) return null;
  const edge = normalizeEdgeFinish(module.edgeFinish);
  const mode = !edge ? "carcass" : edge === EDGE_FINISH_FACADE ? "facade" : "own";
  const root = module.stackedOnId ? project.modules.find((candidate) => candidate.id === module.stackedOnId) ?? module : module;
  const stack = module.type === "shelving"
    ? [root, ...project.modules.filter((candidate) => candidate.stackedOnId === root.id && candidate.type === "shelving")]
    : [module];
  const set = (value: string | undefined) => stack.forEach((candidate) => updateModule(candidate.id, { edgeFinish: value }));
  const target = edgeFinishTarget(project, module);
  const carcass = resolveDecor(module.decor ?? project.carcassDecor);
  return (
    <div className="back-decor edge-finish">
      <div className="back-decor__heading">
        <span className="eyebrow">Кромка корпуса</span>
        <strong>{mode === "carcass" ? "Как корпус" : mode === "facade" ? "В цвет фасадов" : resolveDecor(edge).name}</strong>
      </div>
      <div className="cell-lighting__place edge-finish__modes" role="group" aria-label="Кромка корпуса">
        <button type="button" className={mode === "carcass" ? "is-selected" : ""} aria-pressed={mode === "carcass"} onClick={() => set(undefined)}>Как корпус</button>
        <button type="button" className={mode === "facade" ? "is-selected" : ""} aria-pressed={mode === "facade"} onClick={() => set(EDGE_FINISH_FACADE)}>Как фасады</button>
        <button type="button" className={mode === "own" ? "is-selected" : ""} aria-pressed={mode === "own"}
          onClick={() => mode !== "own" && set(BACK_DECOR_SUGGESTIONS.find((code) => code !== carcass.code) ?? BACK_DECOR_SUGGESTIONS[0])}>Свой декор</button>
      </div>
      {mode === "own" && <DecorPicker label="Декор кромки" value={edge} onChange={(code) => set(code)} />}
      {mode === "facade" && !target && <div className="soft-note back-decor__note">
        Фасады в том же декоре, что и корпус, или их нет — кромка совпадает с корпусом.
      </div>}
      <small className="sl-help">Торцы боковин, полок и стоек, которые видны в зазорах между фасадами.</small>
    </div>
  );
}

/** Комод: столешница как корпус или в своём декоре — по тому же принципу, что задняя стенка (15.10). */
function TopDecorSection({ module }: { module: WardrobeModule }) {
  const updateModule = usePlannerStore((state) => state.updateModule);
  if (module.type !== "shelving" || module.shelvingKind !== "chest" || module.stackedOnId) return null;
  const own = normalizeTopDecor(module);
  const topEnamel = chestTopEnamel(module);
  const noEnamel = { topEnamelHex: undefined, topEnamelCode: undefined };
  const setTop = (code: string | undefined) => updateModule(module.id, { topDecor: code, ...noEnamel });
  const enamel = chestEnamelFinish(module);
  const mode = topEnamel ? "enamel" : own ? "decor" : "carcass";
  return (
    <div className="back-decor">
      <div className="back-decor__heading">
        <span className="eyebrow">Столешница</span>
        <strong>{topEnamel ? `Эмаль · ${enamelColorLabel(topEnamel)}` : own ? resolveDecor(own).name : "Как корпус"}</strong>
      </div>
      <div className="cell-lighting__place" role="group" aria-label="Столешница">
        <button type="button" className={mode === "carcass" ? "is-selected" : ""} aria-pressed={mode === "carcass"} onClick={() => setTop(undefined)}>Как корпус</button>
        <button type="button" className={mode === "decor" ? "is-selected" : ""} aria-pressed={mode === "decor"}
          onClick={() => mode !== "decor" && setTop(BACK_DECOR_SUGGESTIONS[0])}>Свой декор</button>
        {/* Столешница в эмали (видео Сергея, 28.10). */}
        <button type="button" className={mode === "enamel" ? "is-selected" : ""} aria-pressed={mode === "enamel"}
          onClick={() => mode !== "enamel" && updateModule(module.id, { topDecor: undefined, topEnamelHex: shelvingCarcassEnamel(module)?.hex ?? "#F6F5F0", topEnamelCode: shelvingCarcassEnamel(module)?.code })}>Эмаль</button>
      </div>
      {topEnamel && <EnamelColorPicker hex={topEnamel.hex} code={topEnamel.code} onChange={(color) => updateModule(module.id, {
        topEnamelHex: color.hex ?? topEnamel.hex,
        topEnamelCode: "code" in color ? color.code : topEnamel.code
      })} />}
      {own && <>
        <div className="back-decor__swatches" role="group" aria-label="Популярные декоры столешницы">
          {BACK_DECOR_SUGGESTIONS.map((code) => {
            const decor = resolveDecor(code);
            const url = decorTextureUrl(decor);
            return <button key={code} type="button" className={own === code ? "is-selected" : ""} aria-pressed={own === code}
              title={`${decor.name} · ${decor.code}`} onClick={() => setTop(code)}>
              <i style={url ? { backgroundImage: `url(${url})`, backgroundSize: "cover" } : { backgroundColor: decor.hex }} aria-hidden="true" />
              <span>{decor.name}</span>
            </button>;
          })}
        </div>
        <DecorPicker label="Декор столешницы" value={own} onChange={(code) => setTop(code)} />
      </>}
      {enamel && <div className="soft-note back-decor__note">Фасады в эмали — цоколь комода тоже в эмали этого цвета, без фрезеровки.</div>}
    </div>
  );
}

/** Задняя стенка: как корпус или свой декор — например, чёрный задник в витрине. */
function BackDecorSection({ project, module }: { project: PlannerProject; module: WardrobeModule }) {
  const updateModule = usePlannerStore((state) => state.updateModule);
  if (!supportsBackDecor(module)) return null;
  const own = normalizeBackDecor(module);
  // Стеллаж с антресолью — одно изделие (видео Сергея 24.10): задник меняется у
  // обоих, а выбранный свой декор сразу включает общую заднюю стенку там, где её нет.
  const root = module.stackedOnId ? project.modules.find((candidate) => candidate.id === module.stackedOnId) ?? module : module;
  const stack = [root, ...project.modules.filter((candidate) => candidate.stackedOnId === root.id)]
    .filter((candidate) => supportsBackDecor(candidate));
  const enableBacks = () => stack
    .filter((candidate) => candidate.type === "shelving" && !shelvingHasBackPanels(candidate))
    .forEach((candidate) => updateModule(candidate.id, { shelvingBackPanel: true }));
  const setBack = (code: string | undefined) => {
    stack.forEach((candidate) => updateModule(candidate.id, { backDecor: code }));
    if (code) enableBacks();
  };
  const noBacks = stack.some((candidate) => candidate.type === "shelving" && !shelvingHasBackPanels(candidate));
  return (
    <div className="back-decor">
      <div className="back-decor__heading">
        <span className="eyebrow">Задняя стенка</span>
        <strong>{own ? resolveDecor(own).name : "Как корпус"}</strong>
      </div>
      <div className="cell-lighting__place cell-lighting__place--two" role="group" aria-label="Декор задней стенки">
        <button type="button" className={!own ? "is-selected" : ""} aria-pressed={!own} onClick={() => setBack(undefined)}>Как корпус</button>
        <button type="button" className={own ? "is-selected" : ""} aria-pressed={Boolean(own)}
          onClick={() => !own && setBack(BACK_DECOR_SUGGESTIONS[0])}>Свой декор</button>
      </div>
      {own && <>
        <div className="back-decor__swatches" role="group" aria-label="Популярные декоры задника">
          {BACK_DECOR_SUGGESTIONS.map((code) => {
            const decor = resolveDecor(code);
            const url = decorTextureUrl(decor);
            return <button key={code} type="button" className={own === code ? "is-selected" : ""} aria-pressed={own === code}
              title={`${decor.name} · ${decor.code}`} onClick={() => setBack(code)}>
              <i style={url ? { backgroundImage: `url(${url})`, backgroundSize: "cover" } : { backgroundColor: decor.hex }} aria-hidden="true" />
              <span>{decor.name}</span>
            </button>;
          })}
        </div>
        <DecorPicker label="Декор задней стенки" value={own} onChange={(code) => setBack(code)} />
      </>}
      {noBacks && <div className="soft-note back-decor__note">
        {stack.length > 1 ? "Задняя стенка есть не везде: у стеллажа или антресоли её нет." : "Сейчас у стеллажа нет задних стенок — декор будет виден, когда они появятся."}
        <button type="button" onClick={enableBacks}>Включить общую заднюю стенку</button>
      </div>}
    </div>
  );
}

/**
 * Полки или ящики шкафа в своём декоре (16.10) — один на весь шкаф: корпус и
 * антресоль над ним меняются вместе. У ящиков меняется лицевая панель; можно
 * «в цвет фасадов», если фасады ЛДСП или эмаль.
 */
function InteriorDecorSection({ project, module, part }: { project: PlannerProject; module: WardrobeModule; part: "shelves" | "drawers" }) {
  const updateModule = usePlannerStore((state) => state.updateModule);
  if (!supportsInteriorDecor(module)) return null;
  const key = part === "shelves" ? "shelfDecor" as const : "drawerDecor" as const;
  const value = part === "shelves" ? normalizeShelfDecor(module) : normalizeDrawerDecor(module);
  const own = value && value !== "facade" ? value : undefined;
  const base = module.stackedOnId ? project.modules.find((candidate) => candidate.id === module.stackedOnId) ?? module : module;
  const set = (code: string | undefined) => {
    [base, ...project.modules.filter((candidate) => candidate.stackedOnId === base.id)]
      .filter((target) => supportsInteriorDecor(target))
      .forEach((target) => updateModule(target.id, { [key]: code }));
  };
  const facadeOption = part === "drawers" && drawerFrontsCanFollowFacade(module);
  const title = part === "shelves" ? "Полки" : "Ящики";
  return (
    <div className="back-decor">
      <div className="back-decor__heading">
        <span className="eyebrow">{title}</span>
        <strong>{value === "facade" ? "В цвет фасадов" : own ? resolveDecor(own).name : "Как корпус"}</strong>
      </div>
      <div className={`cell-lighting__place cell-lighting__place--${facadeOption ? "three" : "two"}`} role="group" aria-label={`Декор: ${title.toLowerCase()}`}>
        <button type="button" className={!value ? "is-selected" : ""} aria-pressed={!value} onClick={() => set(undefined)}>Как корпус</button>
        <button type="button" className={own ? "is-selected" : ""} aria-pressed={Boolean(own)}
          onClick={() => !own && set(INTERIOR_DECOR_SUGGESTIONS[0])}>Свой декор</button>
        {facadeOption && <button type="button" className={value === "facade" ? "is-selected" : ""} aria-pressed={value === "facade"}
          onClick={() => set("facade")}>Как фасады</button>}
      </div>
      {own && <>
        <div className="back-decor__swatches" role="group" aria-label={`Популярные декоры: ${title.toLowerCase()}`}>
          {INTERIOR_DECOR_SUGGESTIONS.map((code) => {
            const decor = resolveDecor(code);
            const url = decorTextureUrl(decor);
            return <button key={code} type="button" className={own === code ? "is-selected" : ""} aria-pressed={own === code}
              title={`${decor.name} · ${decor.code}`} onClick={() => set(code)}>
              <i style={url ? { backgroundImage: `url(${url})`, backgroundSize: "cover" } : { backgroundColor: decor.hex }} aria-hidden="true" />
              <span>{decor.name}</span>
            </button>;
          })}
        </div>
        <DecorPicker label={part === "shelves" ? "Декор полок" : "Декор лицевых панелей ящиков"} value={own} onChange={(code) => set(code)} />
      </>}
      {part === "drawers" && value && <p className="sl-help">Меняется лицевая панель ящика, короб остаётся обычным.</p>}
    </div>
  );
}

function ItemEditor({
  module,
  item
}: {
  module: WardrobeModule;
  item: InternalItem;
}) {
  const updateItem = usePlannerStore((state) => state.updateItem);
  const remove = usePlannerStore((state) => state.removeSelectedItem);
  const setDividerEdge = usePlannerStore((state) => state.setDividerEdge);
  const setShelfPieceLight = usePlannerStore((state) => state.setShelfPieceLight);
  const [draftY, setDraftY] = useState(item.y);
  const [draftX, setDraftX] = useState(item.x ?? 0);
  const dividerXLimit = Math.max(0, module.width / 2 - 66);
  const dividerHeight =
    item.height ?? Math.min(1400, module.height - 180);
  // Низ и верх перегородки — отдельными ползунками (приёмка 07.10, wf-10).
  const dividerLimits = dividerSpanLimits(module);
  const dividerBottom = Math.round(item.y - dividerHeight / 2);
  const dividerTop = Math.round(item.y + dividerHeight / 2);
  const [draftBottom, setDraftBottom] = useState(dividerBottom);
  const [draftTop, setDraftTop] = useState(dividerTop);
  // Высота внутреннего ящика (31.10): не поместился — поле возвращается к прежней высоте.
  const [drawerNote, setDrawerNote] = useState("");
  const [drawerAttempt, setDrawerAttempt] = useState(0);
  useEffect(() => setDrawerNote(""), [item.id]);

  useEffect(() => {
    setDraftY(item.y);
    setDraftX(item.x ?? 0);
    setDraftBottom(dividerBottom);
    setDraftTop(dividerTop);
  }, [item.id, item.y, item.x, dividerBottom, dividerTop]);

  return (
    <div className="item-editor">
      <div className="item-editor__heading">
        <div>
          <span className="eyebrow">Выбранный элемент{itemCompartmentLabel(module, item) ? ` · ${itemCompartmentLabel(module, item)}` : ""}</span>
          <h3>{INTERNALS[item.type].name}</h3>
        </div>
        <button className="icon-danger" type="button" onClick={remove}>
          <Icon name="trash" size={17} />
        </button>
      </div>
      {item.type !== "divider" && <label className="range-field">
        <span>
          Высота установки <strong>{draftY} мм</strong>
        </span>
        <input
          type="range"
          min={internalItemMinimumY(module, item)}
          max={module.height - 80}
          step={32}
          value={draftY}
          onChange={(event) => setDraftY(Number(event.target.value))}
          onPointerUp={(event) =>
            updateItem(module.id, item.id, {
              y: Number(event.currentTarget.value)
            })
          }
          onKeyUp={(event) =>
            updateItem(module.id, item.id, {
              y: Number(event.currentTarget.value)
            })
          }
        />
      </label>}
      {item.type === "drawer" && <>
        <DimensionField key={`${item.id}-${drawerAttempt}`} label="Высота ящика" value={item.height ?? 180}
          min={INTERNAL_DRAWER_MIN_HEIGHT_MM} max={INTERNAL_DRAWER_MAX_HEIGHT_MM} onInvalid={setDrawerNote}
          onCommit={(height) => {
            if (height === item.height) return;
            if (!resizeInternalDrawer(module, item.id, height)) {
              setDrawerNote(`Ящик ${height} мм здесь не помещается: мешает полка или ящик выше. Поднимите их или выберите высоту меньше.`);
              setDrawerAttempt((value) => value + 1);
              return;
            }
            setDrawerNote("");
            updateItem(module.id, item.id, { height });
          }} />
        <p className={drawerNote ? "soft-note" : "sl-help"}>{drawerNote ||
          `От ${INTERNAL_DRAWER_MIN_HEIGHT_MM} до ${INTERNAL_DRAWER_MAX_HEIGHT_MM} мм. Ящик растёт вверх; если выше полка — встанет ниже, где есть место.`}</p>
      </>}
      {item.type === "divider" && (() => {
        // От опоры до опоры (приёмка 22.10): края двигаются кнопками по полкам.
        const steps = dividerEdgeSteps(module, item);
        const floorTop = interiorVerticalRangeMm(module)[0];
        const roof = module.height - CARCASS_MM;
        const where = (level: number, edge: "bottom" | "top") => edge === "bottom"
          ? Math.abs(level - floorTop) < 1 ? "на дне" : `на полке (${Math.round(level)} мм от пола)`
          : Math.abs(level - roof) < 1 ? "в крышу" : `в полку (${Math.round(level)} мм от пола)`;
        return <>
          <p className="sl-help">Перегородка {dividerTop - dividerBottom} мм: стоит {where(dividerBottom, "bottom")}, упирается {where(dividerTop, "top")}.
            Тяните её в 3D — она встанет в соседний промежуток от полки до полки.</p>
          {steps && <div className="divider-edge-steps">
            <span>Верх</span>
            <button type="button" disabled={steps.topDown === null} onClick={() => steps.topDown !== null && setDividerEdge(module.id, item.id, "top", steps.topDown)}>на полку ниже</button>
            <button type="button" disabled={steps.topUp === null} title={steps.blockedAbove ? "Выше ящик или корзина" : undefined}
              onClick={() => steps.topUp !== null && setDividerEdge(module.id, item.id, "top", steps.topUp)}>на полку выше</button>
            <span>Низ</span>
            <button type="button" disabled={steps.bottomDown === null} title={steps.blockedBelow ? "Ниже ящик или корзина" : undefined}
              onClick={() => steps.bottomDown !== null && setDividerEdge(module.id, item.id, "bottom", steps.bottomDown)}>ниже</button>
            <button type="button" disabled={steps.bottomUp === null} onClick={() => steps.bottomUp !== null && setDividerEdge(module.id, item.id, "bottom", steps.bottomUp)}>выше</button>
          </div>}
          {steps?.blockedBelow && <p className="sl-help divider-blocked">Ниже ящик или корзина — перегородку до дна не поставить. Поставьте над ними полку или уберите их.</p>}
          <label className="range-field">
            <span>
              Смещение влево/вправо <strong>{draftX} мм</strong>
            </span>
            <input
              type="range"
              min={-dividerXLimit}
              max={dividerXLimit}
              step={32}
              value={draftX}
              onChange={(event) => setDraftX(Number(event.target.value))}
              onPointerUp={(event) =>
                updateItem(module.id, item.id, {
                  x: Number(event.currentTarget.value)
                })
              }
              onKeyUp={(event) =>
                updateItem(module.id, item.id, {
                  x: Number(event.currentTarget.value)
                })
              }
            />
          </label>
        </>;
      })()}
      {item.type === "shelf" && cabinetLightMode(module) === "shelves" && (
        <div className="shelf-light-toggle">
          {(() => {
            // Полка идёт через несколько отсеков — свет включается в каждом отдельно (wf-15).
            const pieces = usableItemPieces(module, item);
            if (pieces.length < 2) return <button type="button" className={`module-lighting__toggle${item.light ? " is-selected" : ""}`} aria-pressed={Boolean(item.light)}
              onClick={() => updateItem(module.id, item.id, { light: item.light ? undefined : true })}>
              <span className="cell-option-mark">{item.light ? "✓" : "+"}</span>
              <span><strong>Подсветка под этой полкой</strong><small>{item.light ? "Включена только у этой полки" : "Выключена только у этой полки"}</small></span>
            </button>;
            const names = pieces.length === 2 ? ["в левом отсеке", "в правом отсеке"]
              : pieces.length === 3 ? ["в левом отсеке", "в среднем отсеке", "в правом отсеке"]
                : pieces.map((_, index) => `в отсеке ${index + 1}`);
            return pieces.map((piece, index) => <button key={index} type="button" className={`module-lighting__toggle${item.light ? " is-selected" : ""}`}
              aria-pressed={Boolean(item.light)} onClick={() => setShelfPieceLight(module.id, item.id, piece.center, !item.light)}>
              <span className="cell-option-mark">{item.light ? "✓" : "+"}</span>
              <span><strong>Подсветка {names[index]}</strong><small>{item.light ? "Нажмите, чтобы выключить только здесь" : "Нажмите, чтобы включить только здесь"}</small></span>
            </button>);
          })()}
        </div>
      )}
      {item.type === "rail" && (
        <p className={clothesFitBelowRail(module, item) ? "sl-help" : "soft-note"}>{railClothesText(module, item)}.</p>
      )}
    </div>
  );
}

function CornerPlanPreview({ module, selectedItem }: { module: WardrobeModule; selectedItem?: InternalItem }) {
  // Угловой (28.10): один корпус вдоль стены; за глухой частью — соседний шкаф (пунктир).
  const plan = cornerPlan(module);
  const right = cornerSide(module) === "right";
  const reach = Math.max(plan.a, plan.b, 1450);
  const x = (value: number) => right ? reach - value : value;
  const points = (values: [number, number][]) => values.map(([px, pz]) => `${x(px)},${pz}`).join(" ");
  const quad = (u0: number, u1: number, v0: number, v1: number) =>
    points([cornerToPlan(module, u0, v0), cornerToPlan(module, u1, v0), cornerToPlan(module, u1, v1), cornerToPlan(module, u0, v1)]);
  const door = cornerDoorSpan(module);
  const rail = cornerRailPlan(module);
  const neighbourDepth = plan.blind - 23;
  // Сосед — за добором соседа (31.10); без дверей доборов нет.
  const doors = module.door !== "open";
  const neighbourStart = doors ? plan.d + 23 + plan.neighbourFiller : plan.d;
  const neighbour = plan.run === "back"
    ? points([[0, neighbourStart], [neighbourDepth, neighbourStart], [neighbourDepth, neighbourStart + 600], [0, neighbourStart + 600]])
    : points([[neighbourStart, 0], [neighbourStart + 600, 0], [neighbourStart + 600, neighbourDepth], [neighbourStart, neighbourDepth]]);
  // Петли обеих дверей — у угла: точки на кромках дверей.
  const hingeDots = doors ? [cornerToPlan(module, door.from + 25, plan.d + 13), cornerToPlan(module, plan.blind - 10, neighbourStart + 25)] : [];
  return <svg className="corner-plan" viewBox={`-240 -150 ${reach + 480} ${reach + 270}`} role="img"
    aria-label={`Угловой шкаф, вид сверху: корпус ${plan.length} мм вдоль ${plan.run === "back" ? "задней" : "боковой"} стены, глухая часть ${plan.blind} мм, добор углового ${plan.filler} мм, добор соседа ${plan.neighbourFiller} мм, дверь ${plan.door} мм; петли обеих дверей у угла.`}>
    <path d={`M${x(0)} ${reach}V0H${x(reach)}`} fill="none" stroke="#b7ab9b" strokeWidth="20" />
    <polygon points={neighbour} fill="none" stroke="#9a8f80" strokeWidth="10" strokeDasharray="34 24" />
    <polygon points={points(plan.points)} fill="#e7d8c3" stroke="#8e7255" strokeWidth="14" />
    <polygon points={quad(0, plan.blind, 0, plan.d)} fill="#d8c6ad" />
    {selectedItem?.type === "shelf" && <polygon points={points(cornerShelfPlan(module))} fill="#b6cbbb" stroke="#41624c" strokeWidth="14" />}
    {module.items.filter(item => item.type === "rail").map(item =>
      <line key={item.id} x1={x(rail.from[0])} y1={rail.from[1]} x2={x(rail.to[0])} y2={rail.to[1]}
        stroke={item.id === selectedItem?.id ? "#285e44" : "#8e8374"} strokeWidth={item.id === selectedItem?.id ? 26 : 12} strokeLinecap="round" />)}
    {doors && <>
      <polygon points={quad(plan.blind, plan.blind + plan.filler, plan.d + 3, plan.d + 23)} fill="#8e7255" />
      <polygon points={quad(plan.blind - 20, plan.blind, plan.d + 3, neighbourStart)} fill="#8e7255" />
      <polygon points={quad(door.from, door.to, plan.d + 3, plan.d + 23)} fill="#352b23" />
      {hingeDots.map(([hx, hz], index) => <circle key={index} cx={x(hx)} cy={hz} r="24" fill="#285e44" />)}
    </>}
    {(() => {
      const [lx, lz] = cornerToPlan(module, plan.blind / 2, plan.d / 2);
      return <text x={x(lx)} y={lz + 22} textAnchor="middle" fontSize="64" fill="#4b3b2c">глухая</text>;
    })()}
    <text x={x(plan.a / 2)} y="-65" textAnchor="middle" fontSize="68" fill="currentColor">{plan.run === "back" ? `${plan.length} мм` : `${plan.d}`}</text>
    {plan.run === "side" && <text x={right ? reach + 100 : -100} y={plan.b / 2} textAnchor="middle" fontSize="64" fill="currentColor"
      transform={`rotate(${right ? 90 : -90} ${right ? reach + 100 : -100} ${plan.b / 2})`}>{plan.length} мм</text>}
  </svg>;
}

function CornerInternalsPanel({ module, selectedItemId, notify }: {
  module: WardrobeModule; selectedItemId: string | null; notify: (message: string) => void;
}) {
  const addItem = usePlannerStore(state => state.addItem);
  const selectItem = usePlannerStore(state => state.selectItem);
  const updateItem = usePlannerStore(state => state.updateItem);
  const updateModule = usePlannerStore(state => state.updateModule);
  const removeItem = usePlannerStore(state => state.removeSelectedItem);
  const sorted = [...module.items].sort((a, b) => b.y - a.y);
  const selected = module.items.find(item => item.id === selectedItemId);
  const canAdd = (type: "shelf" | "rail") => type === "rail"
    ? cornerCanAddRail(module) && cornerRailPlacement(module,
      { id: "new-corner-item", type, y: INTERNALS.rail.defaultY }, INTERNALS.rail.defaultY) !== undefined
    : cornerItemPosition(module, { id: "new-corner-item", type, y: INTERNALS[type].defaultY }, INTERNALS[type].defaultY) !== undefined;
  const railCount = module.items.filter(item => item.type === "rail").length;
  const setHeight = (y: number) => {
    if (!selected) return;
    updateItem(module.id, selected.id, { y });
    const actual = usePlannerStore.getState().project.modules.find(m => m.id === module.id)?.items.find(i => i.id === selected.id);
    if (actual && actual.y !== y) notify(`Установлена высота ${actual.y} мм: учтены шаг крепления 32 мм и соседнее наполнение`);
  };
  const rail = cornerRailPlan(module);
  return <section className="panel-section corner-filling">
    <div className="section-heading"><h3>Наполнение углового шкафа</h3></div>
    <CornerPlanPreview module={module} selectedItem={selected} />
    <p className="sl-help">Вид сверху. Внутри углового — только полки и штанги: во всю длину корпуса, вглубь угла за соседний шкаф.</p>
    <div className="add-module-grid">
      <button type="button" disabled={!canAdd("shelf")} onClick={() => addItem("shelf")}><Icon name="plus" size={17} />Добавить полку</button>
      <button type="button" disabled={!canAdd("rail")} onClick={() => addItem("rail")}><Icon name="plus" size={17} />Добавить штангу</button>
    </div>
    {!canAdd("shelf") && <p className="soft-note">Для нового уровня недостаточно места. Удалите элемент или увеличьте высоту корпуса.</p>}
    <p className="sl-help">{railCount >= CORNER_MAX_RAILS
      ? `Штанг уже ${CORNER_MAX_RAILS} — больше в угловой не ставится: вещи верхней заканчиваются над нижней.`
      : railCount === 1 && cornerSecondRailShortfallMm(module) > 0
        ? `Для второй штанги не хватает ${cornerSecondRailShortfallMm(module)} мм высоты: вещи на обеих должны быть не короче 800 мм. Поднимите полку над штангой или увеличьте высоту корпуса.`
      : railCount === 1
        ? "Вторая штанга встанет ниже или выше первой, так чтобы вещам на обеих хватило места."
        : `Штанг — до двух. Штанга ${Math.round(rail.length)} мм: от стены в углу до наружного торца.`}</p>
    <div className="corner-levels" aria-label="Уровни наполнения сверху вниз">
      {sorted.map((item, index) => <button type="button" key={item.id} aria-pressed={item.id === selected?.id}
        onClick={() => selectItem(module.id, item.id)}>
        <span className="corner-levels__index">{index + 1}</span>
        <span><strong>{item.type === "shelf" ? "Полка" : "Штанга"}</strong>
          <small>{item.type === "shelf" ? "Центр полки" : "Ось штанги"} от пола</small></span>
        <span>{item.y} мм</span>
      </button>)}
    </div>
    {!selected && <p className="soft-note">{sorted.length ? "Выберите уровень в списке или нажмите на наполнение в 3D, чтобы настроить его." : "Добавьте первую полку или штангу."}</p>}
    {selected && <div className="corner-item-editor" key={selected.id}>
      <strong>{selected.type === "shelf" ? "Настройка полки" : "Настройка штанги"}</strong>
      {selected.type === "rail" && <p className="sl-help">Длина штанги {Math.round(rail.length)} мм — во всю длину, вглубь угла.</p>}
      <DimensionField label={selected.type === "shelf" ? "Центр полки от пола" : "Ось штанги от пола"}
        value={selected.y} min={cornerItemBounds(module, selected).min} max={cornerItemBounds(module, selected).max}
        onInvalid={notify} onCommit={setHeight} />
      <div className="corner-level-actions">
        <button type="button" className="secondary-button" disabled={cornerItemPosition(module, selected, selected.y - 32) === selected.y}
          onClick={() => setHeight(selected.y - 32)}>↓ Ниже на 32 мм</button>
        <button type="button" className="secondary-button" disabled={cornerItemPosition(module, selected, selected.y + 32) === selected.y}
          onClick={() => setHeight(selected.y + 32)}>↑ Выше на 32 мм</button>
      </div>
      {selected.type === "rail" && <p className={clothesFitBelowRail(module, selected) ? "sl-help" : "soft-note"}>{railClothesText(module, selected)}.</p>}
      <button type="button" className="danger-button" onClick={removeItem}>Удалить {selected.type === "shelf" ? "полку" : "штангу"}</button>
    </div>}
  </section>;
}

/**
 * Настройка ячеек стеллажа — в выезжающей панели слева, с любого шага.
 * Отдельного «продвинутого конструктора» больше нет: нажали ячейку в 3D или
 * на схеме — панель открылась, закрыли — снова выделено всё изделие.
 */
function ShelvingCellsSlideOut({ project, module, onEditStructure }: {
  project: PlannerProject;
  module: WardrobeModule;
  onEditStructure: () => void;
}) {
  const parsed = project.selectedShelvingCellId ? parseShelvingCellId(project.selectedShelvingCellId) : null;
  const title = parsed && parsed.moduleId === module.id
    ? `Колонка ${parsed.column + 1} · ряд ${parsed.row + 1}`
    : "Ячейки стеллажа";
  return (
    <SlideOutPanel owner={SHELVING_CELLS_PANEL} eyebrow={module.stackedOnId ? "Ячейки антресоли" : "Ячейки стеллажа"}
      title={title} onClose={onEditStructure}>
      <ShelvingLayoutEditor key={module.id} project={project} module={module} onEditStructure={onEditStructure}>
        <ShelvingCellInternalsEditor project={project} module={module} />
      </ShelvingLayoutEditor>
      <div className="soft-note shelving-section-note">
        Нажмите на дверцу или ящик в 3D, чтобы открыть его. Повторное нажатие
        закрывает элемент.
      </div>
    </SlideOutPanel>
  );
}

function InternalsPanel({
  project,
  notify,
  onEditStructure
}: {
  project: PlannerProject;
  notify: (message: string) => void;
  onEditStructure: () => void;
}) {
  const addItem = usePlannerStore((state) => state.addItem);
  const selectItem = usePlannerStore((state) => state.selectItem);
  const prices = usePriceStore((state) => state.values);
  const applyFillingPreset = usePlannerStore(
    (state) => state.applyFillingPreset
  );
  const copyFillingToModule = usePlannerStore(
    (state) => state.copyFillingToModule
  );
  const selectedModule = project.modules.find(
    (module) => module.id === project.selectedModuleId
  );
  const selectedItem = selectedModule?.items.find(
    (item) => item.id === project.selectedItemId
  );
  // Корзины и брючница — только под пенал 600–900 мм: нет такого отсека — кнопка неактивна.
  const nominalBlocked = (type: ItemType) => Boolean(selectedModule) && NOMINAL_WIDTH_FITTINGS.has(type) &&
    !usableItemPieces(selectedModule!, { id: "probe", type, y: INTERNALS[type].defaultY, height: INTERNALS[type].height }).length;
  const blockedNominal = (Object.keys(INTERNALS) as ItemType[]).filter(nominalBlocked);

  if (!selectedModule) return <ModuleSelectionPrompt />;
  if (selectedModule.type === "corner45") return <CornerInternalsPanel module={selectedModule}
    selectedItemId={project.selectedItemId} notify={notify} />;
  if (selectedModule.type === "end") {
    return (
      <section className="panel-section">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Торцевой модуль</span>
            <h3>{endModuleTierCount(selectedModule)} открытых ярусов</h3>
          </div>
        </div>
        <div className="soft-note">
          Ярусы распределяются равномерно. Их количество и форма меняются в
          разделе «Корпусы», декор — в разделе «Материал».
        </div>
      </section>
    );
  }
  if (selectedModule.type === "shelving") {
    return (
      <section className="panel-section">
        <div className="section-heading">
          <div>
            <span className="eyebrow">{selectedModule.stackedOnId ? "Антресоль стеллажа" : "Стеллаж"}</span>
            <h3>{selectedModule.stackedOnId ? "Ячейки антресоли" : "Ячейки стеллажа"}</h3>
          </div>
        </div>
        <ShelvingLayoutEditor key={selectedModule.id} project={project} module={selectedModule} onEditStructure={onEditStructure}>
          <ShelvingCellInternalsEditor project={project} module={selectedModule} />
        </ShelvingLayoutEditor>
        <div className="soft-note shelving-section-note">
          Нажмите на дверцу или ящик в 3D, чтобы открыть его. Повторное нажатие
          закрывает элемент.
        </div>
      </section>
    );
  }

  const compatibleTargets = project.modules.filter(
    (module) =>
      module.id !== selectedModule.id &&
      module.type !== "shelving" &&
      module.type !== "corner45" &&
      module.type !== "mezzanine" &&
      module.type !== "end"
  );

  const clearSection = () => {
    if (!window.confirm("Убрать из секции всё наполнение?")) return;
    applyFillingPreset("empty");
    notify("Секция очищена");
  };

  return (
    <>
      <section className="panel-section">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Секция</span>
            <h3>
              {productModules(project, "wardrobe").findIndex((module) => module.id === selectedModule.id) + 1}.{" "}
              {MODULE_NAMES[selectedModule.type]}
            </h3>
          </div>
          <span className="section-metric">{selectedModule.items.length} элем.
            {selectedModule.items.length > 0 && <button type="button" className="section-clear-link" onClick={clearSection}>Очистить</button>}
          </span>
        </div>
        {selectedModule.items.length ? (
          <div className="item-list">
            {[...selectedModule.items]
              .sort((a, b) => b.y - a.y)
              .map((item) => (
                <button
                  className={`item-row${item.id === selectedItem?.id ? " is-selected" : ""}`}
                  type="button"
                  key={item.id}
                  onClick={() => selectItem(selectedModule.id, item.id)}
                >
                  <span className="item-row__icon">
                    <Icon name={item.type === "rail" ? "layers" : "shelf"} size={17} />
                  </span>
                  <span>
                    <strong>{INTERNALS[item.type].name}</strong>
                    <small>{itemCompartmentLabel(selectedModule, item) ?? INTERNALS[item.type].short}</small>
                  </span>
                  <span className="item-row__height">{item.y}</span>
                </button>
              ))}
          </div>
        ) : (
          <div className="empty-state">
            <Icon name="shelf" />
            <strong>Секция пока пустая</strong>
            <span>Добавьте полки, штанги или системы хранения</span>
          </div>
        )}
        {selectedModule.items.length > 0 && (
          <button type="button" className="secondary-button" onClick={clearSection}>Очистить секцию</button>
        )}
        {/* Замечания по этой секции видны сразу здесь, а не только в «Итоге» (приёмка 04.10, wf-13). */}
        {(() => {
          const sectionWarnings = validateProject(project).filter(message => message.moduleId === selectedModule.id ||
            (message.moduleId && project.modules.find(module => module.id === message.moduleId)?.stackedOnId === selectedModule.id));
          return sectionWarnings.length ? <div className="soft-note section-warnings">
            {sectionWarnings.map(message => <p key={message.id}>{message.text}</p>)}
          </div> : null;
        })()}
      </section>

      {selectedItem && <ItemEditor module={selectedModule} item={selectedItem} />}

      {cabinetSupportsLighting(selectedModule) && <section className="panel-section">
        <CabinetLightingControls module={selectedModule} />
      </section>}

      {selectedModule.items.length > 0 && compatibleTargets.length > 0 && (
        <section className="panel-section">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Повторить схему</span>
              <h3>Скопировать в другую секцию</h3>
            </div>
          </div>
          <div className="copy-filling-list">
            {compatibleTargets.map((target) => {
              const targetIndex = productModules(project, "wardrobe").findIndex(
                (module) => module.id === target.id
              );
              return (
                <button
                  type="button"
                  key={target.id}
                  onClick={() => {
                    if (
                      target.items.length > 0 &&
                      !window.confirm(
                        `В секции ${targetIndex + 1} уже есть наполнение. Заменить его?`
                      )
                    ) {
                      return;
                    }
                    copyFillingToModule(target.id);
                    notify(`Наполнение скопировано в секцию ${targetIndex + 1}`);
                  }}
                >
                  <span className="module-card__number">{targetIndex + 1}</span>
                  <span>
                    <strong>{MODULE_NAMES[target.type]}</strong>
                    <small>
                      {target.width} × {target.height} × {target.depth} мм
                    </small>
                  </span>
                  <Icon name="chevron" size={17} />
                </button>
              );
            })}
          </div>
        </section>
      )}

      <section className="panel-section">
        <span className="eyebrow">Добавить наполнение</span>
        <div className="catalog-grid">
          {(Object.keys(INTERNALS) as ItemType[]).filter((type) => type !== "led").map((type) => {
            // Корзины и брючница — только в пенал 600, 700, 800 или 900 мм (замечание № 26, видео 31.10).
            const blocked = nominalBlocked(type);
            return <button type="button" key={type} onClick={() => addItem(type)} disabled={blocked}
              title={blocked ? nominalFittingHint(type) : undefined}>
              <span className="catalog-grid__icon">
                <Icon name={type === "rail" || type === "pantograph" || type === "trouserRack" ? "layers" : "shelf"} />
              </span>
              <span>
                <strong>{INTERNALS[type].name}</strong>
                <small>{blocked ? "Пенал 600–900 мм" : priceValue(prices, priceKeys.item(type)) !== undefined
                  ? `+ ${formatPrice(priceValue(prices, priceKeys.item(type))!)} ₽` : INTERNALS[type].short}</small>
              </span>
            </button>;
          })}
        </div>
        {blockedNominal.length > 0 && <p className="sl-help">Корзины и брючницу выпускают под пенал 600, 700, 800 или 900 мм.
          В {selectedModule.type === "double" ? "этот широкий пенал" : "этот отсек"} их не поставить — только ящики, полки и штанги.
          В широкий пенал корзина встанет, если разделить его стойкой на два отсека.</p>}
      </section>
    </>
  );
}

/**
 * Наружные ящики внизу шкафа (пример Сергея: пенал 1200, внизу три ящика
 * со своими фасадами, выше шкаф со штангой). В каждом пролёте корпуса.
 */
function CabinetDrawersControl({ module, notify }: { module: WardrobeModule; notify: (message: string) => void }) {
  const updateModule = usePlannerStore((state) => state.updateModule);
  if (!supportsCabinetDrawers(module)) return null;
  const count = cabinetDrawerCount(module);
  const frontHeight = cabinetDrawerFrontHeightMm(module);
  const max = maxCabinetDrawerCount(module);
  const layout = cabinetDrawerLayout(module);
  const bays = cabinetSectionWidths(module.width).length;
  return <div className="cabinet-drawers">
    <div className="shelving-flow-heading">
      <strong>Выдвижные цокольные ящики</strong>
      <span>Свои фасады, выше — двери</span>
    </div>
    <div className="segmented-control cabinet-drawers__count" role="group" aria-label="Число цокольных ящиков">
      {[0, 1, 2, 3, 4].map((value) => (
        <button type="button" key={value}
          className={count === value ? "is-selected" : ""}
          aria-pressed={count === value}
          disabled={value > max}
          title={value > max ? "Над ящиками не останется места под дверь" : undefined}
          onClick={() => updateModule(module.id, { cabinetDrawers: value || undefined })}>
          {value === 0 ? "Нет" : value}
        </button>
      ))}
    </div>
    {count > 0 && <div className="cabinet-drawers__height">
      <DimensionField label="Высота фасада ящика" value={frontHeight}
        min={CABINET_DRAWER_MIN_FRONT_MM} max={CABINET_DRAWER_MAX_FRONT_MM} onInvalid={notify}
        onCommit={(height) => updateModule(module.id, { cabinetDrawerHeight: height })} />
    </div>}
    <p className="sl-help">
      {layout
        ? `${bays > 1 ? `В каждом из ${bays} пролётов` : "В пролёте"} ${count} ${count === 1 ? "ящик" : "ящика"} с фасадом ${frontHeight} мм, над ними неподвижная полка. Ручек нет: над каждым фасадом зазор ${CABINET_DRAWER_SEAM_MM} мм — зацеп под палец. Двери начинаются на высоте ${layout.doorBottomMm} мм, наполнение шкафа — выше полки.`
        : max > 0
          ? "Например, три ящика внизу, выше — шкаф со штангой. Фасад ящика не ниже 250 мм."
          : "Корпус слишком низкий: над ящиками не останется места под дверь."}
    </p>
  </div>;
}


/** Вид сверху: две стойки и фасад перед ними (накладной) или между ними (вкладной). */
function FacadeMountSketch({ mount }: { mount: "overlay" | "inset" }) {
  return <svg className="facade-mount-sketch" viewBox="0 0 72 28" aria-hidden="true">
    <rect x="6" y="3" width="6" height="19" rx="0.5" className="facade-mount-sketch__board" />
    <rect x="60" y="3" width="6" height="19" rx="0.5" className="facade-mount-sketch__board" />
    {mount === "overlay"
      ? <rect x="7" y="22.6" width="58" height="4.4" rx="0.8" className="facade-mount-sketch__facade" />
      : <rect x="13" y="17.6" width="46" height="4.4" rx="0.8" className="facade-mount-sketch__facade" />}
  </svg>;
}

/** Накладные или вкладные фасады — одним выбором на весь стеллаж (и его антресоль). */
function ShelvingFacadeMountPicker({ project, module }: { project: PlannerProject; module: WardrobeModule }) {
  const updateModule = usePlannerStore((state) => state.updateModule);
  const mount = shelvingFacadeMount(module);
  const baseId = module.stackedOnId ?? module.id;
  const run = project.modules.filter((candidate) => candidate.type === "shelving" &&
    (candidate.id === baseId || candidate.stackedOnId === baseId));
  const choose = (value: "overlay" | "inset") => {
    run.forEach((candidate) => updateModule(candidate.id, { shelvingFacadeMount: value === "inset" ? "inset" : undefined }));
  };
  return <div className="shelving-facade-mount">
    <div className="shelving-flow-heading">
      <strong>Как стоят фасады</strong>
      <span>{module.shelvingKind === "chest" ? "Для всех ящиков комода" : "Для всех дверец и ящиков стеллажа"}</span>
    </div>
    <div className="shelving-base-grid shelving-base-grid--two" role="group" aria-label="Крепление фасадов">
      {([
        ["overlay", "Накладные", "Закрывают торцы стоек и полок"],
        ["inset", "Вкладные", "В проёме вровень с кромкой, торцы стоек видны"]
      ] as const).map(([value, label, description]) => (
        <button type="button" key={value} aria-pressed={mount === value} onClick={() => choose(value)}>
          <FacadeMountSketch mount={value} />
          <strong>{label}</strong><small>{description}</small>
        </button>
      ))}
    </div>
  </div>;
}

/** Цвет петель (видео Сергея 23.10): обычно серебристые, бывают чёрные. */
function HingeColorPicker({ module }: { module: WardrobeModule }) {
  const updateModule = usePlannerStore((state) => state.updateModule);
  const black = module.hingeColor === "black";
  return <div className="hinge-color-picker">
    <span className="eyebrow">Цвет петель</span>
    <div className="segmented-control two" role="group" aria-label="Цвет петель">
      <button type="button" className={!black ? "is-selected" : ""} aria-pressed={!black}
        onClick={() => updateModule(module.id, { hingeColor: undefined })}>Серебристые</button>
      <button type="button" className={black ? "is-selected" : ""} aria-pressed={black}
        onClick={() => updateModule(module.id, { hingeColor: "black" })}>Чёрные</button>
    </div>
  </div>;
}

/**
 * Фасады антресоли (видео Сергея 24.10): отделка, цвет, фрезеровка и ручка —
 * как у шкафа под ней и меняются вместе с ним; своё — только открывание.
 */
function MezzanineFacadePanel({ project, module, base }: { project: PlannerProject; module: WardrobeModule; base: WardrobeModule }) {
  const updateModule = usePlannerStore((state) => state.updateModule);
  const selectModule = usePlannerStore((state) => state.selectModule);
  return <>
    <section className="panel-section">
      <div className="section-heading">
        <div><span className="eyebrow">Антресоль</span><h3>Фасад — как у шкафа</h3></div>
      </div>
      <div className="soft-note">
        {base.door === "open"
          ? "У шкафа под антресолью нет дверей — у антресоли их тоже нет."
          : "Отделка, цвет, фрезеровка и ручка антресоли — те же, что у шкафа под ней, и меняются вместе с ним: у дверей одна толщина и один цвет."}
      </div>
      <button type="button" className="product-add-action" onClick={() => selectModule(base.id)}>Настроить фасад шкафа</button>
    </section>
    {module.door === "hinged" && <section className="panel-section">
      <MezzanineOpeningPicker module={module} headroomMm={moduleHeadroomMm(project, module)} onPatch={(patch) => updateModule(module.id, patch)} />
      {!mezzanineFlapLayout(module) && doorLeafCount(module) === 1 && <>
        <span className="eyebrow">Сторона открывания</span>
        <div className="segmented-control two">
          {([["left", "Петли слева"], ["right", "Петли справа"]] as const).map(([value, label]) => (
            <button key={value} type="button" className={module.doorHinge === value ? "is-selected" : ""}
              onClick={() => updateModule(module.id, { doorHinge: value })}>{label}</button>
          ))}
        </div>
      </>}
    </section>}
  </>;
}

function FacadesPanel({ project }: { project: PlannerProject }) {
  const selected = project.modules.find(
    (module) => module.id === project.selectedModuleId
  );
  const updateModule = usePlannerStore((state) => state.updateModule);
  const applyFacadeToAllCabinets = usePlannerStore((state) => state.applyFacadeToAllCabinets);
  const [appliedToAll, setAppliedToAll] = useState<{ moduleId: string; count: number } | null>(null);
  // Какую створку настраиваем (16.10): null — все двери шкафа.
  const [doorTarget, setDoorTarget] = useState<{ moduleId: string; index: number } | null>(null);
  const prices = usePriceStore((state) => state.values);
  // Фасад ЛДСП — по цене плиты своего декора: «от» самой недорогой группы.
  const facadeFrom = (key: FacadeType) => {
    if (key !== "wood") return priceValue(prices, priceKeys.facade(key));
    const boards = (["plain", "wood", "stone", "metal", "fabric"] as const)
      .map((group) => priceValue(prices, priceKeys.board(group)))
      .filter((value): value is number => value !== undefined);
    return boards.length ? Math.min(...boards) : undefined;
  };
  if (!selected) return <ModuleSelectionPrompt />;
  if (selected.type === "end") {
    return (
      <section className="panel-section">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Торцевой модуль</span>
            <h3>Открытое завершение без фасада</h3>
          </div>
        </div>
        <div className="soft-note">
          Для торцевого модуля фасады и ручки не применяются. Форма, сторона и
          количество ярусов задаются в разделе «Корпусы».
        </div>
      </section>
    );
  }
  const mezzanineBaseModule = selected.type === "mezzanine" ? baseModuleFor(project, selected) : undefined;
  if (selected.type === "mezzanine" && mezzanineBaseModule) {
    return <MezzanineFacadePanel project={project} module={selected} base={mezzanineBaseModule} />;
  }
  if (selected.type === "shelving" && selected.shelvingKind === "chest") {
    return (
      <section className="panel-section">
        <ShelvingFacadeMountPicker project={project} module={selected} />
        {(selected.shelvingCells ?? []).some((cell) => cell.content === "door") && <HingeColorPicker module={selected} />}
        <ShelvingCellFacadesEditor project={project} module={selected} applyToAll />
        <div className="soft-note shelving-section-note">
          Число колонок и ящиков меняется на шаге «Размеры». Нажмите на ящик в 3D — он выдвинется.
        </div>
      </section>
    );
  }
  if (selected.type === "shelving") {
    return (
      <section className="panel-section">
        <div className="section-heading">
          <div>
            <span className="eyebrow">{selected.stackedOnId ? "Антресоль стеллажа" : "Стеллаж"}</span>
            <h3>Фасад выбранной ячейки</h3>
          </div>
        </div>
        <ShelvingFacadeMountPicker project={project} module={selected} />
        {(selected.shelvingCells ?? []).some((cell) => cell.content === "door") && <HingeColorPicker module={selected} />}
        <ShelvingCellFacadesEditor project={project} module={selected} />
        <div className="soft-note shelving-section-note">
          Открытая ячейка, фасад или ящики выбираются в панели ячеек — нажмите ячейку в 3D.
          Здесь настраиваются материал, ручка и сторона петель.
        </div>
      </section>
    );
  }
  const selectedFacadeDecor =
    selected.facadeDecor ?? selected.decor ?? project.carcassDecor;
  const selectedFacadeColor = selected.facadeColor ?? "white";
  const selectedFacadePattern = selected.facadePattern ?? "plain";
  // Под стеклянной или зеркальной дверью наружные ящики — ЛДСП (см. cabinetDrawerFinish).
  const plainDrawerFronts =
    selected.door !== "open" && !!cabinetDrawerLayout(selected) && cabinetDrawerFinishDiffers(selected);
  const leafCount = doorLeafCount(selected);
  const hasSharedCabinetUpright = selected.type !== "corner45" && cabinetSectionWidths(selected.width).length > 1;
  // Свой фасад у отдельной створки (16.10): выбрана дверь — разделы фасада
  // ниже меняют только её. Ручка, открывание и петли — общие.
  const ownLeafCount = doorFacadeLeafCount(selected);
  const target = doorTarget && doorTarget.moduleId === selected.id && doorTarget.index < ownLeafCount && ownLeafCount > 1
    ? doorTarget.index : null;
  const edited: WardrobeModule = target === null ? selected : leafFinish(selected, target);
  const patchFacade = (patch: Partial<WardrobeModule>) => {
    if (target === null) {
      // «Все двери» — меняются все двери: свои фасады створок сбрасываются (23.10).
      updateModule(selected.id, hasDoorFacades(selected) ? { ...patch, doorFacades: undefined } : patch);
      return;
    }
    const { handle: _handle, ...facadePatch } = patch;
    void _handle;
    const current = doorFacadeOverride(selected, target) ?? moduleDoorFacade(selected);
    updateModule(selected.id, { doorFacades: withDoorFacade(selected, target, { ...current, ...facadePatch } as typeof current) });
  };
  const editedFacadeDecor = edited.facadeDecor ?? selected.decor ?? project.carcassDecor;
  const editedFacadeColor = edited.facadeColor ?? "white";
  const editedFacadePattern = edited.facadePattern ?? "plain";
  return (
    <>
      <section className="panel-section">
        <span className="eyebrow">Тип открывания</span>
        <div className="segmented-control two">
          {(
            [
              ["hinged", "Распашные"],
              ["open", "Без дверей"]
            ] as const
          ).map(([value, label]) => (
            <button
              className={selected.door === value ? "is-selected" : ""}
              type="button"
              key={value}
              onClick={() => updateModule(selected.id, { door: value })}
            >
              {label}
            </button>
          ))}
        </div>
        {/* Кнопка «сделать одинаково» (видео Сергея 24.10): фасад этого шкафа — на все шкафы проекта. */}
        {project.modules.filter((module) => module.type === "single" || module.type === "double" || module.type === "corner45").length > 1 && <>
          <button type="button" className="product-add-action facade-apply-all"
            onClick={() => setAppliedToAll({ moduleId: selected.id, count: applyFacadeToAllCabinets(selected.id) })}>
            Применить этот фасад ко всем шкафам
          </button>
          {appliedToAll?.moduleId === selected.id && <small className="soft-note">
            {appliedToAll.count ? `Готово: фасад, ручка и двери как у этого шкафа — ещё у ${appliedToAll.count} ${appliedToAll.count === 1 ? "шкафа" : "шкафов"}.` : "Другие шкафы уже такие же."}
          </small>}
        </>}
      </section>

      {selected.door === "hinged" && (
        <section className="panel-section">
          {selected.type === "corner45" && <div className="corner-facade-count">
            <span className="eyebrow">Дверь углового</span>
            <p className="sl-help">Одна дверь {cornerDoorWidthMm(selected)} мм, петли у угла ({cornerHingeSide(selected) < 0 ? "слева" : "справа"}), ручка у наружного торца; у соседнего шкафа петли тоже у угла. Доборы — углового {cornerFillerMm(selected)} мм и соседа {cornerNeighbourFillerMm(selected)} мм — в отделке фасадов, неподвижные.</p>
          </div>}
          {selected.type === "mezzanine" && !hasUnifiedFacade(project, baseModuleFor(project, selected)) &&
            <MezzanineOpeningPicker module={selected} headroomMm={moduleHeadroomMm(project, selected)} onPatch={(patch) => updateModule(selected.id, patch)} />}
          {mezzanineFlapLayout(selected) || selected.type === "corner45" ? null : <><span className="eyebrow">Сторона открывания</span>
          {leafCount === 1 ? (
            <div className="segmented-control two">
              {(
                [
                  ["left", "Петли слева"],
                  ["right", "Петли справа"]
                ] as const
              ).map(([value, label]) => (
                <button
                  className={selected.doorHinge === value ? "is-selected" : ""}
                  type="button"
                  key={value}
                  onClick={() =>
                    updateModule(selected.id, { doorHinge: value })
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          ) : (
            <div className="soft-note">
              {hasSharedCabinetUpright
                ? "Крайние створки — на накладных петлях. Центральные створки крепятся полунакладными петлями к противоположным граням общих стоек."
                : "У двухстворчатого модуля петли расположены по внешним сторонам."}
            </div>
          )}</>}
          <HingeColorPicker module={selected} />
        </section>
      )}

      {ownLeafCount > 1 && (
        <section className="panel-section door-facade-target">
          <span className="eyebrow">Какие двери настраиваем</span>
          <div className="door-facade-target__choices" role="group" aria-label="Фасад для дверей">
            <button type="button" className={target === null ? "is-selected" : ""} aria-pressed={target === null}
              onClick={() => setDoorTarget(null)}>Все двери</button>
            {Array.from({ length: ownLeafCount }, (_, index) => (
              <button type="button" key={index} className={target === index ? "is-selected" : ""} aria-pressed={target === index}
                onClick={() => setDoorTarget({ moduleId: selected.id, index })}>
                {doorLeafName(selected, index)}
                {doorFacadeOverride(selected, index) && <i className="door-facade-target__own" title="Свой фасад" aria-label="свой фасад" />}
              </button>
            ))}
          </div>
          <p className="sl-help">{target === null
            ? hasDoorFacades(selected)
              ? "Выбор ниже применится ко всем дверям — свои фасады отдельных дверей сбросятся."
              : "Фасад ниже — для всех дверей шкафа. Чтобы одна дверь была другой (например, стекло), выберите её."
            : doorFacadeOverride(selected, target)
              ? `${doorLeafName(selected, target)} — свой фасад. Ручка общая, у стекла — зацеп в профиле.`
              : `${doorLeafName(selected, target)} пока как у шкафа — выберите для неё фасад ниже.`}</p>
          {target !== null && doorFacadeOverride(selected, target) && (
            <button type="button" className="secondary-button"
              onClick={() => updateModule(selected.id, { doorFacades: withDoorFacade(selected, target, null) })}>
              Как у шкафа
            </button>
          )}
        </section>
      )}

      <section className="panel-section">
        <span className="eyebrow">{target === null ? "Вариант фасада" : `Фасад: ${doorLeafName(selected, target).toLowerCase()}`}</span>
        <div className="choice-list">
          {availableFacadeTypes.map((key) => {
            const facade = FACADES[key];
            return (
            <button
              className={edited.facade === key ? "is-selected" : ""}
              type="button"
              key={key}
              disabled={selected.door === "open"}
              onClick={() =>
                patchFacade({
                  facade: key,
                  ...(facadeSupportsMilling(key)
                    ? {}
                    : { facadePattern: "plain" as const }),
                  ...(key === "satin"
                    ? {}
                    : selected.handle === "integrated"
                      ? { handle: "bar" as const }
                      : {})
                })
              }
            >
              <span>
                <strong>{facade.name}</strong>
                <small>{facade.subtitle}{facadeFrom(key) !== undefined ? ` · от ${formatPrice(facadeFrom(key)!)} ₽ / м²` : ""}</small>
              </span>
              <span className="radio-mark" />
            </button>
            );
          })}
        </div>
        {edited.facade === "wood" && selected.door !== "open" && (
          <div className="soft-note">
            ЛДСП выпускается гладким. Для объёмной фрезеровки выберите
            МДФ в эмали.
          </div>
        )}
        {plainDrawerFronts && target === null && (
          <div className="soft-note">
            Стекло и зеркало на ящики не ставят: фасады цокольных ящиков
            остаются из ЛДСП, их декор — ниже.
          </div>
        )}
      </section>

      {selected.door !== "open" && (edited.facade === "wood" || (plainDrawerFronts && target === null)) && (
        <section className="panel-section">
          <DecorPicker
            label={edited.facade === "wood" ? target === null ? "Декор фасада" : `Декор: ${doorLeafName(selected, target).toLowerCase()}` : "Декор фасадов ящиков"}
            value={target === null ? selectedFacadeDecor : editedFacadeDecor}
            onChange={(code) =>
              patchFacade({
                facadeDecor: code,
                facadeMaterial: decorPriceMaterial(code)
              })
            }
          />
          <div className="soft-note">
            {target === null
              ? "Декор меняет только фасад выбранного модуля. Корпус остаётся без изменений."
              : "Декор меняет только эту дверь."}
          </div>
        </section>
      )}

      {selected.door !== "open" &&
        (edited.facade === "satin" || edited.facade === "gloss") && (
          <section className="panel-section">
            <span className="eyebrow">Цвет эмали</span>
            <EnamelColorPicker
              hex={facadePaintHex({
                facadeColor: target === null ? selectedFacadeColor : editedFacadeColor,
                facadeColorHex: edited.facadeColorHex,
                facadeColorCode: edited.facadeColorCode
              })}
              code={edited.facadeColorCode}
              onChange={(color) => patchFacade({
                ...("hex" in color ? { facadeColorHex: color.hex } : {}),
                ...("code" in color ? { facadeColorCode: color.code } : {})
              })}
            />
            <PaintSidesPicker twoSided={edited.facadePaintSides === "two"}
              onChange={(facadePaintSides) => patchFacade({ facadePaintSides })} />
          </section>
        )}

      {selected.door !== "open" && facadeSupportsPattern(edited.facade) && (
        <section className="panel-section">
          <FacadePatternPicker
            value={target === null ? selectedFacadePattern : editedFacadePattern}
            onChange={(pattern) =>
              patchFacade({ facadePattern: pattern })
            }
          />
        </section>
      )}

      <section className="panel-section">
        <span className="eyebrow">Ручка</span>
        <label className="select-field">
          <span>Открывание</span>
          <select
            value={selected.handle}
            disabled={selected.door === "open"}
            onChange={(event) =>
              updateModule(selected.id, {
                handle: event.target.value as HandleType,
                handleLength: undefined,
                handleOrientation: undefined
              })
            }
          >
            {handlesForFacade(selected.facade).map(([key, handle]) => (
              <option value={key} key={key}>
                {handle.name}
              </option>
            ))}
          </select>
        </label>
        {selected.door !== "open" && <HandleChoiceNote handle={selected.handle} />}
        <HandleGeometryControls
          module={selected}
          onChange={(patch) => updateModule(selected.id, patch)}
        />
      </section>
      <SideMirrorSection project={project} module={selected} />
    </>
  );
}

/**
 * Зеркало на открытой боковине. Закрытую боковину (соседний корпус или
 * торцевые полки) выбрать нельзя: там зеркала не видно, в заказ оно не идёт.
 */
function SideMirrorSection({ project, module }: { project: PlannerProject; module: WardrobeModule }) {
  const updateModule = usePlannerStore((state) => state.updateModule);
  if (!supportsSideMirror(module)) return null;
  const stored = normalizeSideMirrors(module) ?? [];
  const size = sideMirrorSize(module);
  const toggle = (side: SideMirrorSide) => {
    const next = stored.includes(side) ? stored.filter((item) => item !== side) : [...stored, side];
    updateModule(module.id, { sideMirrors: next.length ? next : undefined });
  };
  return (
    <section className="panel-section">
      <span className="eyebrow">Зеркало на боковине</span>
      <div className="shelving-cell-options side-mirror-options">
        {(["left", "right"] as const).map((side) => {
          const blocker = sideMirrorBlocker(project, module, side);
          const on = stored.includes(side) && !blocker;
          return (
            <button key={side} type="button" className={on ? "is-selected" : ""} aria-pressed={on}
              disabled={Boolean(blocker)} onClick={() => toggle(side)}>
              <span className="cell-option-mark">{on ? "✓" : "+"}</span>
              <span>
                <strong>{side === "left" ? "Слева" : "Справа"}</strong>
                <small>{blocker === "end" ? "Здесь торцевые полки" : blocker ? "Примыкает соседний корпус" : `${size.width} × ${size.height} мм`}</small>
              </span>
            </button>
          );
        })}
      </div>
      <div className="soft-note">
        Зеркало клеится на открытую боковину с отступом {SIDE_MIRROR_MARGIN_MM} мм от кромок и начинается над цоколем.
      </div>
    </section>
  );
}

function RoomPanel({
  project,
  notify
}: {
  project: PlannerProject;
  notify: (message: string) => void;
}) {
  const setRoom = usePlannerStore((state) => state.setRoom);
  const setRoomWidth = usePlannerStore((state) => state.setRoomWidth);
  const setRoomStyle = usePlannerStore((state) => state.setRoomStyle);
  // Подбор минимума перебирает раскладки — считать только при смене проекта.
  const roomWidthMin = useMemo(() => roomWidthMinimumMm(project), [project]);
  return (
    <>
      <section className="panel-section">
        <span className="eyebrow">Размеры комнаты</span>
        {/* Ширина задней стены: под неё раскладываются шкафы (приёмка 05.10, wb-04). */}
        <DimensionField
          label="Ширина комнаты"
          value={roomWidthMm(project)}
          min={roomWidthMin}
          max={MAX_ROOM_WIDTH_MM}
          minReason={roomWidthMin > MIN_ROOM_WIDTH_MM ? "у задней стены стоят шкафы, сначала сдвиньте их" : undefined}
          onInvalid={notify}
          onCommit={(width) => setRoomWidth(width)}
        />
        <DimensionField
          label="Высота потолка"
          value={ceilingHeightMm(project)}
          min={MIN_CEILING_HEIGHT_MM}
          max={MAX_CEILING_HEIGHT_MM}
          onInvalid={notify}
          onCommit={(ceilingHeight) => {
            const needsAdjustment = maxHeight(project) > ceilingHeight - ceilingGapMm(project);
            setRoom({ ceilingHeight });
            if (needsAdjustment) {
              notify("Высота модулей скорректирована под новый потолок");
            }
          }}
        />
        <DimensionField
          label="Зазор до потолка"
          value={ceilingGapMm(project)}
          min={0}
          max={MAX_CEILING_GAP_MM}
          onInvalid={notify}
          onCommit={(ceilingGap) => {
            const needsAdjustment = maxHeight(project) > ceilingHeightMm(project) - ceilingGap;
            setRoom({ ceilingGap });
            if (needsAdjustment) notify("Высота модулей скорректирована под новый зазор");
          }}
        />
        <div className="soft-note">
          Шкаф можно сделать до {ceilingHeightMm(project) - ceilingGapMm(project)} мм: потолок {ceilingHeightMm(project)} минус
          зазор {ceilingGapMm(project)} мм. Зазор нужен, чтобы поставить шкаф на место; сверху его можно закрыть планкой.
        </div>
      </section>
      <section className="panel-section">
        <div className="section-heading room-style-heading">
          <div>
            <span className="eyebrow">Стиль комнаты</span>
            <h3>Готовое оформление</h3>
          </div>
          {project.room.style === "custom" && (
            <span className="section-metric">Вручную</span>
          )}
        </div>
        <div className="room-style-grid">
          {ROOM_STYLE_OPTIONS.map((style) => (
            <button
              className={project.room.style === style.value ? "is-selected" : ""}
              type="button"
              key={style.value}
              onClick={() => setRoomStyle(style.value)}
            >
              <RoomPreview wallColor={style.wallColor} floor={style.floor} />
              <span>
                <strong>{style.name}</strong>
                <small>{style.description}</small>
              </span>
              <span className="radio-mark" />
            </button>
          ))}
        </div>
        {project.room.style === "custom" && (
          <div className="soft-note">
            Стены, пол или свет настроены вручную. Выберите стиль, чтобы снова
            применить готовое сочетание.
          </div>
        )}
      </section>


      <section className="panel-section">
        <span className="eyebrow">Цвет стен</span>
        <div className="wall-palette">
          {WALL_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              aria-label={`Цвет стен ${color}`}
              className={project.room.wallColor === color ? "is-selected" : ""}
              style={{ background: color }}
              onClick={() => setRoom({ wallColor: color })}
            />
          ))}
        </div>
        <label className="hex-field">
          <span>Свой цвет</span>
          <input
            type="color"
            value={project.room.wallColor}
            onChange={(event) => setRoom({ wallColor: event.target.value })}
          />
          <code>{project.room.wallColor.toUpperCase()}</code>
        </label>
      </section>

      <section className="panel-section">
        <span className="eyebrow">Покрытие пола</span>
        <div className="choice-list">
          {(
            [
              ["light", "Светлый паркет"],
              ["dark", "Тёмный паркет"],
              ["laminate", "Натуральный ламинат"],
              ["tile", "Крупная плитка"]
            ] as const
          ).map(([value, label]) => (
            <button
              className={project.room.floor === value ? "is-selected" : ""}
              type="button"
              key={value}
              onClick={() => setRoom({ floor: value })}
            >
              <span>
                <strong>{label}</strong>
              </span>
              <span className="radio-mark" />
            </button>
          ))}
        </div>
      </section>

      <section className="panel-section">
        <span className="eyebrow">Сценарий освещения</span>
        <div className="segmented-control">
          <button
            className={project.room.lighting === "day" ? "is-selected" : ""}
            type="button"
            onClick={() => setRoom({ lighting: "day" })}
          >
            Дневной
          </button>
          <button
            className={project.room.lighting === "sunny" ? "is-selected" : ""}
            type="button"
            onClick={() => setRoom({ lighting: "sunny" })}
          >
            Солнечный день
          </button>
          <button
            className={project.room.lighting === "evening" ? "is-selected" : ""}
            type="button"
            onClick={() => setRoom({ lighting: "evening" })}
          >
            Вечерний
          </button>
        </div>
      </section>
    </>
  );
}

function SummaryPanel({
  project,
  onOrder,
  onDownload,
  onDownloadTechCard,
  onRender,
  renderAttached
}: {
  project: PlannerProject;
  onOrder: () => void;
  onDownload: () => Promise<void>;
  /** Только у сотрудника: техкарта сразу в файл, без отправки (27.10). */
  onDownloadTechCard?: () => Promise<void>;
  onRender: () => void;
  renderAttached: boolean;
}) {
  const prices = usePriceStore((state) => state.values);
  const price = useMemo(() => calculatePrice(project, prices), [project, prices]);
  const complete = priceIsComplete(price);
  const warnings = validateProject(project);
  const [exporting, setExporting] = useState<"" | "project" | "techCard">("");

  const runExport = async (kind: "project" | "techCard", task: () => Promise<void>) => {
    if (exporting) return;
    setExporting(kind);
    try {
      await task();
    } finally {
      setExporting("");
    }
  };

  return (
    <>
      <section className="summary-price">
        <span>Предварительная стоимость</span>
        <strong>{complete ? `${formatPrice(price.total)} ₽` : "уточняется"}</strong>
        <small>{complete ? "Точная цена будет подтверждена дизайнером" : "Цены на часть позиций ещё не заданы — стоимость назовёт дизайнер"}</small>
      </section>

      <section className="panel-section summary-project-meta">
        <span className="eyebrow">Проект</span>
        <div>
          <span>Название</span>
          <strong>{project.name}</strong>
        </div>
        <div>
          <span>Оформление комнаты</span>
          <strong>{roomStyleName(project.room.style)}</strong>
        </div>
      </section>

      <section className="panel-section price-lines">
        {[
          ["Корпус", price.carcass],
          ["Наполнение", price.internals],
          ["Фасады", price.facades],
          ["Фурнитура", price.hardware]
        ].map(([label, value]) => (
          <div key={label}>
            <span>{label}</span>
            <strong>{complete ? `${formatPrice(value as number)} ₽` : "—"}</strong>
          </div>
        ))}
      </section>

      <section className="panel-section">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Проверка проекта</span>
            <h3>{warnings.length ? `${warnings.length} замечания` : "Всё готово"}</h3>
          </div>
          <span className={`status-orb${warnings.length ? " warning" : ""}`}>
            <Icon name={warnings.length ? "help" : "check"} size={18} />
          </span>
        </div>
        <div className="validation-list">
          {warnings.length ? (
            warnings.map((warning) => (
              <div className={`validation-item ${warning.level}`} key={warning.id}>
                <span />
                <p>{warning.text}</p>
              </div>
            ))
          ) : (
            <div className="success-note">
              Размеры и выбранные компоненты совместимы.
            </div>
          )}
        </div>
      </section>

      <section className="panel-section">
        <button
          className="ai-render-button"
          type="button"
          onClick={onRender}
        >
          <span className="ai-render-button__icon">
            <Icon name="sparkles" size={19} />
          </span>
          <span>
            <strong>AI-визуализация</strong>
            <small>
              {renderAttached
                ? "Выбранный рендер добавлен к заявке"
                : "Фоторендер проекта или примерка в комнате"}
            </small>
          </span>
          <span className="ai-render-button__badge">
            {renderAttached ? "Готово" : "Новое"}
          </span>
        </button>
        <button
          className="download-button"
          type="button"
          onClick={() => runExport("project", onDownload)}
          disabled={Boolean(exporting)}
        >
          <Icon name="download" size={18} />
          {exporting === "project" ? "Формируем PDF…" : "Скачать проект в PDF"}
        </button>
        {onDownloadTechCard && (
          <button
            className="download-button"
            type="button"
            onClick={() => runExport("techCard", onDownloadTechCard)}
            disabled={Boolean(exporting)}
            title="Техническая карта с чертежами — сразу в файл, без отправки в Telegram"
          >
            <Icon name="download" size={18} />
            {exporting === "techCard" ? "Формируем техкарту…" : "Скачать техкарту (PDF)"}
          </button>
        )}
        <button className="primary-button full" type="button" onClick={onOrder}>
          Отправить дизайнеру
        </button>
      </section>
    </>
  );
}

function OrderDialog({
  open,
  visualizationAttached,
  onClose,
  onSubmit
}: {
  open: boolean;
  visualizationAttached: boolean;
  onClose: () => void;
  onSubmit: (contact: DesignerContact) => Promise<void>;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) setError("");
  }, [open]);
  // Escape закрывает форму, как и крестик (самопроверка 08.10).
  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open, submitting, onClose]);
  if (!open) return null;
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    const form = new FormData(event.currentTarget);
    setSubmitting(true);
    setError("");
    try {
      await onSubmit({
        name: String(form.get("name") ?? "").trim(),
        phone: String(form.get("phone") ?? "").trim(),
        email: String(form.get("email") ?? "").trim(),
        comment: String(form.get("comment") ?? "").trim()
      });
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Не удалось отправить заявку дизайнеру"
      );
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div
      className="dialog-backdrop"
      role="presentation"
      onMouseDown={() => !submitting && onClose()}
    >
      <div
        className="order-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button
          className="dialog-close"
          type="button"
          aria-label="Закрыть"
          disabled={submitting}
          onClick={onClose}
        >
          <Icon name="close" />
        </button>
        <span className="eyebrow">Следующий шаг</span>
        <h2 id="order-title">Обсудить проект с дизайнером</h2>
        <p>
          Дизайнер получит ваши контакты, клиентский PDF и техническую карту
          {visualizationAttached
            ? ", а также выбранную AI-визуализацию."
            : ", а также изображение проекта."}
          {" "}После проверки он сможет открыть дополнительные визуализации.
        </p>
        <form onSubmit={submit}>
          <label>
            <span>Имя</span>
            <input required name="name" autoComplete="name" placeholder="Как к вам обращаться" />
          </label>
          <label>
            <span>Телефон</span>
            <input required name="phone" autoComplete="tel" placeholder="+7 999 000-00-00" />
          </label>
          <label>
            <span>Email</span>
            <input type="email" name="email" autoComplete="email" placeholder="mail@example.ru" />
          </label>
          <label>
            <span>Комментарий</span>
            <textarea name="comment" rows={3} placeholder="Что важно учесть?" />
          </label>
          {error && (
            <div className="dialog-error" role="alert">
              {error}
            </div>
          )}
          <button
            className="primary-button full"
            type="submit"
            disabled={submitting}
          >
            {submitting ? "Отправляем дизайнеру…" : "Отправить дизайнеру"}
          </button>
        </form>
      </div>
    </div>
  );
}

/** Наверх страницы: на компьютере прокручивается витрина, на телефоне — сама страница. */
const scrollPageToTop = () => {
  document.querySelector<HTMLElement>(".workspace-page")?.scrollTo({ top: 0, behavior: "smooth" });
  window.scrollTo({ top: 0, behavior: "smooth" });
};

export default function App() {
  const [workspaceStatus, setWorkspaceStatus] = useState("Сохранено локально");
  const [isStaff, setIsStaff] = useState(false);
  const [panel, setPanel] = useState<Panel>("overview");
  const [family, setFamily] = useState<ProductFamily | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [roomRenderOpen, setRoomRenderOpen] = useState(false);
  // Публикация модели попросила обложку визуализатором (№ 29).
  const coverRequested = useCatalogCover((state) => state.requested);
  useEffect(() => {
    if (coverRequested) setRoomRenderOpen(true);
  }, [coverRequested]);
  const [designerVisualization, setDesignerVisualization] =
    useState<DesignerVisualization | null>(null);
  const [dimensionsVisible, setDimensionsVisible] = useState(true);
  // Предметы интерьера в 3D — книги, вазы, одежда, телевизор (приёмка 05.10, ch-05, sg-11).
  const [interiorPropsVisible, setInteriorPropsVisible] = useState(true);
  const [presentationMode, setPresentationMode] = useState(false);
  const [toast, setToast] = useState("");
  // На телефоне 3D закреплено над панелью. Пока набирают число, клавиатура
  // съедает половину экрана — 3D отпускается, чтобы не закрыть поле ввода.
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    const phone = window.matchMedia("(max-width: 860px)");
    const isField = (target: EventTarget | null) => target instanceof HTMLTextAreaElement ||
      (target instanceof HTMLInputElement && !["checkbox", "radio", "range", "file", "button", "submit", "color"].includes(target.type));
    const onFocusIn = (event: FocusEvent) => { if (phone.matches && isField(event.target)) setTyping(true); };
    const onFocusOut = (event: FocusEvent) => { if (!isField(event.relatedTarget)) setTyping(false); };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);
  const canvasRef = useRef<PlannerCanvasHandle>(null);
  const inspectorContentRef = useRef<HTMLDivElement>(null);
  // У стеллажа размеры ячеек — на схеме в панели ячеек, 3D остаётся чистым.
  // Кнопка «Размеры» по-прежнему включает их; расстояния при перетаскивании
  // полок и стоек видны всегда.
  useEffect(() => {
    // На телефоне 3D маленькое: размерные линии закрыли бы изделие. Габарит — в плашке под 3D.
    const visible = family !== "shelving" && !window.matchMedia("(max-width: 860px)").matches;
    setDimensionsVisible(visible);
    canvasRef.current?.setDimensionsVisible(visible);
  }, [family]);
  const revealInspectorRef = useRef(false);
  const presentationExitRef = useRef<HTMLButtonElement>(null);
  const presentationTriggerRef = useRef<HTMLButtonElement>(null);
  const project = usePlannerStore((state) => state.project);
  const past = usePlannerStore((state) => state.past);
  const future = usePlannerStore((state) => state.future);
  const undo = usePlannerStore((state) => state.undo);
  const redo = usePlannerStore((state) => state.redo);
  useEffect(() => {
    inspectorContentRef.current?.scrollTo({ top: 0, behavior: "instant" });
    if (revealInspectorRef.current && window.matchMedia("(max-width: 860px)").matches) {
      inspectorContentRef.current?.closest(".inspector")?.scrollIntoView({ block: "start", behavior: "instant" });
    }
    revealInspectorRef.current = false;
  }, [panel]);
  const prices = usePriceStore((state) => state.values);
  const loadPrices = usePriceStore((state) => state.load);
  useEffect(() => { void loadPrices(); }, [loadPrices]);
  const price = useMemo(() => calculatePrice(project, prices), [project, prices]);
  const warnings = useMemo(() => validateProject(project), [project]);
  const footprint = useMemo(() => {
    const layout = createCompositionLayout(project);
    return { ...layout.bounds, widthMm: compositionWidthMm(layout) };
  }, [project]);
  const selectedModule = project.modules.find(
    (module) => module.id === project.selectedModuleId
  );
  const familyModules = family ? productModules(project, family) : [];
  const editorProject = {
    ...project,
    selectedModuleId: selectedModule && canEditProduct(selectedModule, family) ? selectedModule.id : null
  };
  const openSlideOut = useSlideOut((state) => state.open);
  const closeSlideOut = useSlideOut((state) => state.close);
  const openShelvingCells = () => {
    const state = usePlannerStore.getState();
    const target = state.project.modules.find((module) => module.id === state.project.selectedModuleId && module.type === "shelving")
      ?? productModules(state.project, "shelving")[0];
    if (!target) return;
    const current = state.project.selectedShelvingCellId;
    const cellId = current && parseShelvingCellId(current)?.moduleId === target.id
      ? current
      : shelvingCellId(target.id, 0, 0);
    state.selectShelvingCell(target.id, cellId);
    openSlideOut(SHELVING_CELLS_PANEL);
  };
  const closeShelvingCells = () => {
    closeSlideOut(SHELVING_CELLS_PANEL);
    const state = usePlannerStore.getState();
    if (state.project.selectedModuleId) state.selectModule(state.project.selectedModuleId);
  };
  // Наведённую полку или перегородку ячейки удаляет клавиша Delete.
  const hoveredBoard = useShelvingBoardHover((state) => state.hovered);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Delete" && event.key !== "Backspace") return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      const hovered = useShelvingBoardHover.getState().hovered;
      if (!hovered) return;
      const state = usePlannerStore.getState();
      const module = state.project.modules.find((candidate) => candidate.id === hovered.moduleId);
      // Участок стойки или полки сетки: убрать его — объединить ячейки по обе стороны.
      // У комода ячейки — колонки ящиков: их не объединяют и не дробят.
      if (module?.shelvingKind === "chest") return;
      const gridBoard = parseShelvingGridBoardRef(hovered.ref);
      if (gridBoard) {
        if (!module || module.type !== "shelving") return;
        event.preventDefault();
        const { area, problem } = shelvingGridSegmentMerge(module, gridBoard);
        if (!area || problem) { notify(problem ?? "Этот участок убрать нельзя"); return; }
        const anchorId = `${module.id}:cell:${area.left}:${area.bottom}`;
        const lost = (module.shelvingCells ?? []).filter((candidate) => candidate.id !== anchorId &&
          candidate.column >= area.left && candidate.column <= area.right &&
          candidate.row >= area.bottom && candidate.row <= area.top && shelvingCellIsConfigured(candidate));
        if (lost.length && !window.confirm(`Ячейки объединятся, и у настроенных (${lost.length}) пропадут фасады, полки и подсветка. Убрать участок?`)) return;
        const refused = state.mergeShelvingCells(module.id, area);
        useShelvingBoardHover.getState().setHovered(null);
        notify(refused ?? `${shelvingGridBoardLabel(gridBoard)}: участок убран, ячейки объединены`);
        return;
      }
      const cell = module?.shelvingCells?.find((candidate) => candidate.id === hovered.cellId);
      if (!cell) return;
      event.preventDefault();
      state.updateShelvingCell(hovered.moduleId, hovered.cellId, withoutShelvingBoard(cell, hovered.ref));
      useShelvingBoardHover.getState().setHovered(null);
      notify(`${boardLabel(hovered.ref)} удалена`);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Выбор ячейки (в 3D или на схеме) открывает панель ячеек, снятие выбора — закрывает.
  const selectedCellId = project.selectedShelvingCellId;
  useEffect(() => {
    // Панель открывается, когда выбрали саму ячейку. Если тянут полку или
    // стойку, открывают ящик или дверцу — ячейка выбирается без панели.
    if (family === "shelving" && selectedCellId) {
      if (!useSlideOut.getState().takeQuiet()) openSlideOut(SHELVING_CELLS_PANEL);
    } else closeSlideOut(SHELVING_CELLS_PANEL);
  }, [family, selectedCellId, openSlideOut, closeSlideOut]);
  // Старый адрес «продвинутого конструктора» ведёт на размеры с открытой панелью ячеек.
  useEffect(() => {
    if (family === "shelving" && panel === "internals") {
      setPanel("shelving");
      openShelvingCells();
    }
  }, [family, panel]);
  useEffect(() => {
    if (presentationMode) useSlideOut.setState({ owner: null });
  }, [presentationMode]);

  const chooseFamily = (nextFamily: ProductFamily) => {
    revealInspectorRef.current = true;
    const currentProject = usePlannerStore.getState().project;
    const currentFamilies = projectProductFamilies(currentProject);
    if (
      !projectAcceptsFamily(currentProject, nextFamily) &&
      !currentFamilies.includes(nextFamily)
    ) {
      const currentTitle = currentFamilies.length === 1
        ? productFamilyTitles[currentFamilies[0]!].toLowerCase()
        : "сохранённое изделие";
      if (!window.confirm(
        `Сейчас в проекте находятся ${currentTitle}. По производственной логике один проект содержит одно изделие. Начать новый проект для раздела «${productFamilyTitles[nextFamily]}»? Прежний вариант можно вернуть кнопкой «Отменить».`
      )) return;
      usePlannerStore.getState().reset({ keepRoom: true });
      notify(`Начат новый проект: ${productFamilyTitles[nextFamily]}`);
    }
    const selected = productSelection(usePlannerStore.getState().project, nextFamily);
    if (selected) usePlannerStore.getState().selectModule(selected.id);
    else usePlannerStore.getState().clearSelection();
    setFamily(nextFamily);
    setGuideOpen(false);
    // Новый заказ начинается с комнаты, уже начатый — с размеров изделия.
    setPanel(productEntryPanel(usePlannerStore.getState().project, nextFamily));
  };
  const navigate = (nextPanel: Panel, revealInspector = true) => {
    const resolved = resolveProductPanel(nextPanel, family);
    revealInspectorRef.current = revealInspector && resolved !== panel;
    if (resolved === "overview") {
      setFamily(null);
      setGuideOpen(false);
    }
    setPanel(resolved);
  };

  useEffect(() => {
    if (!presentationMode) return;
    const previousOverflow = document.body.style.overflow;
    const scrollPosition = { left: window.scrollX, top: window.scrollY };
    document.body.style.overflow = "hidden";
    presentationExitRef.current?.focus({ preventScroll: true });
    const exitOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setPresentationMode(false);
      }
    };
    window.addEventListener("keydown", exitOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", exitOnEscape);
      // Keep the mobile inspector's place and return keyboard focus to the
      // trigger after the normal layout has been restored.
      window.requestAnimationFrame(() => {
        window.scrollTo({ ...scrollPosition, behavior: "instant" });
        presentationTriggerRef.current?.focus({ preventScroll: true });
      });
    };
  }, [presentationMode]);

  const notify = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  // Ссылка с сайта Egel (12.10): ?start= открывает раздел, &new=1 — пустой
  // проект, ?model= — модель витрины. Параметры убираются из адреса сразу,
  // чтобы перезагрузка не открывала модель повторно.
  const openSiteLink = async (link: SiteLink) => {
    if (link.model) {
      try {
        const { model } = await catalogRequest<{ model: CatalogModelDetail }>(`/catalog/${encodeURIComponent(link.model)}`);
        if (
          usePlannerStore.getState().project.modules.length &&
          !window.confirm(`Открыть модель «${model.name}»? Она заменит изделие в текущем проекте. Прежний вариант можно вернуть кнопкой «Отменить».`)
        ) return;
        const modelFamily = applyCatalogModel(model);
        lockSiteFamily(modelFamily);
        chooseFamily(modelFamily);
        scrollPageToTop();
        window.setTimeout(() => canvasRef.current?.setView("front"), 120);
        notify(`«${model.name}» — в конструкторе, меняйте под себя`);
      } catch (error) {
        notify(error instanceof Error ? error.message : "Не удалось открыть модель");
      }
      return;
    }
    if (!link.start) return;
    lockSiteFamily(link.start);
    if (
      link.fresh &&
      usePlannerStore.getState().project.modules.length &&
      window.confirm(`Начать новый проект: ${productFamilyTitles[link.start].toLowerCase()}? Текущий проект можно вернуть кнопкой «Отменить».`)
    ) usePlannerStore.getState().reset({ keepRoom: true });
    chooseFamily(link.start);
    // «Отправить дизайнеру» на странице модели: проект уже перенесён — сразу форма заявки.
    if (link.order && usePlannerStore.getState().project.modules.length) setOrderOpen(true);
  };
  // С сайта за шкафом — без выбора «шкаф / стеллаж / комод» (приёмка 22.10).
  const [siteFamily, setSiteFamily] = useState<ProductFamily | null>(() =>
    readSiteFamilyLock(Boolean(readSiteLink(window.location.search))));
  function lockSiteFamily(next: ProductFamily) {
    if (!siteHome()) return;
    storeSiteFamilyLock(next);
    setSiteFamily(next);
  }
  const productBackLabel = siteFamily ? SITE_FAMILY_BACK_LABELS[siteFamily] : "К изделиям";
  const navigateFromPanel = (nextPanel: Panel) => {
    const siteUrl = nextPanel === "overview" && siteFamily ? siteFamilyUrl(siteFamily) : null;
    if (siteUrl) window.location.href = siteUrl;
    else navigate(nextPanel);
  };
  const siteLinkHandled = useRef(false);
  useEffect(() => {
    if (siteLinkHandled.current) return;
    siteLinkHandled.current = true;
    const link = readSiteLink(window.location.search);
    if (!link) return;
    window.history.replaceState(window.history.state, "", withoutSiteLink(window.location.href));
    // Запрос — когда сцена уже построена: на медленном телефоне первая сборка
    // 3D занимает поток, и запрос модели не должен упираться в её время.
    const run = () => void openSiteLink(link);
    if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(run, { timeout: 4000 });
    else setTimeout(run, 300);
    // Один раз при открытии конструктора.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const siteHomeUrl = siteHome();

  const downloadConfiguration = async () => {
    await new Promise<void>((resolve) =>
      window.requestAnimationFrame(() => resolve())
    );
    const screenshot = canvasRef.current?.getSnapshot();
    if (!screenshot) {
      notify("3D-сцена ещё загружается. Попробуйте ещё раз");
      return;
    }
    try {
      const { downloadProjectPdf } = await import("./domain/exportPdf");
      await downloadProjectPdf(project, screenshot, usePriceStore.getState().values);
      notify("PDF проекта сохранён");
    } catch (error) {
      console.error("Unable to export project PDF", error);
      notify("Не удалось сформировать PDF");
    }
  };

  // Сотрудник скачивает техкарту сразу, без отправки в Telegram (27.10).
  const downloadTechCard = async () => {
    await new Promise<void>((resolve) =>
      window.requestAnimationFrame(() => resolve())
    );
    try {
      const { downloadAssemblyPdf } = await import("./domain/assemblyPdf");
      await downloadAssemblyPdf(project, canvasRef.current?.getSnapshot() ?? undefined);
      notify("Техкарта сохранена");
    } catch (error) {
      console.error("Unable to export tech card PDF", error);
      notify("Не удалось сформировать техкарту");
    }
  };

  const submitDesignerOrder = async (contact: DesignerContact) => {
    await new Promise<void>((resolve) =>
      window.requestAnimationFrame(() => resolve())
    );
    const screenshot = canvasRef.current?.getSnapshot();
    if (!screenshot) {
      throw new Error("3D-сцена ещё загружается. Попробуйте ещё раз");
    }
    const orderResult = await sendDesignerOrder(
      project,
      screenshot,
      contact,
      usePriceStore.getState().values,
      designerVisualization ?? undefined
    );
    setOrderOpen(false);
    // Номер заявки по порядку — клиенту для связи с дизайнером (09.10, 1.1).
    const numbered = orderResult.number ? `Заявка № ${orderResult.number}` : "Заявка";
    notify(
      orderResult.approvalStatus === "approved"
        ? `${numbered} подтверждена — дополнительные визуализации доступны`
        : orderResult.duplicate
          ? `${numbered} уже передана дизайнеру и ожидает проверки`
          : `${numbered} отправлена — дизайнер проверит её и откроет дополнительные визуализации`
    );
  };

  const panelContent = {
    overview: (
      <>
        <section className="project-kickoff">
          <div className="project-kickoff__heading">
            <span className="eyebrow">Шаг 1 · Комната</span>
            <h3>Сначала задайте место для изделия</h3>
            <p>Высота потолка и оформление комнаты будут общими для всего текущего заказа.</p>
          </div>
          <ProjectSetupCard project={project} onOpenRoom={() => navigate("room")} />
        </section>
        <ProductChooser project={project} onChoose={chooseFamily} />
        <div className="project-shortcuts">
          <button type="button" onClick={() => navigate("room")}>Комната</button>
          <button type="button" onClick={() => navigate("summary")}>Итог проекта</button>
        </div>
        {project.modules.length > 0 && (
          <button
            className="secondary-button project-reset"
            type="button"
            onClick={() => {
              const count = project.modules.length;
              if (
                !window.confirm(
                  `Комната опустеет: ${count} ${productWord(count)} будет убрано, название и оформление вернутся к исходным. Вернуть всё можно кнопкой «Отменить». Начать новый проект?`
                )
              ) {
                return;
              }
              usePlannerStore.getState().reset({ keepRoom: true });
              notify("Проект очищен. Вернуть прежний можно кнопкой «Отменить».");
            }}
          >
            Начать новый проект
          </button>
        )}
        <button
          className="model-showcase-nudge"
          type="button"
          onClick={() =>
            document.querySelector<HTMLElement>(".model-catalog--showcase")?.scrollIntoView({
              behavior: "smooth",
              block: "start"
            })
          }
        >
          <span className="model-showcase-nudge__eyebrow">Не хотите собирать с нуля?</span>
          <strong>Готовые модели уже ниже</strong>
          <span className="model-showcase-nudge__action">
            Выберите модель и настройте её под себя
            <Icon name="chevron" size={18} />
          </span>
        </button>
      </>
    ),
    modules: (
      <ModulesPanel
        project={editorProject}
        notify={notify}
        onOpenRoom={() => navigate("room")}
      />
    ),
    shelving: (
      <ShelvingProductPanel
        project={editorProject}
        notify={notify}
        onOpenRoom={() => navigate("room")}
        onContinue={openShelvingCells}
        onBrowseModels={() =>
          document.querySelector<HTMLElement>(".model-catalog--showcase")?.scrollIntoView({
            behavior: "smooth",
            block: "start"
          })
        }
      />
    ),
    chest: (
      <ChestProductPanel
        project={editorProject}
        notify={notify}
        onBrowseModels={() =>
          document.querySelector<HTMLElement>(".model-catalog--showcase")?.scrollIntoView({
            behavior: "smooth",
            block: "start"
          })
        }
      />
    ),
    materials: <MaterialsPanel project={editorProject} />,
    internals: (
      <InternalsPanel
        project={editorProject}
        notify={notify}
        onEditStructure={() => navigate("shelving")}
      />
    ),
    facades: <FacadesPanel project={editorProject} />,
    room: <RoomPanel project={project} notify={notify} />,
    summary: (
      <SummaryPanel
        project={project}
        onDownload={downloadConfiguration}
        onDownloadTechCard={isStaff ? downloadTechCard : undefined}
        onOrder={() => setOrderOpen(true)}
        onRender={() => setRoomRenderOpen(true)}
        renderAttached={Boolean(designerVisualization)}
      />
    )
  } satisfies Record<Panel, ReactNode>;

  const wideInspector = panel === "materials" || panel === "internals" || panel === "facades" ||
    (family === "shelving" && panel === "shelving");
  // Витрина живёт под конструктором во всех разделах: у шкафов теперь тоже есть готовые модели.
  const showModelShowcase = !presentationMode && panel !== "room" && panel !== "summary";

  return (
    <div className={`workspace-page${showModelShowcase ? " workspace-page--with-showcase" : ""}`}>
    <div className={`app-shell${presentationMode ? " is-presenting" : ""}${wideInspector ? " has-wide-inspector" : ""}${typing ? " is-typing" : ""}`}>
      <header className="topbar">
        {siteHomeUrl ? (
          <a className="brand brand--site" href={siteHomeUrl} title="Вернуться на сайт Egel Design">
            <div className="brand-mark">
              <span>E</span>
            </div>
            <div>
              <strong>Egel Design</strong>
              <span>← на сайт</span>
            </div>
          </a>
        ) : (
          <div className="brand">
            <div className="brand-mark">
              <span>E</span>
            </div>
            <div>
              <strong>Egel Design</strong>
              <span>Planner</span>
            </div>
          </div>
        )}
        <div className="project-title">
          <div className="project-runtime">
            <span className="save-state">
              <i />
              {workspaceStatus}
            </span>
            {isStaff && <button
              className="build-status"
              type="button"
              title="Обновить конфигуратор без удаления проекта"
              onClick={() => {
                if (
                  window.confirm(
                    "Обновить конфигуратор? Сохранённый проект останется на месте."
                  )
                ) {
                  reloadWithFreshAssets();
                }
              }}
            >
              <span>v{APP_VERSION} · {BUILD_LABEL}</span>
              <span className="build-status__refresh">Обновить</span>
            </button>}
          </div>
          <strong>{project.name}</strong>
        </div>
        <div className="topbar-actions">
          <EmployeeWorkspace onStatus={setWorkspaceStatus} onOpen={() => navigate("overview")} onStaffChange={setIsStaff}
            getPreview={() => canvasRef.current?.getProjectPreview() ?? null} />
          <button className="price-compact" type="button" title="Открыть итог проекта" onClick={() => navigate("summary")}>
            <span>Предварительно</span>
            <strong>{priceIsComplete(price) ? `${formatPrice(price.total)} ₽` : "уточняется"}</strong>
          </button>
          <button
            className="summary-button"
            type="button"
            onClick={() => navigate("summary")}
          >
            Итог
            {warnings.length > 0 && <span>{warnings.length}</span>}
          </button>
          <button
            className="primary-button"
            type="button"
            onClick={() => setOrderOpen(true)}
          >
            Отправить<span className="primary-button__tail"> дизайнеру</span>
          </button>
        </div>
      </header>

      <aside className="tool-rail" aria-label="Инструменты">
        <div className="tool-group">
          <ToolButton
            label="Отменить"
            icon="undo"
            disabled={!past.length}
            onClick={undo}
          />
          <ToolButton
            label="Повторить"
            icon="redo"
            disabled={!future.length}
            onClick={redo}
          />
        </div>
        <div className="tool-divider" />
        <div className="tool-group">
          <ToolButton
            label="Вид спереди"
            icon="front"
            onClick={() => canvasRef.current?.setView("front")}
          />
          <ToolButton
            label="Перспектива"
            icon="perspective"
            onClick={() => canvasRef.current?.setView("perspective")}
          />
          <ToolButton
            label="Настроить комнату"
            icon="room"
            onClick={() => navigate("room")}
          />
          <ToolButton
            label="Скачать изображение"
            icon="camera"
            onClick={() => {
              canvasRef.current?.capture();
              notify("Изображение проекта сохранено");
            }}
          />
        </div>
        <div className="tool-spacer" />
        <ToolButton
          label="Помощь"
          icon="help"
          onClick={() => setGuideOpen(open => !open)}
        />
      </aside>

      <main className="stage">
        {/* На телефоне боковой панели инструментов нет: «Отменить» и «Повторить» — прямо над 3D. */}
        <div className="stage-history" role="group" aria-label="История изменений">
          <button type="button" aria-label="Отменить" title="Отменить" disabled={!past.length} onClick={undo}>
            <Icon name="undo" size={18} />
          </button>
          <button type="button" aria-label="Повторить" title="Повторить" disabled={!future.length} onClick={redo}>
            <Icon name="redo" size={18} />
          </button>
        </div>
        <PlannerCanvas
          ref={canvasRef}
          presentationMode={presentationMode}
          productFamily={family}
          onBlockedSelection={(type) => notify(family
            ? `Откройте «К изделиям» → «${productFamilyTitles[type]}», чтобы редактировать это изделие`
            : "Сначала выберите «Шкафы», «Стеллажи» или «Комоды» в меню справа")}
          onSelectionIntent={target => {
            if (!family) return;
            // Keep the 3D canvas in place while the user is selecting or dragging.
            // A shelving cell is edited across several steps. Clicking its
            // door/drawer must only change the selected cell — jumping from
            // dimensions to facades makes the same object impossible to tune.
            if ((family === "shelving" || family === "chest") && (target === "internals" || target === "facades")) {
              return;
            }
            if (target !== "modules" || panel === "overview") navigate(target, false);
          }}
        />
        <button
          className="presentation-toggle"
          ref={presentationTriggerRef}
          type="button"
          aria-label="Открыть режим просмотра без размеров и панелей"
          aria-pressed={presentationMode}
          onClick={() => setPresentationMode(true)}
        >
          <Icon name="expand" size={17} />
          <span className="presentation-toggle__label">Режим просмотра</span>
        </button>
        {presentationMode && (
          <div className="presentation-chrome">
            <div className="presentation-header">
              <div className="presentation-title">
                <span>Egel Design</span>
                <strong>{project.name}</strong>
              </div>
              <button
                className="presentation-exit"
                ref={presentationExitRef}
                type="button"
                aria-label="Вернуться к редактированию, Escape"
                onClick={() => setPresentationMode(false)}
              >
                <Icon name="back" size={18} />
                К проекту
                <kbd>Esc</kbd>
              </button>
            </div>
            <div className="presentation-footer">
              <p className="presentation-hint">
                Поверните сцену движением мыши · приблизьте колесом · сдвиньте правой кнопкой
              </p>
              <nav className="presentation-controls" aria-label="Ракурс и снимок проекта">
                <button type="button" onClick={() => canvasRef.current?.setView("front")}>
                  <Icon name="front" size={19} />
                  <span>Спереди</span>
                </button>
                <button type="button" onClick={() => canvasRef.current?.setView("perspective")}>
                  <Icon name="perspective" size={19} />
                  <span>3/4</span>
                </button>
                <button type="button" onClick={() => canvasRef.current?.resetCamera()}>
                  <Icon name="expand" size={19} />
                  <span>Весь проект</span>
                </button>
                <span className="presentation-controls__divider" aria-hidden="true" />
                <button
                  className="presentation-capture"
                  type="button"
                  aria-label="Скачать изображение проекта без интерфейса"
                  onClick={() => {
                    canvasRef.current?.capture();
                    notify("Изображение проекта сохранено");
                  }}
                >
                  <Icon name="camera" size={19} />
                  <span>Снимок</span>
                </button>
              </nav>
            </div>
          </div>
        )}
        <div className="stage-meta">
          <span>
            {footprint.widthMm} × {maxHeight(project)} ×{" "}
            {Math.round(footprint.depth * 1000)} мм
          </span>
          <span>
            {project.modules.length} {sectionWord(project.modules.length)}
          </span>
        </div>
        <div className="camera-switcher" aria-label="Управление сценой">
          <button
            className={dimensionsVisible ? "is-selected" : undefined}
            type="button"
            aria-pressed={dimensionsVisible}
            title={dimensionsVisible ? "Скрыть размеры" : "Показать размеры"}
            onClick={() => {
              // Побочное действие — не внутри функции-обновления состояния: React может
              // вызвать её дважды, и размеры мигали «включено-выключено» (приёмка 04.10, v3-02).
              const nextVisible = !dimensionsVisible;
              setDimensionsVisible(nextVisible);
              canvasRef.current?.setDimensionsVisible(nextVisible);
            }}
          >
            <Icon name="ruler" size={17} />
            <span className="camera-switcher__label">Размеры</span>
          </button>
          <button
            className={interiorPropsVisible ? "is-selected" : undefined}
            type="button"
            aria-pressed={interiorPropsVisible}
            title={interiorPropsVisible ? "Скрыть предметы интерьера: книги, вазы, одежду, телевизор" : "Показать предметы интерьера"}
            onClick={() => {
              const nextVisible = !interiorPropsVisible;
              setInteriorPropsVisible(nextVisible);
              canvasRef.current?.setInteriorPropsVisible(nextVisible);
            }}
          >
            <Icon name="sparkles" size={17} />
            <span className="camera-switcher__label">Предметы</span>
          </button>
          <span className="camera-switcher__divider" />
          <button type="button" title="Вид спереди" onClick={() => canvasRef.current?.setView("front")}>
            <Icon name="front" size={17} />
            <span className="camera-switcher__label">Фронт</span>
          </button>
          <button
            className="is-selected"
            type="button"
            title="Вид в три четверти"
            onClick={() => canvasRef.current?.setView("perspective")}
          >
            <Icon name="perspective" size={17} />
            <span className="camera-switcher__label">3/4</span>
          </button>
        </div>
        {hoveredBoard && !presentationMode && <div className="board-hover-hint" role="status">
          {boardHoverHint(hoveredBoard.ref, hoveredBoard.touch, (() => {
            const board = parseShelvingGridBoardRef(hoveredBoard.ref);
            const hoveredModule = editorProject.modules.find((module) => module.id === hoveredBoard.moduleId);
            return Boolean(board?.axis === "column" && hoveredModule?.type === "shelving" &&
              getShelvingGridLayout(hoveredModule).rowCustomColumns[board.segment]);
          })())}
        </div>}
        <div id={SLIDE_OUT_ROOT_ID} className="slideout-root" />
        {family === "shelving" && !presentationMode && selectedModule?.type === "shelving" && canEditProduct(selectedModule, family) && (
          <ShelvingCellsSlideOut project={editorProject} module={selectedModule} onEditStructure={closeShelvingCells} />
        )}
      </main>

      <aside className="inspector">
        <PanelHeader panel={panel} family={family} project={editorProject} onNavigate={navigateFromPanel} backLabel={productBackLabel} />
        <div className="inspector-content" ref={inspectorContentRef}>
          {guideOpen && <ProductGuide family={family} initiallyOpen />}
          {panelContent[panel]}
          {!guideOpen && family && (panel === "modules" || panel === "shelving") && <ProductGuide family={family} />}
        </div>
        {family === "shelving" && panel === "internals" && (
          <div className="workflow-footer workflow-footer--single">
            <button
              className="workflow-footer__next"
              type="button"
              onClick={() => navigate("shelving")}
            >
              <span>
                Готово
                <strong>К размерам и сетке</strong>
              </span>
              <Icon name="chevron" size={18} />
            </button>
          </div>
        )}
        {family && productWorkflows[family].includes(panel as WorkflowPanel) && (
          <WorkflowFooter panel={panel as WorkflowPanel} family={family}
            canContinue={familyModules.length > 0 || panel === "room" || panel === "summary"} onNavigate={navigateFromPanel} backLabel={productBackLabel} />
        )}
      </aside>

      <OrderDialog
        open={orderOpen}
        visualizationAttached={Boolean(designerVisualization)}
        onClose={() => setOrderOpen(false)}
        onSubmit={submitDesignerOrder}
      />

      <RoomRenderDialog
        open={roomRenderOpen}
        projectName={project.name}
        projectSpecification={createProjectRenderSpecification(project, { interiorProps: interiorPropsVisible })}
        moduleCount={project.modules.length}
        getProjectRenderImage={() =>
          canvasRef.current?.getProjectRenderSnapshot() ?? null
        }
        getProjectRenderReferenceImage={() =>
          canvasRef.current?.getProjectRenderReferenceSnapshot() ?? null
        }
        getProjectRenderStateSpecification={() =>
          canvasRef.current?.getProjectRenderStateSpecification() ?? ""
        }
        onAttachResult={(blob, fileName) => {
          setDesignerVisualization({ blob, fileName });
          notify("Визуализация добавлена к заявке");
        }}
        onUseAsCover={coverRequested ? async (blob) => {
          try {
            useCatalogCover.getState().deliver(await prepareCover(blob, false));
            setRoomRenderOpen(false);
          } catch (error) {
            notify(error instanceof Error ? error.message : "Не удалось подготовить обложку");
          }
        } : undefined}
        onRequestDesigner={() => {
          setRoomRenderOpen(false);
          setOrderOpen(true);
        }}
        onClose={() => {
          setRoomRenderOpen(false);
          useCatalogCover.getState().cancel();
        }}
      />

      <div className={`toast${toast ? " is-visible" : ""}`} role="status">
        {toast}
      </div>
    </div>
    {showModelShowcase && <ModelCatalog
      variant="showcase"
      family={family}
      onApplied={(appliedFamily) => {
        chooseFamily(appliedFamily);
        scrollPageToTop();
        // Камера — прямо напротив новой модели (приёмка 05.10, cat-06).
        window.setTimeout(() => canvasRef.current?.setView("front"), 120);
      }}
      onEdit={() => {
        const current = projectProductFamilies(usePlannerStore.getState().project);
        chooseFamily(current[0] ?? family ?? "shelving");
        scrollPageToTop();
      }}
    />}
    </div>
  );
}
